import React, { useState, useEffect } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { supabase, TABLES } from '../supabase'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import * as XLSX from 'xlsx'

const EventDetails = () => {
  const { eventId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { judgeAccessCodes, eventName } = location.state || {}

  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  const [resettingScores, setResettingScores] = useState(false)
  const [resetPassword, setResetPassword] = useState('')
  const [resetPasswordError, setResetPasswordError] = useState('')
  const [editingJudge, setEditingJudge] = useState(null)
  const [editJudgeName, setEditJudgeName] = useState('')
  
  // Add/Remove Judge states
  const [showAddJudgeModal, setShowAddJudgeModal] = useState(false)
  const [showRemoveJudgeModal, setShowRemoveJudgeModal] = useState(false)
  const [judgeToRemove, setJudgeToRemove] = useState(null)
  const [judgeActionPassword, setJudgeActionPassword] = useState('')
  const [judgeActionError, setJudgeActionError] = useState('')
  const [isProcessingJudgeAction, setIsProcessingJudgeAction] = useState(false)
  const [showMobileActions, setShowMobileActions] = useState(false)
  
  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false)
  const [editData, setEditData] = useState({
    name: '',
    logo: null,
    logoPreview: null,
    criteria: [],
    candidates: []
  })
  const [saving, setSaving] = useState(false)
  
  // Winner animation states
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [showWinner, setShowWinner] = useState(false)
  const [countdown, setCountdown] = useState(3)
  const [winnerData, setWinnerData] = useState(null)
  const [displayMode, setDisplayMode] = useState('rankings')
  const [selectedRanking, setSelectedRanking] = useState(null)
  const [selectedAward, setSelectedAward] = useState(null)
  
  // Awards states
  const [specialAwards, setSpecialAwards] = useState([])
  const [awardCategories, setAwardCategories] = useState([])
  const [showAddAwardModal, setShowAddAwardModal] = useState(false)
  const [newAward, setNewAward] = useState({
    name: '',
    description: '',
    award_type: 'assigned',
    assigned_candidate_id: '',
    category_id: '',
    criteria_id: '',
    overall_ranking_position: '',
    add_to_overall: false,
    overall_points: 0
  })
  const [showEditAwardModal, setShowEditAwardModal] = useState(false)
  const [editingAward, setEditingAward] = useState(null)
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false)
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false)
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: ''
  })
  const [editingCategory, setEditingCategory] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isConnected, setIsConnected] = useState(true)
  const [refreshTimeout, setRefreshTimeout] = useState(null)
  const [showScoringDetailsModal, setShowScoringDetailsModal] = useState(false)
  const [scoringDetailsData, setScoringDetailsData] = useState(null)
  const [isReorderingCriteria, setIsReorderingCriteria] = useState(false)
  const [criteriaOrder, setCriteriaOrder] = useState([])
  const [draggedCriteria, setDraggedCriteria] = useState(null)
  const [isReorderingAwards, setIsReorderingAwards] = useState(false)
  const [awardsOrder, setAwardsOrder] = useState([])
  const [draggedAward, setDraggedAward] = useState(null)
  
  // Unified Scoring Sequence states
  const [isReorderingScoringSequence, setIsReorderingScoringSequence] = useState(false)
  const [scoringSequenceOrder, setScoringSequenceOrder] = useState([])
  const [draggedSequenceItem, setDraggedSequenceItem] = useState(null)
  
  const [showRestoreModal, setShowRestoreModal] = useState(false)
  const [restoreFile, setRestoreFile] = useState(null)
  const [isRestoring, setIsRestoring] = useState(false)
  const [showCopyEventModal, setShowCopyEventModal] = useState(false)
  const [copyEventFile, setCopyEventFile] = useState(null)
  const [isCopying, setIsCopying] = useState(false)
  const [newEventName, setNewEventName] = useState('')
  const [scoringViewMode, setScoringViewMode] = useState('per-criteria') // 'per-criteria' or 'per-judge'
  
  // Locked Criteria states
  const [showAdminScoringModal, setShowAdminScoringModal] = useState(false)
  const [selectedLockedCriteria, setSelectedLockedCriteria] = useState(null)
  const [adminScores, setAdminScores] = useState({}) // {candidateId-criteriaId: score}
  const [showAdminNotesModal, setShowAdminNotesModal] = useState(false)
  const [selectedCriteriaForNotes, setSelectedCriteriaForNotes] = useState(null)
  const [adminNotesInput, setAdminNotesInput] = useState('')
  
  // Event Settings states
  const [showEventSettingsModal, setShowEventSettingsModal] = useState(false)
  const [coAdminEmail, setCoAdminEmail] = useState('')
  const [coAdminsDetails, setCoAdminsDetails] = useState([])
  const [addingCoAdmin, setAddingCoAdmin] = useState(false)
  const [castBackgroundFile, setCastBackgroundFile] = useState(null)
  const [castBackgroundPreview, setCastBackgroundPreview] = useState(null)
  const [uploadingBackground, setUploadingBackground] = useState(false)
  const [settingsTab, setSettingsTab] = useState(event?.created_by === user?.id ? 'coadmins' : 'cast-background') // 'coadmins' or 'cast-background' or 'activity-logs'
  const [loadingCoAdmins, setLoadingCoAdmins] = useState(false)
  const [activityLogs, setActivityLogs] = useState([])
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [logsFilter, setLogsFilter] = useState('all') // 'all', 'create', 'update', 'delete'

  // Score Change Request states
  const [changeRequests, setChangeRequests] = useState([])
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0)
  const [processingRequest, setProcessingRequest] = useState(null)
  
  // Help Request states
  const [helpRequests, setHelpRequests] = useState([])
  const [pendingHelpCount, setPendingHelpCount] = useState(0)
  const [showNotifications, setShowNotifications] = useState(false)

  // Set correct tab when modal opens based on user role
  useEffect(() => {
    if (showEventSettingsModal && event && user) {
      if (event.created_by === user.id) {
        setSettingsTab('coadmins')
      } else {
        setSettingsTab('cast-background')
      }
    }
  }, [showEventSettingsModal, event, user])

  // Fetch co-admin details when event data changes
  useEffect(() => {
    const fetchCoAdminDetails = async () => {
      if (!event || !event.co_admins || event.co_admins.length === 0) {
        setCoAdminsDetails([])
        return
      }

      setLoadingCoAdmins(true)
      try {
        const details = await Promise.all(
          event.co_admins.map(async (userId) => {
            try {
              const { data, error } = await supabase
                .rpc('get_user_email_by_id', { user_uuid: userId })
              
              if (error || !data || data.length === 0) {
                return { id: userId, email: 'Unknown User' }
              }
              
              return { id: userId, email: data[0].email_address }
            } catch (err) {
              return { id: userId, email: 'Error loading' }
            }
          })
        )
        setCoAdminsDetails(details)
      } catch (error) {
        console.error('Error fetching co-admin details:', error)
      } finally {
        setLoadingCoAdmins(false)
      }
    }

    if (showEventSettingsModal && settingsTab === 'coadmins') {
      fetchCoAdminDetails()
    }
  }, [event, showEventSettingsModal, settingsTab])

  useEffect(() => {
    fetchEventData()
    // Set up real-time subscription for automatic data refresh
    const subscription = supabase
      .channel('event-updates')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: TABLES.SCORES },
        (payload) => {
          console.log('Scores updated:', payload)
          handleDataRefresh(false) // Don't show notification for automatic updates
        }
      )
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: TABLES.JUDGES },
        (payload) => {
          console.log('Judges updated:', payload)
          handleDataRefresh(false)
        }
      )
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: TABLES.SPECIAL_AWARD_VOTES },
        (payload) => {
          console.log('Special award votes updated:', payload)
          handleDataRefresh(false)
        }
      )
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: TABLES.SPECIAL_AWARDS },
        (payload) => {
          console.log('Special awards updated:', payload)
          handleDataRefresh(false)
        }
      )
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: TABLES.CANDIDATES },
        (payload) => {
          console.log('Candidates updated:', payload)
          handleDataRefresh(false)
        }
      )
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: TABLES.CRITERIA },
        (payload) => {
          console.log('Criteria updated:', payload)
          handleDataRefresh(false)
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status)
        setIsConnected(status === 'SUBSCRIBED')
      })

    return () => {
      subscription.unsubscribe()
      // Clean up any pending refresh timeout
      if (refreshTimeout) {
        clearTimeout(refreshTimeout)
      }
    }
  }, [eventId])

  // Handle data refresh with visual feedback and debouncing
  const handleDataRefresh = async (showNotification = true) => {
    // Clear any existing timeout
    if (refreshTimeout) {
      clearTimeout(refreshTimeout)
    }

    // Set a new timeout to debounce rapid updates
    const timeout = setTimeout(async () => {
      setIsRefreshing(true)
      try {
        await fetchEventData()
        // Show a subtle notification that data was refreshed
        if (showNotification) {
          toast.success('Data updated automatically', { 
            duration: 2000,
            position: 'top-right',
            style: {
              background: '#10B981',
              color: 'white',
            }
          })
        }
      } catch (error) {
        console.error('Error refreshing data:', error)
        if (showNotification) {
          toast.error('Failed to refresh data', { duration: 3000 })
        }
      } finally {
        setIsRefreshing(false)
      }
    }, 500) // 500ms debounce

    setRefreshTimeout(timeout)
  }

  // Manual refresh function (always shows notification)
  const handleManualRefresh = () => {
    handleDataRefresh(true)
  }

  // Show success message with judge access codes if event was just created
  useEffect(() => {
    if (judgeAccessCodes && judgeAccessCodes.length > 0) {
      toast.success(
        <div>
          <div className="font-semibold mb-2">Event created successfully!</div>
          <div className="text-sm">
            {judgeAccessCodes.length} judge access codes generated. 
            Check the "Judges" tab to view all codes.
          </div>
        </div>,
        { duration: 6000 }
      )
    }
  }, [judgeAccessCodes])

  // Focus password input when delete modal opens
  useEffect(() => {
    if (showDeleteModal) {
      const passwordInput = document.querySelector('input[type="password"]')
      if (passwordInput) {
        setTimeout(() => passwordInput.focus(), 100)
      }
    }
  }, [showDeleteModal])

  // Initialize edit data when switching to edit tab
  useEffect(() => {
    if (activeTab === 'edit' && event) {
      setEditData({
        name: event.name || '',
        logo: null,
        logoPreview: event.logo_url || null,
        criteria: event.criteria?.map(c => ({
          id: c.id,
          name: c.name || '',
          description: c.description || '',
          percentage: c.percentage || 0
        })) || [],
        candidates: event.candidates?.map(c => ({
          id: c.id,
          name: c.name || '',
          representation: c.representation || '',
          candidate_number: c.candidate_number || 0
        })) || []
      })
    }
  }, [activeTab, event])

  const fetchEventData = async () => {
    try {
      // First, fetch the event and its basic relationships
      const { data: eventData, error: eventError } = await supabase
        .from(TABLES.EVENTS)
        .select(`
          *,
          criteria (
            id,
            name,
            description,
            percentage,
            display_order,
            is_locked,
            admin_notes,
            event_id
          ),
          candidates (
            id,
            name,
            representation,
            candidate_number,
            event_id
          ),
          judges (
            id,
            name,
            judge_number,
            judge_access_code,
            completed_criteria,
            submitted_special_awards,
            scores_certified,
            certification_timestamp,
            created_at,
            event_id
          ),
          scores (
            id,
            judge_id,
            candidate_id,
            criteria_id,
            score,
            event_id
          )
        `)
        .eq('id', eventId)
        .single()

      if (eventError) throw eventError
      if (!eventData) throw new Error('Event not found')

      // Sort criteria by display_order
      if (eventData.criteria) {
        eventData.criteria.sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      }

      // Fix num_judges if it's out of sync with actual judge numbers
      if (eventData.judges) {
        const correctNumJudges = eventData.judges.length > 0 
          ? Math.max(...eventData.judges.map(j => j.judge_number))
          : 0
        
        if (correctNumJudges !== eventData.num_judges) {
          console.log(`Fixing num_judges: was ${eventData.num_judges}, should be ${correctNumJudges}`)
          const { error: updateError } = await supabase
            .from(TABLES.EVENTS)
            .update({ num_judges: correctNumJudges })
            .eq('id', eventId)
          
          if (!updateError) {
            eventData.num_judges = correctNumJudges
          }
        }
      }

      setEvent(eventData)
      
      // Fetch special awards and categories separately to avoid nesting issues
      await fetchAwardCategories()
      await fetchSpecialAwards()
      await loadAdminScores()
      await loadChangeRequests()
      await loadHelpRequests()
    } catch (error) {
      console.error('Error fetching event:', error)
      toast.error('Failed to load event data')
      navigate('/admin')
    } finally {
      setLoading(false)
    }
  }

  const loadAdminScores = async () => {
    try {
      const { data: adminScoresData, error } = await supabase
        .from(TABLES.ADMIN_CRITERIA_SCORES)
        .select('*')
        .eq('event_id', eventId)

      if (error) throw error

      // Convert to map for easy lookup
      const scoresMap = {}
      if (adminScoresData) {
        adminScoresData.forEach(score => {
          const key = `${score.candidate_id}-${score.criteria_id}`
          scoresMap[key] = score.score
        })
      }

      setAdminScores(scoresMap)
    } catch (error) {
      console.error('Error loading admin scores:', error)
    }
  }

  const loadChangeRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('score_change_requests')
        .select(`
          *,
          judge:judges(name, judge_number),
          criteria:criteria(name),
          special_award:special_awards(name)
        `)
        .eq('event_id', eventId)
        .order('requested_at', { ascending: false })

      if (error) throw error

      setChangeRequests(data || [])
      const pendingCount = (data || []).filter(req => req.status === 'pending').length
      setPendingRequestsCount(pendingCount)
    } catch (error) {
      console.error('Error loading change requests:', error)
    }
  }
  
  const loadHelpRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('help_requests')
        .select(`
          *,
          judge:judges(name, judge_number)
        `)
        .eq('event_id', eventId)
        .order('created_at', { ascending: false })

      if (error) throw error

      setHelpRequests(data || [])
      const pendingCount = (data || []).filter(req => req.status === 'pending').length
      setPendingHelpCount(pendingCount)
    } catch (error) {
      console.error('Error loading help requests:', error)
    }
  }
  
  const handleResolveHelpRequest = async (requestId, notes = '') => {
    try {
      setProcessingRequest(requestId)
      
      const { error } = await supabase
        .from('help_requests')
        .update({
          status: 'resolved',
          resolved_at: new Date().toISOString(),
          resolved_by: user.id,
          admin_notes: notes
        })
        .eq('id', requestId)
      
      if (error) throw error
      
      toast.success('Help request resolved!')
      await loadHelpRequests()
    } catch (error) {
      console.error('Error resolving help request:', error)
      toast.error('Failed to resolve help request')
    } finally {
      setProcessingRequest(null)
    }
  }

  const handleApproveChangeRequest = async (requestId) => {
    try {
      setProcessingRequest(requestId)

      const { error } = await supabase
        .from('score_change_requests')
        .update({
          status: 'approved',
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id
        })
        .eq('id', requestId)

      if (error) throw error

      toast.success('Change request approved! Judge can now edit their scores.')
      await loadChangeRequests()
      await fetchEventData()
    } catch (error) {
      console.error('Error approving change request:', error)
      toast.error('Failed to approve change request')
    } finally {
      setProcessingRequest(null)
    }
  }

  const handleRejectChangeRequest = async (requestId, adminNotes = '') => {
    try {
      setProcessingRequest(requestId)

      const { error } = await supabase
        .from('score_change_requests')
        .update({
          status: 'rejected',
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
          admin_notes: adminNotes
        })
        .eq('id', requestId)

      if (error) throw error

      toast.success('Change request rejected.')
      await loadChangeRequests()
    } catch (error) {
      console.error('Error rejecting change request:', error)
      toast.error('Failed to reject change request')
    } finally {
      setProcessingRequest(null)
    }
  }

  const handleToggleCriteriaLock = async (criteriaId) => {
    try {
      const criteria = event.criteria.find(c => c.id === criteriaId)
      const newLockState = !criteria.is_locked

      // Update in database
      const { error } = await supabase
        .from(TABLES.CRITERIA)
        .update({ is_locked: newLockState })
        .eq('id', criteriaId)

      if (error) throw error

      toast.success(`Criteria ${newLockState ? 'locked' : 'unlocked'} successfully!`)
      
      // Refresh event data
      await fetchEventData()
    } catch (error) {
      console.error('Error toggling criteria lock:', error)
      toast.error('Failed to update criteria lock status')
    }
  }

  const handleOpenAdminScoring = (criteria) => {
    setSelectedLockedCriteria(criteria)
    setShowAdminScoringModal(true)
  }

  const handleSaveAdminScores = async () => {
    try {
      if (!selectedLockedCriteria) return

      // Prepare scores data
      const scoresData = event.candidates.map(candidate => {
        const key = `${candidate.id}-${selectedLockedCriteria.id}`
        const score = adminScores[key] || 0
        return {
          event_id: eventId,
          candidate_id: candidate.id,
          criteria_id: selectedLockedCriteria.id,
          score: parseFloat(score),
          created_by: user.id
        }
      })

      // Upsert scores (insert or update if exists)
      const { error } = await supabase
        .from(TABLES.ADMIN_CRITERIA_SCORES)
        .upsert(scoresData, { 
          onConflict: 'candidate_id,criteria_id',
          ignoreDuplicates: false 
        })

      if (error) throw error

      toast.success('Admin scores saved successfully!')
      setShowAdminScoringModal(false)
      setSelectedLockedCriteria(null)
      
      // Refresh event data
      await fetchEventData()
    } catch (error) {
      console.error('Error saving admin scores:', error)
      toast.error('Failed to save admin scores')
    }
  }

  const handleAdminScoreChange = (candidateId, criteriaId, score) => {
    const criteria = event.criteria.find(c => c.id === criteriaId)
    const maxScore = criteria ? criteria.percentage : 100
    const normalizedScore = Math.min(Math.max(parseFloat(score) || 0, 0), maxScore)
    
    const key = `${candidateId}-${criteriaId}`
    setAdminScores(prev => ({
      ...prev,
      [key]: normalizedScore
    }))
  }

  const handleOpenAdminNotes = (criteria) => {
    setSelectedCriteriaForNotes(criteria)
    setAdminNotesInput(criteria.admin_notes || '')
    setShowAdminNotesModal(true)
  }

  const handleSaveAdminNotes = async () => {
    try {
      if (!selectedCriteriaForNotes) return

      const { error } = await supabase
        .from(TABLES.CRITERIA)
        .update({ admin_notes: adminNotesInput })
        .eq('id', selectedCriteriaForNotes.id)

      if (error) throw error

      toast.success('Admin notes saved successfully!')
      setShowAdminNotesModal(false)
      setSelectedCriteriaForNotes(null)
      setAdminNotesInput('')
      
      // Refresh event data
      await fetchEventData()
    } catch (error) {
      console.error('Error saving admin notes:', error)
      toast.error('Failed to save admin notes')
    }
  }

  const calculateCandidateScores = () => {
    if (!event) return []

    const candidateScores = {}
    
    // Add safety check for scores
    if (!event.scores || !Array.isArray(event.scores)) {
      return []
    }

    event.scores.forEach(score => {
      const candidateId = score.candidate_id
      const criteriaId = score.criteria_id
      const judgeId = score.judge_id

      if (!candidateScores[candidateId]) {
        const candidate = event.candidates?.find(c => c.id === candidateId)
        candidateScores[candidateId] = {
          candidate: candidate,
          scores: {},
          criteriaScores: {},
          totalScore: 0,
          averageScore: 0,
          judgeCount: 0
        }
      }

      if (!candidateScores[candidateId].scores[criteriaId]) {
        candidateScores[candidateId].scores[criteriaId] = []
      }

      // Look up judge and criteria from the event data instead of from the score
      const judge = event.judges?.find(j => j.id === judgeId)
      const criteria = event.criteria?.find(c => c.id === criteriaId)

      candidateScores[candidateId].scores[criteriaId].push({
        score: score.score,
        judge: judge?.name || 'Unknown Judge',
        criteria: criteria
      })
    })

    // Ensure all candidates exist in candidateScores (even if they have no judge scores)
    event.candidates?.forEach(candidate => {
      if (!candidateScores[candidate.id]) {
        candidateScores[candidate.id] = {
          candidate: candidate,
          scores: {},
          criteriaScores: {},
          totalScore: 0,
          averageScore: 0,
          judgeCount: 0
        }
      }
    })

    // Calculate totals: sum of per-criteria averages (each 0..criteria.percentage)
    Object.values(candidateScores).forEach(candidate => {
      let totalScore = 0
      const judgeIds = new Set()

      event.criteria?.forEach(criteria => {
        // Check if this criteria is locked
        if (criteria.is_locked) {
          // Use admin score for locked criteria
          const key = `${candidate.candidate.id}-${criteria.id}`
          const adminScore = adminScores[key] || 0
          candidate.criteriaScores[criteria.id] = adminScore
          totalScore += adminScore
        } else {
          // Use judge scores for unlocked criteria
          const criteriaScores = candidate.scores[criteria.id] || []
          if (criteriaScores.length > 0) {
            const averageScore = criteriaScores.reduce((sum, s) => sum + s.score, 0) / criteriaScores.length
            // Store the average score for this criteria (used in criteria-based awards)
            candidate.criteriaScores[criteria.id] = averageScore
            // Since each criterion's max equals its percentage, just add the average directly
            totalScore += averageScore
            criteriaScores.forEach(s => judgeIds.add(s.judge))
          } else {
            candidate.criteriaScores[criteria.id] = 0
          }
        }
      })

      candidate.totalScore = totalScore // 0..sum(criteria.percentage) typically 0..100
      candidate.averageScore = totalScore
      candidate.judgeCount = judgeIds.size
      candidate.awardBonusPoints = 0 // Initialize award bonus points
      candidate.awardBonuses = [] // Track which awards gave bonus points
    })

    // Add bonus points from assigned awards with add_to_overall enabled
    const assignedBonusAwards = specialAwards.filter(award => 
      award.award_type === 'assigned' && award.add_to_overall && award.overall_points > 0 && award.assigned_candidate_id
    )

    assignedBonusAwards.forEach(award => {
      const winnerId = award.assigned_candidate_id
      if (candidateScores[winnerId]) {
        const bonusPoints = parseFloat(award.overall_points) || 0
        candidateScores[winnerId].totalScore += bonusPoints
        candidateScores[winnerId].averageScore += bonusPoints
        candidateScores[winnerId].awardBonusPoints += bonusPoints
        candidateScores[winnerId].awardBonuses.push({
          awardName: award.name,
          points: bonusPoints
        })
      }
    })

    // Add bonus points from vote-type awards with add_to_overall enabled
    const voteBonusAwards = specialAwards.filter(award => 
      award.award_type === 'vote' && award.add_to_overall && award.overall_points > 0
    )

    voteBonusAwards.forEach(award => {
      // Get all votes for this award
      const awardVotes = event?.special_award_votes?.filter(vote => 
        vote.special_award_id === award.id
      ) || []
      
      if (awardVotes.length > 0) {
        // Calculate total scores for each candidate
        const voteScores = {}
        awardVotes.forEach(vote => {
          if (!voteScores[vote.candidate_id]) {
            voteScores[vote.candidate_id] = 0
          }
          voteScores[vote.candidate_id] += vote.score
        })
        
        // Find candidate with highest score (the winner)
        const winnerId = Object.keys(voteScores).reduce((a, b) => 
          voteScores[a] > voteScores[b] ? a : b
        )
        
        // Add bonus points to the winner
        if (candidateScores[winnerId]) {
          const bonusPoints = parseFloat(award.overall_points) || 0
          candidateScores[winnerId].totalScore += bonusPoints
          candidateScores[winnerId].averageScore += bonusPoints
          candidateScores[winnerId].awardBonusPoints += bonusPoints
          candidateScores[winnerId].awardBonuses.push({
            awardName: award.name,
            points: bonusPoints
          })
        }
      }
    })

    return Object.values(candidateScores).sort((a, b) => b.averageScore - a.averageScore)
  }

  const exportToExcel = () => {
    // Create workbook
    const wb = XLSX.utils.book_new()

    // Create scoring sheet for each judge
    const sortedJudges = event.judges
      .filter(j => j.name)
      .sort((a, b) => a.judge_number - b.judge_number)

    sortedJudges.forEach(judge => {
      // Create header rows
      const scoringData = []
      
      // Title and judge info
      scoringData.push([`${event.name} - Manual Scoring Sheet`])
      scoringData.push([`Judge ${judge.judge_number}: ${judge.name}`])
      scoringData.push([`Instructions: Score each candidate for each criteria. Write scores in the designated cells.`])
      scoringData.push([]) // Empty row
      
      // Create table header
      const headerRow = ['#', 'Candidate Name', 'Representation']
      event.criteria.forEach(criteria => {
        headerRow.push(`${criteria.name}\n(Max: ${criteria.percentage})`)
      })
      headerRow.push('Total')
      scoringData.push(headerRow)
      
      // Add candidate rows with empty scoring cells
      event.candidates
        .sort((a, b) => a.candidate_number - b.candidate_number)
        .forEach(candidate => {
          const row = [
            candidate.candidate_number,
            candidate.name,
            candidate.representation || '-'
          ]
          
          // Add empty cells for each criteria score
          event.criteria.forEach(() => {
            row.push('')
          })
          
          // Add formula for total
          const startCol = 4 // D column (0-indexed: 3 + 1)
          const endCol = startCol + event.criteria.length - 1
          const rowNum = scoringData.length + 1
          row.push(`=SUM(${XLSX.utils.encode_col(startCol - 1)}${rowNum}:${XLSX.utils.encode_col(endCol - 1)}${rowNum})`)
          
          scoringData.push(row)
        })
      
      // Add summary row
      scoringData.push([])
      scoringData.push(['', '', 'CRITERIA TOTALS:'])
      const summaryRow = ['', '', '']
      event.criteria.forEach((criteria, idx) => {
        const col = XLSX.utils.encode_col(3 + idx)
        const startRow = 6
        const endRow = 5 + event.candidates.length
        summaryRow.push(`=SUM(${col}${startRow}:${col}${endRow})`)
      })
      summaryRow.push('') // Empty total cell
      scoringData.push(summaryRow)

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(scoringData)
      
      // Set column widths for better fit on 8.5x13 paper
      const colWidths = [
        { wch: 4 },  // #
        { wch: 20 }, // Name
        { wch: 18 }  // Representation
      ]
      event.criteria.forEach(() => {
        colWidths.push({ wch: 12 }) // Criteria columns
      })
      colWidths.push({ wch: 10 }) // Total
      ws['!cols'] = colWidths

      // Apply professional styling to all cells
      const range = XLSX.utils.decode_range(ws['!ref'])
      
      // Style title rows (0-2)
      for (let R = 0; R <= 2; R++) {
        for (let C = range.s.c; C <= range.e.c; C++) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
          if (ws[cellAddress]) {
            ws[cellAddress].s = {
              font: { name: 'Calibri', sz: R === 0 ? 14 : 11, bold: true },
              alignment: { horizontal: 'center', vertical: 'center', wrapText: true }
            }
          }
        }
      }
      
      // Style header row (row 4)
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 4, c: C })
        if (ws[cellAddress]) {
          ws[cellAddress].s = {
            font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "4472C4" } },
            alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
            border: {
              top: { style: 'thin', color: { rgb: "000000" } },
              bottom: { style: 'thin', color: { rgb: "000000" } },
              left: { style: 'thin', color: { rgb: "000000" } },
              right: { style: 'thin', color: { rgb: "000000" } }
            }
          }
        }
      }
      
      // Style data rows
      for (let R = 5; R < 5 + event.candidates.length; R++) {
        for (let C = range.s.c; C <= range.e.c; C++) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
          if (ws[cellAddress]) {
            ws[cellAddress].s = {
              font: { name: 'Calibri', sz: 10 },
              alignment: { 
                horizontal: C === 0 || C > 2 ? 'center' : 'left', 
                vertical: 'center',
                wrapText: true 
              },
              border: {
                top: { style: 'thin', color: { rgb: "D3D3D3" } },
                bottom: { style: 'thin', color: { rgb: "D3D3D3" } },
                left: { style: 'thin', color: { rgb: "D3D3D3" } },
                right: { style: 'thin', color: { rgb: "D3D3D3" } }
              }
            }
          }
        }
      }
      
      // Style summary rows
      const summaryStartRow = 5 + event.candidates.length + 1
      for (let R = summaryStartRow; R <= summaryStartRow + 1; R++) {
        for (let C = range.s.c; C <= range.e.c; C++) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
          if (ws[cellAddress]) {
            ws[cellAddress].s = {
              font: { name: 'Calibri', sz: 10, bold: true },
              alignment: { horizontal: 'center', vertical: 'center' },
              fill: { fgColor: { rgb: "E7E6E6" } }
            }
          }
        }
      }

      // Set row heights for professional appearance
      ws['!rows'] = []
      ws['!rows'][0] = { hpt: 24 } // Title
      ws['!rows'][1] = { hpt: 18 } // Judge info
      ws['!rows'][2] = { hpt: 18 } // Instructions
      ws['!rows'][4] = { hpt: 30 } // Header row (taller for wrapped text)

      // Page setup for 8.5x13 paper with 0.5 inch margins
      ws['!pageSetup'] = {
        paperSize: 5, // Legal size (8.5 x 14, closest to 8.5 x 13)
        orientation: 'portrait',
        scale: 100,
        fitToWidth: 1,
        fitToHeight: 0,
        horizontalDpi: 300,
        verticalDpi: 300
      }
      
      ws['!margins'] = {
        left: 0.5,
        right: 0.5,
        top: 0.5,
        bottom: 0.5,
        header: 0.3,
        footer: 0.3
      }

      // Add worksheet to workbook with judge name
      const sheetName = `Judge ${judge.judge_number}`
      XLSX.utils.book_append_sheet(wb, ws, sheetName)
    })

    // Create consolidated scoring sheet
    const consolidatedData = []
    consolidatedData.push([`${event.name} - Consolidated Scores`])
    consolidatedData.push(['Total Scores from All Judges'])
    consolidatedData.push([])
    
    // Header row - simplified to only show criteria totals
    const consolidatedHeader = ['Rank', '#', 'Candidate Name', 'Representation']
    
    // Add criteria total columns
    event.criteria.forEach(criteria => {
      consolidatedHeader.push(`${criteria.name}\n(Total)`)
    })
    consolidatedHeader.push('Grand Total')
    consolidatedData.push(consolidatedHeader)
    
    // Add candidate rows
    event.candidates
      .sort((a, b) => a.candidate_number - b.candidate_number)
      .forEach((candidate, candIdx) => {
        const currentRow = consolidatedData.length + 1
        
        // Calculate Grand Total column position
        const grandTotalCol = XLSX.utils.encode_col(4 + event.criteria.length)
        
        // Add rank formula (will rank based on Grand Total, descending order)
        const firstDataRow = 5 // First candidate row
        const lastDataRow = 4 + event.candidates.length // Last candidate row
        const rankFormula = `=RANK(${grandTotalCol}${currentRow},$${grandTotalCol}$${firstDataRow}:$${grandTotalCol}$${lastDataRow},0)`
        
        const row = [
          rankFormula, // Rank formula
          candidate.candidate_number,
          candidate.name,
          candidate.representation || '-'
        ]
        
        // Add formulas to sum scores from all judges for each criteria
        event.criteria.forEach((criteria, critIdx) => {
          const scoreRefs = []
          sortedJudges.forEach((judge) => {
            const judgeSheetName = `Judge ${judge.judge_number}`
            const scoreRow = 6 + candIdx // Row in judge sheet where this candidate's score is
            const scoreCol = XLSX.utils.encode_col(3 + critIdx) // Column for this criteria
            scoreRefs.push(`'${judgeSheetName}'!${scoreCol}${scoreRow}`)
          })
          row.push(`=SUM(${scoreRefs.join(',')})`)
        })
        
        // Add grand total (sum of all criteria totals)
        const criteriaStartCol = 4
        const criteriaEndCol = 4 + event.criteria.length - 1
        row.push(`=SUM(${XLSX.utils.encode_col(criteriaStartCol)}${currentRow}:${XLSX.utils.encode_col(criteriaEndCol)}${currentRow})`)
        
        consolidatedData.push(row)
      })

    const wsConsolidated = XLSX.utils.aoa_to_sheet(consolidatedData)
    
    // Set column widths - simplified
    const consolidatedColWidths = [
      { wch: 5 },  // Rank
      { wch: 4 },  // #
      { wch: 25 }, // Name
      { wch: 20 }  // Representation
    ]
    
    // Criteria total columns
    event.criteria.forEach(() => {
      consolidatedColWidths.push({ wch: 14 })
    })
    consolidatedColWidths.push({ wch: 14 }) // Grand Total
    
    wsConsolidated['!cols'] = consolidatedColWidths

    // Style consolidated sheet
    const consRange = XLSX.utils.decode_range(wsConsolidated['!ref'])
    
    // Title rows
    for (let R = 0; R <= 1; R++) {
      for (let C = consRange.s.c; C <= consRange.e.c; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
        if (wsConsolidated[cellAddress]) {
          wsConsolidated[cellAddress].s = {
            font: { name: 'Calibri', sz: R === 0 ? 14 : 12, bold: true },
            alignment: { horizontal: 'center', vertical: 'center' }
          }
        }
      }
    }
    
    // Header row
    for (let C = consRange.s.c; C <= consRange.e.c; C++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 3, c: C })
      if (wsConsolidated[cellAddress]) {
        wsConsolidated[cellAddress].s = {
          font: { name: 'Calibri', sz: 9, bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "70AD47" } },
          alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
          border: {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
          }
        }
      }
    }
    
    // Data rows
    for (let R = 4; R <= consRange.e.r; R++) {
      for (let C = consRange.s.c; C <= consRange.e.c; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
        if (wsConsolidated[cellAddress]) {
          wsConsolidated[cellAddress].s = {
            font: { name: 'Calibri', sz: 9 },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: {
              top: { style: 'thin', color: { rgb: "D3D3D3" } },
              bottom: { style: 'thin', color: { rgb: "D3D3D3" } },
              left: { style: 'thin', color: { rgb: "D3D3D3" } },
              right: { style: 'thin', color: { rgb: "D3D3D3" } }
            }
          }
        }
      }
    }

    wsConsolidated['!rows'] = []
    wsConsolidated['!rows'][0] = { hpt: 24 }
    wsConsolidated['!rows'][1] = { hpt: 18 }
    wsConsolidated['!rows'][3] = { hpt: 30 }

    wsConsolidated['!pageSetup'] = {
      paperSize: 5,
      orientation: 'landscape',
      scale: 100,
      fitToWidth: 1,
      fitToHeight: 0
    }
    
    wsConsolidated['!margins'] = {
      left: 0.5,
      right: 0.5,
      top: 0.5,
      bottom: 0.5,
      header: 0.3,
      footer: 0.3
    }

    XLSX.utils.book_append_sheet(wb, wsConsolidated, 'Consolidated Scores')

    // Create summary instructions sheet
    const instructionsData = [
      [`${event.name} - Manual Scoring Instructions`],
      [],
      ['HOW TO USE THIS WORKBOOK:'],
      [],
      ['1. Each judge sheet is for one judge to score candidates'],
      ['2. Write the score for each candidate in the criteria columns'],
      ['3. The Total column will automatically calculate the sum'],
      ['4. Make sure to stay within the maximum score for each criteria'],
      ['5. The "Consolidated Scores" sheet automatically calculates totals from all judges'],
      [],
      ['PRINT SETTINGS:'],
      [],
      ['• Paper Size: 8.5" x 13" (Legal)'],
      ['• Margins: 0.5 inches on all sides'],
      ['• Each sheet is optimized to fit on one page'],
      [],
      ['EVENT CRITERIA:'],
      []
    ]
    
    event.criteria.forEach(criteria => {
      instructionsData.push([
        `${criteria.name}:`,
        `Maximum ${criteria.percentage} points`,
        criteria.description || ''
      ])
    })
    
    instructionsData.push([])
    instructionsData.push(['JUDGES:'])
    instructionsData.push([])
    
    sortedJudges.forEach(judge => {
      instructionsData.push([`Judge ${judge.judge_number}:`, judge.name])
    })

    const wsInstructions = XLSX.utils.aoa_to_sheet(instructionsData)
    wsInstructions['!cols'] = [{ wch: 30 }, { wch: 25 }, { wch: 40 }]
    
    // Style instructions sheet
    for (let R = 0; R < instructionsData.length; R++) {
      for (let C = 0; C < 3; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
        if (wsInstructions[cellAddress]) {
          wsInstructions[cellAddress].s = {
            font: { name: 'Calibri', sz: R === 0 ? 14 : 11, bold: R === 0 || R === 2 || R === 10 || R === 16 || instructionsData[R][0]?.includes(':') },
            alignment: { horizontal: 'left', vertical: 'top', wrapText: true }
          }
        }
      }
    }
    
    wsInstructions['!pageSetup'] = {
      paperSize: 5,
      orientation: 'portrait'
    }
    
    wsInstructions['!margins'] = {
      left: 0.5,
      right: 0.5,
      top: 0.5,
      bottom: 0.5
    }
    
    XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions', true)

    // Save file
    XLSX.writeFile(wb, `${event.name}_Manual_Scoring_Sheets.xlsx`)
    toast.success('Manual scoring sheets exported successfully!')
  }

  const printCover = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank')
    
    // Get the event logo URL or use a placeholder
    const logoUrl = event.logo_url || ''
    
    // Create the HTML content for the cover page
    const coverHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${event.name} - Cover Page</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <link href="https://fonts.googleapis.com/css2?family=Vivaldi&display=swap" rel="stylesheet">
        <style>
          @page {
            size: 5.5in 8.5in; /* Half letter size */
            margin: 0;
            padding: 0;
            @top-left { content: ""; }
            @top-center { content: ""; }
            @top-right { content: ""; }
            @bottom-left { content: ""; }
            @bottom-center { content: ""; }
            @bottom-right { content: ""; }
          }
          
          body {
            margin: 0;
            padding: 0;
            width: 100%;
            height: 100vh;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            align-items: center;
            background: white;
            font-family: 'Vivaldi', cursive;
          }
          
          .cover-content {
            width: 100%;
            height: 50vh; /* Half of the page height */
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            border-top: 3px solid #28a745;
          }
          
          .logo-container {
            margin-bottom: 2rem;
            text-align: center;
          }
          
          .event-logo {
            max-width: 200px;
            max-height: 150px;
            object-fit: contain;
            border-radius: 10px;
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          }
          
          .event-name {
            font-size: 3.2rem;
            font-weight: normal;
            color: #2c3e50;
            text-align: center;
            margin: 0;
            text-shadow: 1px 1px 3px rgba(0,0,0,0.15);
            line-height: 1.1;
            letter-spacing: 1px;
            text-transform: capitalize;
          }
          
          .no-logo {
            width: 200px;
            height: 150px;
            background: #e9ecef;
            border: 2px dashed #adb5bd;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #6c757d;
            font-size: 1rem;
            margin-bottom: 2rem;
          }
          
          @media print {
            body {
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
            }
            
            /* Remove any potential headers/footers */
            @page {
              margin: 0 !important;
              padding: 0 !important;
              size: 5.5in 8.5in !important;
            }
            
            /* Hide any browser-generated content */
            body::before,
            body::after {
              display: none !important;
            }
            
            /* Additional rules to remove headers/footers */
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
          }
          
          /* Additional CSS to prevent headers/footers */
          @media print {
            @page {
              margin: 0 !important;
              padding: 0 !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="cover-content">
          <div class="logo-container">
            ${logoUrl ? 
              `<img src="${logoUrl}" alt="Event Logo" class="event-logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
               <div class="no-logo" style="display: none;">No Logo Available</div>` :
              `<div class="no-logo">No Logo Available</div>`
            }
          </div>
          <h1 class="event-name">${event.name}</h1>
        </div>
      </body>
      </html>
    `
    
    // Write the content to the new window
    printWindow.document.write(coverHTML)
    printWindow.document.close()
    
    // Wait for images to load, then print
    printWindow.onload = () => {
      setTimeout(() => {
        // Try to disable headers and footers programmatically
        try {
          printWindow.document.execCommand('print', false, null)
        } catch (e) {
          // Fallback to regular print
          printWindow.print()
        }
        printWindow.close()
      }, 500)
    }
    
    toast.success('Print dialog opened!')
  }

  const printJudgeInstructions = () => {
    const printWindow = window.open('', '_blank')
    
    const lockedCriteria = event.criteria?.filter(c => c.is_locked) || []
    const unlockedCriteria = event.criteria?.filter(c => !c.is_locked) || []
    const hasSpecialAwards = event.special_awards?.some(a => a.award_type === 'vote') || false
    
    const instructionsHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Judge Instructions - ${event.name}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          @page {
            size: letter;
            margin: 0.75in;
          }
          
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.5;
            color: #333;
            max-width: 8.5in;
            margin: 0 auto;
            padding: 0;
          }
          
          .header {
            text-align: center;
            border-bottom: 3px solid #3b82f6;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          
          .event-logo {
            max-width: 150px;
            max-height: 100px;
            object-fit: contain;
            margin-bottom: 10px;
          }
          
          h1 {
            color: #1e40af;
            font-size: 28px;
            margin: 8px 0;
          }
          
          h2 {
            color: #2563eb;
            font-size: 20px;
            margin-top: 18px;
            margin-bottom: 10px;
            padding-bottom: 6px;
            border-bottom: 2px solid #dbeafe;
          }
          
          h3 {
            color: #3b82f6;
            font-size: 16px;
            margin-top: 14px;
            margin-bottom: 8px;
          }
          
          .important-box {
            background: #fef3c7;
            border-left: 4px solid #f59e0b;
            padding: 12px;
            margin: 14px 0;
            page-break-inside: avoid;
          }
          
          .info-box {
            background: #dbeafe;
            border-left: 4px solid #3b82f6;
            padding: 12px;
            margin: 14px 0;
            page-break-inside: avoid;
          }
          
          .success-box {
            background: #d1fae5;
            border-left: 4px solid #10b981;
            padding: 12px;
            margin: 14px 0;
            page-break-inside: avoid;
          }
          
          .locked-box {
            background: #f3e8ff;
            border-left: 4px solid #a855f7;
            padding: 12px;
            margin: 14px 0;
            page-break-inside: avoid;
          }
          
          ul, ol {
            margin: 8px 0;
            padding-left: 25px;
          }
          
          li {
            margin: 5px 0;
          }
          
          strong {
            color: #1e293b;
          }
          
          .step-number {
            display: inline-block;
            background: #3b82f6;
            color: white;
            width: 28px;
            height: 28px;
            border-radius: 50%;
            text-align: center;
            line-height: 28px;
            font-weight: bold;
            margin-right: 10px;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 12px 0;
            page-break-inside: avoid;
          }
          
          th, td {
            border: 1px solid #cbd5e1;
            padding: 8px;
            text-align: left;
          }
          
          th {
            background: #f1f5f9;
            font-weight: 600;
            color: #1e293b;
          }
          
          .criteria-list {
            margin: 12px 0;
            page-break-inside: avoid;
          }
          
          .criteria-item {
            padding: 8px;
            margin: 6px 0;
            background: #f8fafc;
            border-radius: 6px;
            border: 1px solid #e2e8f0;
          }
          
          .locked-item {
            background: #faf5ff;
            border: 1px solid #e9d5ff;
          }
          
          .footer {
            margin-top: 25px;
            padding-top: 15px;
            border-top: 2px solid #e2e8f0;
            text-align: center;
            color: #64748b;
            font-size: 12px;
          }
          
          @media print {
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .page-break {
              page-break-before: always;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          ${event.logo_url ? `<img src="${event.logo_url}" class="event-logo" alt="Event Logo">` : ''}
          <h1>Judge Instructions</h1>
          <p style="font-size: 18px; color: #64748b; margin: 5px 0;">${event.name}</p>
        </div>

        <div class="important-box">
          <strong>⚠️ IMPORTANT:</strong> Please read all instructions carefully before beginning your scoring. Understanding these requirements will ensure accurate and complete submissions.
        </div>

        <h2>📋 Overview</h2>
        <p>Welcome! As a judge for <strong>${event.name}</strong>, you will be evaluating <strong>${event.candidates?.length || 0} candidates</strong> across ${unlockedCriteria.length > 0 ? `<strong>${unlockedCriteria.length} scoring criteria</strong>` : 'criteria'}${hasSpecialAwards ? ' and special awards' : ''}. This document provides complete instructions for accessing the system and submitting your scores.</p>

        <div class="info-box">
          <strong>✨ New Features:</strong> The judge panel now includes smart recommendations to guide you through the scoring process, automatic card collapse after submissions, and a comprehensive score review and certification system for final verification.
        </div>

        <h2>🔐 Accessing the Judge Panel</h2>
        <ol>
          <li><strong>Navigate to the event website</strong> or scan the QR code provided</li>
          <li>Click on <strong>"Access as Judge"</strong></li>
          <li>Enter your unique <strong>Judge Access Code</strong> (provided separately)</li>
          <li>Enter your <strong>Judge Name</strong> exactly as provided</li>
          <li>Click <strong>"Access Event"</strong></li>
        </ol>

        <div class="info-box">
          <strong>💡 Pro Tip:</strong> Bookmark the judge panel page after logging in for quick access. Your session will remain active as long as you don't close the browser tab.
        </div>

        ${unlockedCriteria.length > 0 ? `
        <h2>⭐ Scoring Criteria (Judge-Scored)</h2>
        <p>You will score candidates on the following ${unlockedCriteria.length} criteria. Each criteria has a maximum score as indicated:</p>
        
        <div class="criteria-list">
          ${unlockedCriteria.map((c, idx) => `
            <div class="criteria-item">
              <strong>Criteria #${idx + 1}: ${c.name}</strong> (Max: ${c.percentage} points)
              ${c.description ? `<br><em style="color: #64748b; font-size: 14px;">${c.description}</em>` : ''}
            </div>
          `).join('')}
        </div>

        <h3>How to Score Each Criteria:</h3>
        <ol>
          <li><strong>Click on a criteria card</strong> to expand the scoring panel</li>
          <li><strong>Review all candidates</strong> listed in the expanded view</li>
          <li><strong>Enter scores</strong> for each candidate (0 to maximum points)
            <ul>
              <li>Use the number input field to enter exact scores</li>
              <li>You can also use the slider for quick adjustments</li>
            </ul>
          </li>
          <li><strong>Lock each score</strong> by clicking the lock icon next to the score input
            <ul>
              <li>🔒 Locked scores appear with a green background</li>
              <li>Locking prevents accidental changes while scrolling</li>
            </ul>
          </li>
          <li><strong>Review all scores</strong> to ensure accuracy</li>
          <li><strong>Click "Submit Scores"</strong> button when ready
            <ul>
              <li><strong>REQUIREMENT:</strong> All scores must be locked before submission</li>
              <li>A confirmation modal will show your scores for final review</li>
            </ul>
          </li>
        </ol>

        <div class="important-box">
          <strong>⚠️ CRITICAL REQUIREMENT:</strong> You MUST lock all candidate scores before the "Submit Scores" button will be enabled. This ensures you have reviewed and confirmed each score before final submission.
        </div>

        <div class="success-box">
          <strong>✅ After Submission:</strong> The criteria card will automatically collapse and be marked as complete with a green checkmark. A recommendation notification will appear suggesting the next item to score. Scores are locked and cannot be edited after submission.
        </div>
        ` : ''}

        ${lockedCriteria.length > 0 ? `
        <div class="page-break"></div>
        <h2>🔒 Locked Criteria (Admin-Managed)</h2>
        <p>The following ${lockedCriteria.length} criteria are <strong>locked and managed by the administrator</strong>. You do NOT need to score these criteria:</p>
        
        <div class="criteria-list">
          ${lockedCriteria.map((c, idx) => `
            <div class="criteria-item locked-item">
              <strong>${c.name}</strong> (Max: ${c.percentage} points) <span style="background: #a855f7; color: white; padding: 2px 8px; border-radius: 12px; font-size: 12px; margin-left: 10px;">LOCKED</span>
              ${c.admin_notes ? `<br><div style="margin-top: 8px; padding: 8px; background: white; border-radius: 4px;"><strong>Admin Note:</strong> ${c.admin_notes}</div>` : ''}
              ${c.description && !c.admin_notes ? `<br><em style="color: #64748b; font-size: 14px;">${c.description}</em>` : ''}
            </div>
          `).join('')}
        </div>

        <div class="locked-box">
          <strong>ℹ️ About Locked Criteria:</strong> These criteria are visible in your judge panel with purple styling but cannot be expanded or scored. They are managed by the admin and will be included in the overall ranking automatically.
        </div>
        ` : ''}

        ${hasSpecialAwards ? `
        <div class="page-break"></div>
        <h2>🏆 Special Awards Voting</h2>
        <p>After completing all criteria scoring, you may be asked to vote for special awards. The process is similar to criteria scoring:</p>
        
        <ol>
          <li><strong>Navigate to the Special Awards section</strong> in your judge panel</li>
          <li><strong>Click on an award card</strong> to expand voting</li>
          <li><strong>Enter scores/votes</strong> for each candidate (0-100 scale)</li>
          <li><strong>Lock all votes</strong> using the lock icon</li>
          <li><strong>Submit votes</strong> when all are locked</li>
        </ol>

        <div class="info-box">
          <strong>📌 Note:</strong> Special awards are separate from judging criteria and may have different evaluation guidelines. Check each award's description for specific voting instructions.
        </div>
        ` : ''}

        <h2>📊 Progress Tracking & Navigation</h2>
        <ul>
          <li><strong>Progress Bar:</strong> Shows your completion percentage at the top of the judge panel</li>
          <li><strong>Completion Count:</strong> Displays completed tasks vs total tasks (e.g., "3/5 completed")</li>
          <li><strong>Criteria Status:</strong> 
            <ul>
              <li>✓ Green checkmark = Completed and submitted</li>
              <li>📝 "Pending" = Not yet submitted</li>
            </ul>
          </li>
          <li><strong>Column View Options:</strong> Use the view toggle to display candidates in 1, 2, or 3 columns for easier scoring</li>
          <li><strong>Smart Recommendations:</strong> After submitting each criteria or award, a notification appears suggesting the next item to score
            <ul>
              <li>Click "Go to [item name]" to navigate directly to the recommended item</li>
              <li>Or dismiss the notification to choose your own path</li>
              <li>Recommendations follow the admin's preferred scoring sequence when available</li>
            </ul>
          </li>
        </ul>

        <h2>🎉 Completion & Score Review</h2>
        <p>After completing ALL criteria and special awards, a green completion banner will appear at the top of your judge panel with the message "All Submissions Complete!"</p>

        <h3>Reviewing Your Scores</h3>
        <ol>
          <li><strong>Click "View All My Scores"</strong> button in the completion banner</li>
          <li><strong>Review all submitted scores</strong> in the detailed modal
            <ul>
              <li>All criteria scores are displayed in organized tables</li>
              <li>All special award scores are shown separately</li>
              <li>Each candidate's score is visible for every criteria/award</li>
              <li>Locked criteria (admin-managed) are not shown in your review</li>
            </ul>
          </li>
        </ol>

        <h2>✅ Score Certification</h2>
        <p>After reviewing your scores, you can certify them to confirm accuracy:</p>
        
        <ol>
          <li><strong>In the score review modal,</strong> scroll to the bottom</li>
          <li><strong>Click "Certify All Scores as Correct"</strong> button
            <ul>
              <li>A confirmation prompt will appear</li>
              <li>This indicates you have reviewed and approved all your scoring</li>
              <li>The admin will be notified of your certification</li>
            </ul>
          </li>
          <li><strong>Confirm your certification</strong> when prompted</li>
        </ol>

        <div class="success-box">
          <strong>🎉 Certification Confirmation:</strong> Once certified, you'll see a green confirmation message with timestamp showing "✓ Scores Certified". This signals to the admin that your judging is complete and verified.
        </div>

        <h2>🔄 Requesting Score Changes</h2>
        <p>If you need to modify submitted scores after completion, you can request permission from the admin:</p>
        
        <h3>How to Request a Change</h3>
        <ol>
          <li><strong>Locate the completed criteria or award</strong> you want to modify</li>
          <li><strong>Click "Request Change"</strong> button below the item header</li>
          <li><strong>Provide a reason</strong> explaining why you need to change your scores
            <ul>
              <li>Be specific and clear about what needs to be changed</li>
              <li>The admin will review your request and reason</li>
            </ul>
          </li>
          <li><strong>Submit your request</strong></li>
        </ol>

        <div class="info-box">
          <strong>📋 Request Status:</strong> You'll see one of these statuses:
          <ul>
            <li><strong>Pending:</strong> Your request is awaiting admin review (amber notification displayed)</li>
            <li><strong>Approved:</strong> Admin has approved - you'll see a green "Edit Scores (Approved)" button</li>
            <li><strong>Rejected:</strong> Request was denied - you can submit a new request if needed</li>
          </ul>
        </div>

        <h3>After Approval</h3>
        <ol>
          <li><strong>Click "Edit Scores (Approved)"</strong> green button</li>
          <li><strong>Re-enter your scores</strong> - previous scores will be cleared</li>
          <li><strong>Lock all new scores</strong> before submitting</li>
          <li><strong>Submit updated scores</strong> when ready</li>
        </ol>

        <div class="important-box">
          <strong>⚠️ Important Notes:</strong>
          <ul>
            <li>Only one change request can be submitted per criteria/award at a time</li>
            <li>Previous scores are deleted when you reopen for editing</li>
            <li>You must re-lock and re-submit all scores</li>
            <li>The approval is single-use - you'll need a new request for future changes</li>
          </ul>
        </div>

        <h2>🔄 General Tips & Best Practices</h2>
        <ul>
          <li><strong>Save your access code:</strong> Keep your judge access code in a safe place</li>
          <li><strong>Stable internet:</strong> Ensure you have a reliable internet connection</li>
          <li><strong>Follow recommendations:</strong> Use the smart recommendation notifications to guide you through the scoring sequence efficiently</li>
          <li><strong>One criteria at a time:</strong> Focus on completing one criteria fully before moving to the next - the card will collapse automatically after submission</li>
          <li><strong>Double-check scores:</strong> Review scores carefully before locking and submitting to avoid needing change requests</li>
          <li><strong>Lock as you go:</strong> Lock each score after entering it to prevent accidental changes while scrolling</li>
          <li><strong>Use the review modal:</strong> Before certifying, carefully review all your scores in the detailed review modal</li>
          <li><strong>Request changes promptly:</strong> If you notice an error, submit a change request as soon as possible</li>
          <li><strong>Take breaks:</strong> You can exit and return anytime - your progress is automatically saved</li>
          <li><strong>Browser compatibility:</strong> Use modern browsers (Chrome, Firefox, Safari, Edge) for best experience</li>
          <li><strong>Mobile friendly:</strong> The judge panel works on tablets and phones</li>
        </ul>

        <h2>❓ Troubleshooting</h2>
        <table>
          <tr>
            <th>Issue</th>
            <th>Solution</th>
          </tr>
          <tr>
            <td>Can't submit scores</td>
            <td>Make sure ALL candidate scores are locked (green background)</td>
          </tr>
          <tr>
            <td>Lost my access code</td>
            <td>Contact the event administrator to retrieve your code</td>
          </tr>
          <tr>
            <td>Scores not saving</td>
            <td>Check your internet connection and try again</td>
          </tr>
          <tr>
            <td>Can't see a criteria</td>
            <td>Criteria may be locked (purple background) and admin-managed</td>
          </tr>
          <tr>
            <td>Want to change submitted scores</td>
            <td>Scores are final after submission - contact admin if correction needed</td>
          </tr>
          <tr>
            <td>Don't see "View All My Scores" button</td>
            <td>Complete ALL criteria and special awards first - button appears when everything is submitted</td>
          </tr>
          <tr>
            <td>Recommendation notification blocking view</td>
            <td>Click the X button to dismiss it, or click "Go to" button to navigate to the suggested item</td>
          </tr>
          <tr>
            <td>Can't submit change request</td>
            <td>Ensure you've provided a reason for the change - the reason field is required</td>
          </tr>
          <tr>
            <td>Change request still pending</td>
            <td>Wait for admin approval - you'll be notified when the status changes</td>
          </tr>
          <tr>
            <td>Edit button doesn't appear after approval</td>
            <td>Refresh the page - the approval notification should then appear</td>
          </tr>
        </table>

        <h2>📞 Support</h2>
        <p>If you encounter any issues or have questions during the judging process:</p>
        <ul>
          <li>Contact the event administrator immediately</li>
          <li>Do not attempt to create a new judge account</li>
          <li>Report any technical errors with as much detail as possible</li>
        </ul>

        <div class="footer">
          <p><strong>${event.name}</strong></p>
          <p>Judge Instructions Document • Generated on ${new Date().toLocaleDateString()}</p>
          <p>Please keep this document for reference throughout the judging process</p>
        </div>
      </body>
      </html>
    `
    
    printWindow.document.write(instructionsHTML)
    printWindow.document.close()
    
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print()
      }, 500)
    }
    
    toast.success('Judge instructions ready to print!')
  }

  const getRankingOptions = () => {
    const totalCandidates = event?.candidates?.length || 0
    const options = []
    
    if (totalCandidates >= 1) {
      options.push({ value: 'winner', label: event?.name || 'Top 1' })
    }

    const getOrdinal = (n) => {
      const rem10 = n % 10
      const rem100 = n % 100
      if (rem10 === 1 && rem100 !== 11) return `${n}st`
      if (rem10 === 2 && rem100 !== 12) return `${n}nd`
      if (rem10 === 3 && rem100 !== 13) return `${n}rd`
      return `${n}th`
    }

    for (let rank = 2; rank <= totalCandidates; rank++) {
      const runnerUpNumber = rank - 1
      const ordinal = getOrdinal(runnerUpNumber)
      options.push({
        value: `${ordinal.toLowerCase()}-runner-up`,
        label: `${ordinal} Runner-up`
      })
    }
    
    return options
  }

  const getWinnerForRanking = (ranking) => {
    const candidateScores = calculateCandidateScores()
    if (!candidateScores.length) return null
    
    let index = 0
    if (ranking === 'winner') {
      index = 0
    } else if (typeof ranking === 'string' && ranking.endsWith('-runner-up')) {
      const numPart = ranking.replace('-runner-up', '').replace(/(st|nd|rd|th)$/i, '')
      const parsed = parseInt(numPart, 10)
      if (!isNaN(parsed) && parsed >= 1) {
        index = parsed
      }
    }
    
    return candidateScores[index] || null
  }

  const getWinnerForAward = (awardId) => {
    const award = specialAwards.find(a => a.id === awardId)
    if (!award) return null
    
    // Get all votes for this award
    const awardVotes = event?.scores?.filter(score => 
      score.special_award_id === awardId
    ) || []
    
    if (!awardVotes.length) return null
    
    // Calculate total scores for each candidate
    const candidateScores = {}
    awardVotes.forEach(vote => {
      if (!candidateScores[vote.candidate_id]) {
        candidateScores[vote.candidate_id] = 0
      }
      candidateScores[vote.candidate_id] += vote.score
    })
    
    // Find candidate with highest score
    const winnerId = Object.keys(candidateScores).reduce((a, b) => 
      candidateScores[a] > candidateScores[b] ? a : b
    )
    
    const winner = event?.candidates?.find(c => c.id === winnerId)
    return winner ? { candidate: winner, score: candidateScores[winnerId] } : null
  }

  const startAnimation = () => {
    if (isAnimating) return
    
    setIsAnimating(true)
    setShowWinner(false)
    setCountdown(3)
    
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval)
          setShowWinner(true)
          
          // Get winner data
          let winner = null
          if (displayMode === 'rankings') {
            winner = getWinnerForRanking(selectedRanking)
          } else {
            winner = getWinnerForAward(selectedAward)
          }
          
          setWinnerData(winner)
          setIsAnimating(false)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const exitFullScreen = () => {
    setIsFullScreen(false)
    setIsAnimating(false)
    setShowWinner(false)
    setCountdown(0)
    setWinnerData(null)
  }

  // Handle spacebar key press
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.code === 'Space' && isFullScreen && !isAnimating) {
        e.preventDefault()
        startAnimation()
      }
      if (e.key === 'Escape' && isFullScreen) {
        exitFullScreen()
      }
    }

    if (isFullScreen) {
      document.addEventListener('keydown', handleKeyPress)
      return () => document.removeEventListener('keydown', handleKeyPress)
    }
  }, [isFullScreen, isAnimating])

  const enterFullScreen = () => {
    if (displayMode === 'rankings' && !selectedRanking) {
      toast.error('Please select a ranking position')
      return
    }
    if (displayMode === 'awards' && !selectedAward) {
      toast.error('Please select an award')
      return
    }
    
    setIsFullScreen(true)
  }

  // Awards Functions
  const fetchAwardCategories = async () => {
    try {
      const { data, error } = await supabase
        .from(TABLES.SPECIAL_AWARD_CATEGORIES)
        .select('*')
        .eq('event_id', eventId)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: true })

      if (error) throw error
      setAwardCategories(data || [])
    } catch (error) {
      console.error('Error fetching award categories:', error)
    }
  }

  const fetchSpecialAwards = async () => {
    try {
      const { data, error } = await supabase
        .from(TABLES.SPECIAL_AWARDS)
        .select(`
          *,
          assigned_candidate:candidates(name, candidate_number, representation)
        `)
        .eq('event_id', eventId)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: true })

      if (error) throw error
      setSpecialAwards(data || [])

      // Also fetch special award votes for this event
      // We need to get votes through the special_awards table since votes don't have event_id directly
      const { data: votesData, error: votesError } = await supabase
        .from(TABLES.SPECIAL_AWARD_VOTES)
        .select(`
          *,
          special_awards!inner(event_id),
          judges(name, judge_number),
          candidates(name, candidate_number, representation)
        `)
        .eq('special_awards.event_id', eventId)

      if (votesError) {
        console.error('Error fetching special award votes:', votesError)
      } else {
        // Update the event state to include special award votes
        setEvent(prev => prev ? { ...prev, special_award_votes: votesData || [] } : null)
      }
    } catch (error) {
      console.error('Error fetching special awards:', error)
      toast.error('Failed to load special awards')
    }
  }

  const handleAddAward = async () => {
    try {
      const { data, error } = await supabase
        .from(TABLES.SPECIAL_AWARDS)
        .insert({
          event_id: eventId,
          name: newAward.name,
          description: newAward.description,
          award_type: newAward.award_type,
          assigned_candidate_id: newAward.award_type === 'assigned' ? newAward.assigned_candidate_id : null,
          category_id: newAward.category_id || null,
          criteria_id: newAward.award_type === 'criteria_based' ? newAward.criteria_id : null,
          overall_ranking_position: newAward.award_type === 'overall_ranking' ? newAward.overall_ranking_position : null,
          add_to_overall: (newAward.award_type === 'vote' || newAward.award_type === 'assigned') ? newAward.add_to_overall : false,
          overall_points: (newAward.award_type === 'vote' || newAward.award_type === 'assigned') && newAward.add_to_overall ? parseFloat(newAward.overall_points) || 0 : 0
        })
        .select()

      if (error) throw error

      // Log activity
      await logActivity('create', 'award', data[0]?.id, newAward.name, {
        description: `Created ${newAward.award_type} award`,
        award_type: newAward.award_type
      })

      toast.success('Special award created successfully!')
      setShowAddAwardModal(false)
      setNewAward({
        name: '',
        description: '',
        award_type: 'assigned',
        assigned_candidate_id: '',
        category_id: '',
        criteria_id: '',
        overall_ranking_position: '',
        add_to_overall: false,
        overall_points: 0
      })
      await fetchSpecialAwards()
    } catch (error) {
      console.error('Error creating special award:', error)
      toast.error('Failed to create special award')
    }
  }

  const handleEditAward = async () => {
    try {
      const { error } = await supabase
        .from(TABLES.SPECIAL_AWARDS)
        .update({
          name: editingAward.name,
          description: editingAward.description,
          award_type: editingAward.award_type,
          assigned_candidate_id: editingAward.award_type === 'assigned' ? editingAward.assigned_candidate_id : null,
          category_id: editingAward.category_id || null,
          criteria_id: editingAward.award_type === 'criteria_based' ? editingAward.criteria_id : null,
          overall_ranking_position: editingAward.award_type === 'overall_ranking' ? editingAward.overall_ranking_position : null,
          add_to_overall: (editingAward.award_type === 'vote' || editingAward.award_type === 'assigned') ? editingAward.add_to_overall : false,
          overall_points: (editingAward.award_type === 'vote' || editingAward.award_type === 'assigned') && editingAward.add_to_overall ? parseFloat(editingAward.overall_points) || 0 : 0
        })
        .eq('id', editingAward.id)

      if (error) throw error

      // Log activity
      await logActivity('update', 'award', editingAward.id, editingAward.name, {
        description: `Updated ${editingAward.award_type} award`,
        award_type: editingAward.award_type
      })

      toast.success('Special award updated successfully!')
      setShowEditAwardModal(false)
      setEditingAward(null)
      await fetchSpecialAwards()
    } catch (error) {
      console.error('Error updating special award:', error)
      toast.error('Failed to update special award')
    }
  }

  const handleDeleteAward = async (awardId) => {
    if (!window.confirm('Are you sure you want to delete this special award?')) return

    try {
      // Get award details before deleting
      const award = specialAwards.find(a => a.id === awardId)
      
      const { error} = await supabase
        .from(TABLES.SPECIAL_AWARDS)
        .delete()
        .eq('id', awardId)

      if (error) throw error

      // Log activity
      if (award) {
        await logActivity('delete', 'award', awardId, award.name, {
          description: `Deleted ${award.award_type} award`,
          award_type: award.award_type
        })
      }

      toast.success('Special award deleted successfully!')
      await fetchSpecialAwards()
    } catch (error) {
      console.error('Error deleting special award:', error)
      toast.error('Failed to delete special award')
    }
  }

  // Criteria Reordering Functions
  const handleStartReorderCriteria = () => {
    setCriteriaOrder([...event.criteria])
    setIsReorderingCriteria(true)
  }

  const handleCancelReorderCriteria = () => {
    setIsReorderingCriteria(false)
    setCriteriaOrder([])
    setDraggedCriteria(null)
  }

  const handleDragStart = (e, criteria, index) => {
    setDraggedCriteria({ criteria, index })
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    
    if (draggedCriteria && draggedCriteria.index !== index) {
      const newOrder = [...criteriaOrder]
      const draggedItem = newOrder[draggedCriteria.index]
      newOrder.splice(draggedCriteria.index, 1)
      newOrder.splice(index, 0, draggedItem)
      setCriteriaOrder(newOrder)
      setDraggedCriteria({ criteria: draggedItem, index })
    }
  }

  const handleDragEnd = () => {
    setDraggedCriteria(null)
  }

  const handleSaveCriteriaOrder = async () => {
    try {
      // Update display_order for each criteria
      const updates = criteriaOrder.map((criteria, index) => 
        supabase
          .from(TABLES.CRITERIA)
          .update({ display_order: index })
          .eq('id', criteria.id)
      )

      await Promise.all(updates)

      toast.success('Criteria order updated successfully!')
      setIsReorderingCriteria(false)
      await fetchEventData()
    } catch (error) {
      console.error('Error updating criteria order:', error)
      toast.error('Failed to update criteria order')
    }
  }

  // Award Reordering Functions
  const handleStartReorderAwards = () => {
    // Only reorder vote-type awards
    const voteAwards = specialAwards.filter(a => a.award_type === 'vote')
    setAwardsOrder(voteAwards)
    setIsReorderingAwards(true)
  }

  const handleCancelReorderAwards = () => {
    setIsReorderingAwards(false)
    setAwardsOrder([])
    setDraggedAward(null)
  }

  const handleAwardDragStart = (e, award, index) => {
    setDraggedAward({ award, index })
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleAwardDragOver = (e, targetIndex) => {
    e.preventDefault()
    
    if (!draggedAward || draggedAward.index === targetIndex) return

    const newOrder = [...awardsOrder]
    const draggedItem = newOrder[draggedAward.index]
    
    newOrder.splice(draggedAward.index, 1)
    newOrder.splice(targetIndex, 0, draggedItem)
    
    setAwardsOrder(newOrder)
    setDraggedAward({ ...draggedAward, index: targetIndex })
  }

  const handleAwardDragEnd = () => {
    setDraggedAward(null)
  }

  const handleSaveAwardOrder = async () => {
    try {
      // Update display_order for each award
      const updates = awardsOrder.map((award, index) =>
        supabase
          .from(TABLES.SPECIAL_AWARDS)
          .update({ display_order: index })
          .eq('id', award.id)
      )

      await Promise.all(updates)

      toast.success('Award order updated successfully!')
      setIsReorderingAwards(false)
      await fetchEventData()
    } catch (error) {
      console.error('Error updating award order:', error)
      toast.error('Failed to update award order')
    }
  }

  // Unified Scoring Sequence Functions
  const handleStartReorderScoringSequence = () => {
    // Build the unified sequence from unlocked criteria and vote-type awards
    const unlockedCriteria = (event.criteria || []).filter(c => !c.is_locked)
    const voteAwards = specialAwards.filter(a => a.award_type === 'vote')
    
    // Check if event has an existing scoring_sequence
    if (event.scoring_sequence && Array.isArray(event.scoring_sequence) && event.scoring_sequence.length > 0) {
      // Use existing sequence but filter out any items that no longer exist
      const validSequence = event.scoring_sequence.filter(item => {
        if (item.type === 'criteria') {
          return unlockedCriteria.some(c => c.id === item.id)
        } else if (item.type === 'award') {
          return voteAwards.some(a => a.id === item.id)
        }
        return false
      }).map(item => {
        if (item.type === 'criteria') {
          const criteria = unlockedCriteria.find(c => c.id === item.id)
          return { type: 'criteria', id: item.id, name: criteria?.name, data: criteria }
        } else {
          const award = voteAwards.find(a => a.id === item.id)
          return { type: 'award', id: item.id, name: award?.name, data: award }
        }
      })
      
      // Add any new items that aren't in the sequence
      const existingIds = validSequence.map(s => s.id)
      const newCriteria = unlockedCriteria
        .filter(c => !existingIds.includes(c.id))
        .map(c => ({ type: 'criteria', id: c.id, name: c.name, data: c }))
      const newAwards = voteAwards
        .filter(a => !existingIds.includes(a.id))
        .map(a => ({ type: 'award', id: a.id, name: a.name, data: a }))
      
      setScoringSequenceOrder([...validSequence, ...newCriteria, ...newAwards])
    } else {
      // Build default sequence: all criteria first (by display_order), then all awards (by display_order)
      const sequence = [
        ...unlockedCriteria.map(c => ({ type: 'criteria', id: c.id, name: c.name, data: c })),
        ...voteAwards.map(a => ({ type: 'award', id: a.id, name: a.name, data: a }))
      ]
      setScoringSequenceOrder(sequence)
    }
    
    setIsReorderingScoringSequence(true)
  }

  const handleCancelReorderScoringSequence = () => {
    setIsReorderingScoringSequence(false)
    setScoringSequenceOrder([])
    setDraggedSequenceItem(null)
  }

  const handleSequenceDragStart = (e, item, index) => {
    setDraggedSequenceItem({ item, index })
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleSequenceDragOver = (e, targetIndex) => {
    e.preventDefault()
    
    if (!draggedSequenceItem || draggedSequenceItem.index === targetIndex) return

    const newOrder = [...scoringSequenceOrder]
    const draggedItem = newOrder[draggedSequenceItem.index]
    
    newOrder.splice(draggedSequenceItem.index, 1)
    newOrder.splice(targetIndex, 0, draggedItem)
    
    setScoringSequenceOrder(newOrder)
    setDraggedSequenceItem({ ...draggedSequenceItem, index: targetIndex })
  }

  const handleSequenceDragEnd = () => {
    setDraggedSequenceItem(null)
  }

  const handleSaveScoringSequence = async () => {
    try {
      // Save the unified sequence to the event
      const sequenceData = scoringSequenceOrder.map(item => ({
        type: item.type,
        id: item.id
      }))

      const { error } = await supabase
        .from(TABLES.EVENTS)
        .update({ scoring_sequence: sequenceData })
        .eq('id', eventId)

      if (error) throw error

      toast.success('Scoring sequence saved successfully!')
      setIsReorderingScoringSequence(false)
      await fetchEventData()
    } catch (error) {
      console.error('Error saving scoring sequence:', error)
      toast.error('Failed to save scoring sequence')
    }
  }

  // Backup and Restore Functions (Scores Only)
  const handleBackupEvent = async () => {
    try {
      if (!event) {
        toast.error('No event data to backup')
        return
      }

      // Only backup scores and special award votes
      const backupData = {
        version: '2.0', // Changed to 2.0 for scores-only backup
        type: 'scores_only',
        timestamp: new Date().toISOString(),
        eventInfo: {
          name: event.name,
          criteriaCount: event.criteria?.length || 0,
          candidatesCount: event.candidates?.length || 0,
          judgesCount: event.judges?.length || 0
        },
        scores: event.scores?.map(score => ({
          judge_number: event.judges?.find(j => j.id === score.judge_id)?.judge_number,
          candidate_number: event.candidates?.find(c => c.id === score.candidate_id)?.candidate_number,
          criteria_name: event.criteria?.find(cr => cr.id === score.criteria_id)?.name,
          score: score.score
        })) || [],
        specialAwardVotes: event.special_award_votes?.map(vote => ({
          judge_number: event.judges?.find(j => j.id === vote.judge_id)?.judge_number,
          candidate_number: event.candidates?.find(c => c.id === vote.candidate_id)?.candidate_number,
          award_name: specialAwards?.find(a => a.id === vote.special_award_id)?.name,
          score: vote.score
        })) || []
      }

      // Create JSON file
      const dataStr = JSON.stringify(backupData, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      
      // Trigger download
      const url = URL.createObjectURL(dataBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${event.name.replace(/\s+/g, '_')}_scores_backup_${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      toast.success('Scores backup downloaded successfully!')
    } catch (error) {
      console.error('Error creating backup:', error)
      toast.error('Failed to create backup')
    }
  }

  // Copy Event Functions (Export Template)
  const handleCopyEventExport = async () => {
    try {
      if (!event) {
        toast.error('No event data to copy')
        return
      }

      // Fetch latest awards data to ensure it's included
      console.log('Fetching awards for event ID:', eventId)
      
      const { data: categoriesData, error: categoriesError } = await supabase
        .from(TABLES.SPECIAL_AWARD_CATEGORIES)
        .select('*')
        .eq('event_id', eventId)
        .order('display_order', { ascending: true })

      console.log('Categories fetch result:', { 
        data: categoriesData, 
        error: categoriesError,
        count: categoriesData?.length || 0 
      })

      if (categoriesError) {
        console.error('Error fetching categories for export:', categoriesError)
      }

      const { data: awardsData, error: awardsError } = await supabase
        .from(TABLES.SPECIAL_AWARDS)
        .select('*')
        .eq('event_id', eventId)
        .order('display_order', { ascending: true })

      console.log('Awards fetch result:', { 
        data: awardsData, 
        error: awardsError,
        count: awardsData?.length || 0 
      })

      if (awardsError) {
        console.error('Error fetching awards for export:', awardsError)
      }

      const exportCategories = categoriesData || []
      const exportAwards = awardsData || []
      
      console.log('State variables check:', {
        awardCategoriesFromState: awardCategories?.length || 0,
        specialAwardsFromState: specialAwards?.length || 0
      })

      // Export event structure without scores (template for new event)
      const templateData = {
        version: '3.0',
        type: 'event_template',
        timestamp: new Date().toISOString(),
        event: {
          name: event.name,
          logo_url: event.logo_url,
          num_judges: event.num_judges
        },
        criteria: event.criteria?.map(c => ({
          name: c.name,
          description: c.description,
          percentage: c.percentage,
          display_order: c.display_order || 0
        })) || [],
        candidates: event.candidates?.map(c => ({
          name: c.name,
          representation: c.representation,
          candidate_number: c.candidate_number
        })) || [],
        judges: event.judges?.map(j => ({
          judge_number: j.judge_number
          // Note: judge names and access codes NOT included - will be regenerated
        })) || [],
        specialAwardCategories: exportCategories.map(cat => ({
          name: cat.name,
          description: cat.description,
          display_order: cat.display_order || 0
        })),
        specialAwards: exportAwards.map(award => ({
          name: award.name,
          description: award.description,
          award_type: award.award_type,
          category_name: exportCategories.find(c => c.id === award.category_id)?.name,
          criteria_name: event.criteria?.find(c => c.id === award.criteria_id)?.name,
          overall_ranking_position: award.overall_ranking_position,
          assigned_candidate_number: event.candidates?.find(c => c.id === award.assigned_candidate_id)?.candidate_number,
          display_order: award.display_order || 0,
          add_to_overall: award.add_to_overall || false,
          overall_points: award.overall_points || 0
        }))
      }

      // Log what's being exported for debugging
      console.log('Exporting template with:', {
        categories: templateData.specialAwardCategories.length,
        awards: templateData.specialAwards.length,
        criteria: templateData.criteria.length,
        candidates: templateData.candidates.length,
        judges: templateData.judges.length
      })
      
      console.log('Exported awards detail:', templateData.specialAwards)
      console.log('Exported categories detail:', templateData.specialAwardCategories)

      // Create JSON file
      const dataStr = JSON.stringify(templateData, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      
      // Trigger download
      const url = URL.createObjectURL(dataBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${event.name.replace(/\s+/g, '_')}_template_${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      toast.success(`Event template downloaded! (${exportCategories.length} categories, ${exportAwards.length} awards)`)
    } catch (error) {
      console.error('Error creating template:', error)
      toast.error('Failed to create template')
    }
  }

  const handleCreateFromTemplate = async () => {
    if (!copyEventFile || !newEventName.trim()) {
      toast.error('Please provide event name and select a template file')
      return
    }

    try {
      setIsCopying(true)
      
      // Read the file
      const fileContent = await copyEventFile.text()
      const templateData = JSON.parse(fileContent)

      // Validate template data
      if (!templateData.version || templateData.type !== 'event_template') {
        throw new Error('Invalid template file format. Please use an event template file.')
      }

      // Log what's being imported
      console.log('Importing template with:', {
        categories: templateData.specialAwardCategories?.length || 0,
        awards: templateData.specialAwards?.length || 0,
        criteria: templateData.criteria?.length || 0,
        candidates: templateData.candidates?.length || 0,
        judges: templateData.judges?.length || 0
      })

      // Create new event
      const { data: newEvent, error: eventError } = await supabase
        .from(TABLES.EVENTS)
        .insert({
          name: newEventName.trim(),
          logo_url: templateData.event.logo_url,
          num_judges: templateData.event.num_judges,
          created_by: (await supabase.auth.getUser()).data.user?.id,
          status: 'active'
        })
        .select()
        .single()

      if (eventError) throw eventError

      const newEventId = newEvent.id

      // Create criteria
      const criteriaIdMap = {}
      for (const criterion of templateData.criteria || []) {
        const { data, error } = await supabase
          .from(TABLES.CRITERIA)
          .insert({
            event_id: newEventId,
            name: criterion.name,
            description: criterion.description,
            percentage: criterion.percentage,
            display_order: criterion.display_order || 0
          })
          .select()
          .single()

        if (error) throw error
        criteriaIdMap[criterion.name] = data.id
      }

      // Create candidates
      const candidateIdMap = {}
      for (const candidate of templateData.candidates || []) {
        const { data, error } = await supabase
          .from(TABLES.CANDIDATES)
          .insert({
            event_id: newEventId,
            name: candidate.name,
            representation: candidate.representation,
            candidate_number: candidate.candidate_number
          })
          .select()
          .single()

        if (error) throw error
        candidateIdMap[candidate.candidate_number] = data.id
      }

      // Create judges with NEW access codes
      for (const judge of templateData.judges || []) {
        const newAccessCode = Math.random().toString(36).substring(2, 10).toUpperCase()
        
        const { error } = await supabase
          .from(TABLES.JUDGES)
          .insert({
            event_id: newEventId,
            name: judge.name,
            judge_number: judge.judge_number,
            judge_access_code: newAccessCode,
            completed_criteria: [],
            submitted_special_awards: []
          })

        if (error) throw error
      }

      // Create award categories
      const categoryIdMap = {}
      for (const category of templateData.specialAwardCategories || []) {
        const { data, error } = await supabase
          .from(TABLES.SPECIAL_AWARD_CATEGORIES)
          .insert({
            event_id: newEventId,
            name: category.name,
            description: category.description,
            display_order: category.display_order || 0
          })
          .select()
          .single()

        if (error) throw error
        categoryIdMap[category.name] = data.id
      }

      // Create special awards
      console.log('Starting award creation:', {
        awardsToCreate: templateData.specialAwards?.length || 0,
        awardsData: templateData.specialAwards,
        categoryIdMap,
        criteriaIdMap,
        candidateIdMap
      })

      for (const award of templateData.specialAwards || []) {
        console.log('Creating award:', award.name, {
          category_name: award.category_name,
          category_id: categoryIdMap[award.category_name],
          criteria_name: award.criteria_name,
          criteria_id: criteriaIdMap[award.criteria_name]
        })

        const awardData = {
          event_id: newEventId,
          name: award.name,
          description: award.description,
          award_type: award.award_type,
          category_id: categoryIdMap[award.category_name] || null,
          criteria_id: criteriaIdMap[award.criteria_name] || null,
          overall_ranking_position: award.overall_ranking_position,
          assigned_candidate_id: candidateIdMap[award.assigned_candidate_number] || null,
          display_order: award.display_order || 0,
          add_to_overall: award.add_to_overall || false,
          overall_points: award.overall_points || 0
        }

        console.log('Inserting award data:', awardData)

        const { data: insertedAward, error } = await supabase
          .from(TABLES.SPECIAL_AWARDS)
          .insert(awardData)
          .select()

        if (error) {
          console.error('Error creating special award:', award.name, error)
          throw error
        }
        
        console.log('Award created successfully:', insertedAward)
      }

      console.log('Import completed successfully:', {
        eventId: newEventId,
        categoriesCreated: templateData.specialAwardCategories?.length || 0,
        awardsCreated: templateData.specialAwards?.length || 0
      })

      toast.success(`Event "${newEventName}" created successfully! (${templateData.criteria?.length || 0} criteria, ${templateData.candidates?.length || 0} candidates, ${templateData.specialAwards?.length || 0} awards)`)
      setShowCopyEventModal(false)
      setCopyEventFile(null)
      setNewEventName('')
      
      // Navigate to new event
      navigate(`/admin/events/${newEventId}`)
      
    } catch (error) {
      console.error('Error creating event from template:', error)
      toast.error(`Failed to create event: ${error.message}`)
    } finally {
      setIsCopying(false)
    }
  }

  const handleRestoreEvent = async () => {
    if (!restoreFile) {
      toast.error('Please select a backup file')
      return
    }

    try {
      setIsRestoring(true)
      
      // Read the file
      const fileContent = await restoreFile.text()
      const backupData = JSON.parse(fileContent)

      // Validate backup data
      if (!backupData.version || backupData.type !== 'scores_only') {
        throw new Error('Invalid backup file format. Please use a scores-only backup file.')
      }

      // Confirm restoration
      if (!window.confirm(
        `This will restore scores from "${backupData.eventInfo.name}":\n\n` +
        `- ${backupData.scores?.length || 0} criteria scores\n` +
        `- ${backupData.specialAwardVotes?.length || 0} special award votes\n\n` +
        `Current scores will be REPLACED.\n` +
        `Criteria, candidates, and judges will remain unchanged.\n\n` +
        `Continue?`
      )) {
        setIsRestoring(false)
        return
      }

      // Delete existing scores only (not criteria, candidates, or judges)
      await Promise.all([
        supabase.from(TABLES.SCORES).delete().eq('event_id', eventId),
        supabase.from(TABLES.SPECIAL_AWARD_VOTES).delete().eq('event_id', eventId)
      ])

      let restoredCount = 0
      let skippedCount = 0

      // Restore criteria scores by matching judge_number, candidate_number, and criteria_name
      if (backupData.scores?.length > 0) {
        for (const scoreData of backupData.scores) {
          // Find matching judge by judge_number
          const judge = event.judges?.find(j => j.judge_number === scoreData.judge_number)
          
          // Find matching candidate by candidate_number
          const candidate = event.candidates?.find(c => c.candidate_number === scoreData.candidate_number)
          
          // Find matching criteria by name
          const criteria = event.criteria?.find(cr => cr.name === scoreData.criteria_name)

          if (judge && candidate && criteria) {
            const { error } = await supabase
              .from(TABLES.SCORES)
              .insert({
                event_id: eventId,
                judge_id: judge.id,
                candidate_id: candidate.id,
                criteria_id: criteria.id,
                score: scoreData.score
              })

            if (error) {
              console.warn('Failed to restore score:', scoreData, error)
              skippedCount++
            } else {
              restoredCount++
            }
          } else {
            console.warn('Could not match score data:', {
              scoreData,
              foundJudge: !!judge,
              foundCandidate: !!candidate,
              foundCriteria: !!criteria
            })
            skippedCount++
          }
        }
      }

      // Restore special award votes by matching judge_number, candidate_number, and award_name
      if (backupData.specialAwardVotes?.length > 0) {
        for (const voteData of backupData.specialAwardVotes) {
          const judge = event.judges?.find(j => j.judge_number === voteData.judge_number)
          const candidate = event.candidates?.find(c => c.candidate_number === voteData.candidate_number)
          const award = specialAwards?.find(a => a.name === voteData.award_name)

          if (judge && candidate && award) {
            const { error } = await supabase
              .from(TABLES.SPECIAL_AWARD_VOTES)
              .insert({
                event_id: eventId,
                judge_id: judge.id,
                candidate_id: candidate.id,
                special_award_id: award.id,
                score: voteData.score || 1
              })

            if (error) {
              console.warn('Failed to restore vote:', voteData, error)
              skippedCount++
            } else {
              restoredCount++
            }
          } else {
            console.warn('Could not match vote data:', {
              voteData,
              foundJudge: !!judge,
              foundCandidate: !!candidate,
              foundAward: !!award
            })
            skippedCount++
          }
        }
      }

      const message = skippedCount > 0 
        ? `Scores restored: ${restoredCount} successful, ${skippedCount} skipped (no match found)`
        : `All ${restoredCount} scores restored successfully!`

      toast.success(message)
      setShowRestoreModal(false)
      setRestoreFile(null)
      await fetchEventData()
      
    } catch (error) {
      console.error('Error restoring scores:', error)
      toast.error(`Failed to restore scores: ${error.message}`)
    } finally {
      setIsRestoring(false)
    }
  }

  const handlePrintAward = (award) => {
    // Get category name
    const category = awardCategories.find(cat => cat.id === award.category_id)
    const categoryName = category ? category.name : 'Uncategorized'
    
    // Get winner and score
    let winnerInfo = ''
    let scoreInfo = ''
    
    if (award.award_type === 'assigned' && award.assigned_candidate) {
      winnerInfo = `
        <div class="winner-section">
          <div class="winner-number">#${award.assigned_candidate.candidate_number}</div>
          <div class="winner-name">${award.assigned_candidate.name}</div>
          ${award.assigned_candidate.representation ? `<div class="winner-representation">${award.assigned_candidate.representation}</div>` : ''}
        </div>
      `
    } else if (award.award_type === 'criteria_based' && award.criteria_id) {
      // Calculate criteria winners (including ties)
      const candidateScores = calculateCandidateScores()
      const scoresWithCandidates = candidateScores.map(cs => ({
        candidate: cs.candidate,
        score: cs.criteriaScores?.[award.criteria_id] || 0
      })).filter(item => item.score > 0)
      
      if (scoresWithCandidates.length > 0) {
        const maxScore = Math.max(...scoresWithCandidates.map(item => item.score))
        const winners = scoresWithCandidates.filter(item => item.score === maxScore)
        
        winnerInfo = winners.map(winner => `
          <div class="winner-section">
            <div class="winner-number">#${winner.candidate.candidate_number}</div>
            <div class="winner-name">${winner.candidate.name}</div>
            ${winner.candidate.representation ? `<div class="winner-representation">${winner.candidate.representation}</div>` : ''}
          </div>
        `).join('')
        
        scoreInfo = `
          <div class="score-badge">${maxScore.toFixed(2)} points</div>
        `
      }
    } else if (award.award_type === 'vote') {
      // Get vote scores and find all winners (including ties)
      const awardScores = event.special_award_votes?.filter(vote => vote.special_award_id === award.id) || []
      const candidateScores = {}
      awardScores.forEach(score => {
        candidateScores[score.candidate_id] = (candidateScores[score.candidate_id] || 0) + (score.score || 0)
      })
      
      const candidatesWithScores = event.candidates
        .map(candidate => ({
          ...candidate,
          totalScore: candidateScores[candidate.id] || 0
        }))
        .filter(c => c.totalScore > 0)
        .sort((a, b) => b.totalScore - a.totalScore)
      
      if (candidatesWithScores.length > 0) {
        const maxScore = candidatesWithScores[0].totalScore
        const winners = candidatesWithScores.filter(c => c.totalScore === maxScore)
        
        winnerInfo = winners.map(winner => `
          <div class="winner-section">
            <div class="winner-number">#${winner.candidate_number}</div>
            <div class="winner-name">${winner.name}</div>
            ${winner.representation ? `<div class="winner-representation">${winner.representation}</div>` : ''}
          </div>
        `).join('')
        
        scoreInfo = `
          <div class="score-badge">${maxScore} points</div>
        `
      }
    } else if (award.award_type === 'overall_ranking' && award.overall_ranking_position) {
      // Use the existing getWinnerForRanking function which handles string values correctly
      const winnerResult = getWinnerForRanking(award.overall_ranking_position)
      
      if (winnerResult) {
        // Get all candidates with the same score (handles ties)
        const candidateScores = calculateCandidateScores()
        const targetScore = winnerResult.totalScore
        const winners = candidateScores.filter(cs => cs.totalScore === targetScore)
        
        winnerInfo = winners.map(winner => `
          <div class="winner-section">
            <div class="winner-number">#${winner.candidate.candidate_number}</div>
            <div class="winner-name">${winner.candidate.name}</div>
            ${winner.candidate.representation ? `<div class="winner-representation">${winner.candidate.representation}</div>` : ''}
          </div>
        `).join('')
        
        scoreInfo = `
          <div class="score-badge">${targetScore.toFixed(2)} points</div>
        `
      }
    }
    
    const printWindow = window.open('', '_blank')
    printWindow.document.write(`
      <html>
        <head>
          <title>${award.name}</title>
          <style>
            @page {
              size: letter;
              margin: 0;
            }
            body { 
              font-family: 'Arial', sans-serif; 
              padding: 0;
              margin: 0;
              background: #ffffff;
              width: 8.5in;
              height: 11in;
              display: flex;
              align-items: flex-end;
              justify-content: center;
            }
            .certificate {
              border: 4px solid #10b981;
              padding: 30px 40px;
              border-radius: 8px;
              text-align: center;
              width: 7.5in;
              height: 4.5in;
              display: flex;
              flex-direction: column;
              justify-content: center;
              box-sizing: border-box;
              margin: 0 auto;
            }
            .category {
              font-size: 11px;
              color: #6b7280;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              margin-bottom: 12px;
              font-weight: 500;
            }
            h1 { 
              color: #1a1a1a; 
              font-size: 32px;
              margin: 0 0 25px 0;
              font-weight: bold;
              line-height: 1.1;
            }
            .winner-section {
              margin-top: 15px;
              padding: 20px 25px;
              background: #f0fdf4;
              border-radius: 6px;
            }
            .winner-section + .winner-section {
              margin-top: 10px;
            }
            .winner-number {
              font-size: 48px;
              font-weight: bold;
              color: #10b981;
              margin-bottom: 12px;
              line-height: 1;
            }
            .winner-name {
              font-size: 26px;
              font-weight: bold;
              color: #1a1a1a;
              margin-bottom: 8px;
              line-height: 1.1;
            }
            .winner-representation {
              font-size: 18px;
              color: #4b5563;
              line-height: 1.3;
            }
            .score-badge {
              display: inline-block;
              margin-top: 15px;
              padding: 8px 18px;
              background: #10b981;
              color: white;
              font-size: 16px;
              font-weight: 600;
              border-radius: 6px;
            }
            @media print { 
              button { display: none !important; }
              body { 
                padding: 0;
                margin: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="certificate">
            <div class="category">${categoryName}</div>
            <h1>🏆 ${award.name}</h1>
            
            ${winnerInfo}
            ${scoreInfo}
          </div>
        </body>
      </html>
    `)
    printWindow.document.close()
    
    // Automatically open print dialog when window loads
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print()
        printWindow.close()
      }, 250)
    }
  }

  const handleAssignAward = async (awardId, candidateId) => {
    try {
      const { error } = await supabase
        .from(TABLES.SPECIAL_AWARDS)
        .update({ assigned_candidate_id: candidateId })
        .eq('id', awardId)

      if (error) throw error

      toast.success('Award assigned successfully!')
      await fetchSpecialAwards()
    } catch (error) {
      console.error('Error assigning award:', error)
      toast.error('Failed to assign award')
    }
  }

  // Award Category Management Functions
  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Category name is required')
      return
    }

    try {
      const { error } = await supabase
        .from(TABLES.SPECIAL_AWARD_CATEGORIES)
        .insert({
          event_id: eventId,
          name: newCategory.name,
          description: newCategory.description || '',
          display_order: awardCategories.length
        })

      if (error) throw error

      toast.success('Category added successfully!')
      await fetchAwardCategories()
      setNewCategory({ name: '', description: '' })
      // Keep modal open for adding more categories
    } catch (error) {
      console.error('Error adding category:', error)
      toast.error('Failed to add category')
    }
  }

  const handleEditCategory = async () => {
    if (!editingCategory.name.trim()) {
      toast.error('Category name is required')
      return
    }

    try {
      const { error } = await supabase
        .from(TABLES.SPECIAL_AWARD_CATEGORIES)
        .update({
          name: editingCategory.name,
          description: editingCategory.description || ''
        })
        .eq('id', editingCategory.id)

      if (error) throw error

      toast.success('Category updated successfully!')
      await fetchAwardCategories()
      setShowEditCategoryModal(false)
      setEditingCategory(null)
    } catch (error) {
      console.error('Error updating category:', error)
      toast.error('Failed to update category')
    }
  }

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? Awards in this category will be moved to "Uncategorized".')) {
      return
    }

    try {
      const { error } = await supabase
        .from(TABLES.SPECIAL_AWARD_CATEGORIES)
        .delete()
        .eq('id', categoryId)

      if (error) throw error

      toast.success('Category deleted successfully!')
      await fetchAwardCategories()
      await fetchSpecialAwards()
    } catch (error) {
      console.error('Error deleting category:', error)
      toast.error('Failed to delete category')
    }
  }

  const handlePrintRankings = () => {
    const candidateScores = calculateCandidateScores()
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank')
    
    // Generate the print content with styling
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Overall Rankings - ${event.name}</title>
          <style>
            @page {
              size: letter landscape;
              margin: 0;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0.5in;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
            }
            .logo {
              margin-bottom: 15px;
              text-align: center;
            }
            .logo img {
              max-width: 80px;
              max-height: 80px;
              object-fit: contain;
              border-radius: 6px;
            }
            .event-name {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .title {
              font-size: 18px;
              color: #666;
              margin-bottom: 30px;
            }
            .rankings-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              font-size: 12px;
            }
            .rankings-table th,
            .rankings-table td {
              padding: 8px;
              border: 1px solid #ddd;
              text-align: left;
            }
            .rankings-table th {
              background-color: #f8f8f8;
              font-weight: bold;
            }
            .average-score {
              font-weight: bold;
              color: #2563eb;
            }
            .rank {
              font-weight: bold;
            }
            @media print {
              .rankings-table th {
                background-color: #f8f8f8 !important;
                -webkit-print-color-adjust: exact;
              }
              .average-score {
                color: #2563eb !important;
                -webkit-print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            ${event.logo_url ? `
            <div class="logo">
              <img src="${event.logo_url}" alt="${event.name} Logo" />
            </div>` : ''}
            <div class="event-name">${event.name}</div>
            <div class="title">Overall Candidate Rankings</div>
          </div>

          <table class="rankings-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Candidate</th>
                <th>Representation</th>
                ${event.criteria.map(criteria => `
                  <th>${criteria.name}<br/>(${criteria.percentage}%)${criteria.is_locked ? '<br/><span style="font-size:10px; color:#a855f7;">🔒 Admin</span>' : ''}</th>
                `).join('')}
                <th>Average</th>
                <th>Judges</th>
              </tr>
            </thead>
            <tbody>
              ${candidateScores.map((candidate, index) => `
                <tr>
                  <td class="rank">#${index + 1}</td>
                  <td>#${candidate.candidate.candidate_number} - ${candidate.candidate.name}</td>
                  <td>${candidate.candidate.representation}</td>
                  ${event.criteria.map(criteria => {
                    // Use pre-calculated criteriaScores which includes admin scores for locked criteria
                    const score = candidate.criteriaScores[criteria.id]
                    const displayScore = score !== undefined && score !== null 
                      ? score.toFixed(1)
                      : 'N/A'
                    const isLocked = criteria.is_locked
                    const cellStyle = isLocked ? 'background-color: #faf5ff; color: #7c3aed; font-weight: 600;' : ''
                    return `<td style="${cellStyle}">${displayScore}</td>`
                  }).join('')}
                  <td class="average-score">${candidate.averageScore.toFixed(1)}</td>
                  <td>${candidate.judgeCount}/${event.num_judges}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="signatures" style="margin-top: 32px; page-break-inside: avoid;">
            <div style="font-size: 16px; font-weight: bold; margin-bottom: 10px;">Certification and Signatures</div>
            <p style="font-size: 12px; color: #444; margin-bottom: 16px;">
              We certify that the rankings and details presented above are accurate and reflect the final deliberations.
            </p>
            <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
              <tbody>
                ${
                  (event.judges && event.judges.length > 0
                    ? event.judges
                        .filter(j => j.name)
                        .map(judge => `
                          <tr>
                            <td style="padding: 12px 8px; width: 60%;">
                              <div style="border-bottom: 1px solid #999; height: 20px;"></div>
                              <div style="margin-top: 6px; color: #666;">Judge ${judge.judge_number} - ${judge.name}</div>
                            </td>
                            <td style="padding: 12px 8px; width: 40%; vertical-align: bottom;">
                              <div style="border-bottom: 1px solid #999; height: 20px;"></div>
                              <div style="margin-top: 6px; color: #666;">Date</div>
                            </td>
                          </tr>
                        `)
                        .join('')
                    : '')
                }
              </tbody>
            </table>
          </div>
        </body>
      </html>
    `

    // Write the content to the new window and print
    printWindow.document.write(printContent)
    printWindow.document.close()
    
    // Wait for content to load then print
    printWindow.onload = function() {
      printWindow.print()
      // Close the window after printing (optional)
      printWindow.onafterprint = function() {
        printWindow.close()
      }
    }
  }

  const handlePrintCriteria = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank')
    
    // Generate the print content with styling
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Judging Criteria - ${event.name}</title>
          <style>
            @page {
              size: letter portrait;
              margin: 0;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0.5in;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
            }
            .logo {
              margin-bottom: 15px;
              text-align: center;
            }
            .logo img {
              max-width: 80px;
              max-height: 80px;
              object-fit: contain;
              border-radius: 6px;
            }
            .event-name {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .title {
              font-size: 18px;
              color: #666;
              margin-bottom: 30px;
            }
            .criteria-list {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            .criteria-list th,
            .criteria-list td {
              padding: 12px;
              border: 1px solid #ddd;
              text-align: left;
            }
            .criteria-list th {
              background-color: #f8f8f8;
              font-weight: bold;
            }
            .percentage {
              text-align: right;
              font-weight: bold;
              color: #2563eb;
            }
            @media print {
              .criteria-list th {
                background-color: #f8f8f8 !important;
                -webkit-print-color-adjust: exact;
              }
              .percentage {
                color: #2563eb !important;
                -webkit-print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            ${event.logo_url ? `
            <div class="logo">
              <img src="${event.logo_url}" alt="${event.name} Logo" />
            </div>` : ''}
            <div class="event-name">${event.name}</div>
            <div class="title">Judging Criteria</div>
          </div>

          <table class="criteria-list">
            <thead>
              <tr>
                <th style="width: 10%;">No.</th>
                <th style="width: 50%;">Criteria</th>
                <th style="width: 15%;">Percentage</th>
              </tr>
            </thead>
            <tbody>
              ${event.criteria.map((criteria, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>
                    <strong>${criteria.name}</strong>
                    ${criteria.description ? `<br/><span style="font-size: 12px; color: #666;">${criteria.description}</span>` : ''}
                  </td>
                  <td class="percentage">${criteria.percentage}%</td>
                </tr>
              `).join('')}
              <tr>
                <td colspan="2" style="text-align: right; font-weight: bold;">Total:</td>
                <td class="percentage">${event.criteria.reduce((sum, c) => sum + c.percentage, 0)}%</td>
              </tr>
            </tbody>
          </table>
        </body>
      </html>
    `

    // Write the content to the new window and print
    printWindow.document.write(printContent)
    printWindow.document.close()
    
    // Wait for content to load then print
    printWindow.onload = function() {
      printWindow.print()
      // Close the window after printing (optional)
      printWindow.onafterprint = function() {
        printWindow.close()
      }
    }
  }

  const handlePrintJudge = (judge) => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank')
    
    // Generate the print content with styling
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Judge Access Code - ${event.name}</title>
          <style>
            @page {
              size: letter portrait;
              margin: 0;
            }
            body {
              font-family: 'Georgia', 'Times New Roman', serif;
              margin: 0;
              padding: 0;
              width: 8.5in;
              height: 11in;
              position: relative;
              background: #ffffff;
              color: #1a1a1a;
            }
            .page {
              width: 8.5in;
              height: 11in;
              position: relative;
              page-break-after: always;
              display: flex;
              flex-direction: column;
            }
            .lower-half {
              height: 5.5in;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-top: 5.5in;
            }
            .card {
              width: 100%;
              max-width: 6in;
              max-height: 5.2in;
              padding: 30px 40px;
              text-align: center;
              background: #ffffff;
              box-sizing: border-box;
            }
            .event-name {
              font-size: 32px;
              font-weight: 700;
              margin-bottom: 20px;
              color: #2c3e50;
              letter-spacing: 0.5px;
              line-height: 1.3;
            }
            .divider {
              width: 100px;
              height: 4px;
              background: #3498db;
              margin: 0 auto 20px;
            }
            .judge-info {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 14px;
              margin-bottom: 20px;
              flex-wrap: wrap;
            }
            .judge-label {
              font-size: 16px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 2.5px;
              color: #7f8c8d;
            }
            .judge-number {
              font-size: 24px;
              font-weight: 700;
              color: #2c3e50;
            }
            .judge-name {
              font-size: 22px;
              font-weight: 700;
              color: #2c3e50;
              padding: 10px 20px;
              background: #f8f9fa;
              border-radius: 4px;
            }
            .access-code-label {
              font-size: 16px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 2.5px;
              color: #7f8c8d;
              margin-bottom: 12px;
            }
            .access-code {
              font-family: 'Courier New', monospace;
              font-size: 48px;
              font-weight: 700;
              letter-spacing: 8px;
              margin: 0 0 20px 0;
              padding: 20px 28px;
              background: #f8f9fa;
              color: #2c3e50;
              border-left: 5px solid #3498db;
              border-radius: 4px;
            }
            .instructions {
              font-size: 12px;
              color: #5a6c7d;
              margin-top: 16px;
              text-align: left;
              padding: 14px 18px;
              background: #f8f9fa;
              line-height: 1.6;
              border-radius: 4px;
            }
            .instructions strong {
              color: #2c3e50;
              font-size: 13px;
              font-weight: 600;
              display: block;
              margin-bottom: 8px;
              text-transform: uppercase;
              letter-spacing: 1.5px;
            }
            .instructions ol {
              margin: 0;
              padding-left: 18px;
            }
            .instructions li {
              margin-bottom: 4px;
              padding-left: 3px;
              white-space: nowrap;
            }
            .instructions li strong {
              display: inline;
              margin: 0;
              text-transform: none;
              letter-spacing: 0;
            }
            .website {
              margin-top: 14px;
              padding: 0;
              font-size: 14px;
              color: #5a6c7d;
              background: transparent;
              display: inline-block;
              font-family: 'Courier New', monospace;
              font-weight: 500;
            }
            .website strong {
              font-weight: 600;
              color: #3498db;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              font-size: 12px;
              display: block;
              margin-bottom: 4px;
            }
            .footer-line {
              width: 100px;
              height: 3px;
              background: #3498db;
              margin: 14px auto 0;
            }
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .access-code {
                background: #f8f9fa !important;
                color: #2c3e50 !important;
                border-left: 5px solid #3498db !important;
                -webkit-print-color-adjust: exact;
              }
              .judge-name {
                background: #f8f9fa !important;
                -webkit-print-color-adjust: exact;
              }
              .instructions {
                background: #f8f9fa !important;
                -webkit-print-color-adjust: exact;
              }
              .divider, .footer-line {
                background: #3498db !important;
                -webkit-print-color-adjust: exact;
              }
              .website strong {
                color: #3498db !important;
                -webkit-print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="page">
            <div class="lower-half">
              <div class="card">
                <div class="event-name">${event.name}</div>
                <div class="divider"></div>
                <div class="judge-info">
                  <span class="judge-label">Judge Credentials:</span>
                  <span class="judge-number">Judge ${judge.judge_number}</span>
                  ${judge.name ? `<span class="judge-name">${judge.name}</span>` : ''}
                </div>
                <div class="access-code-label">Access Code</div>
                <div class="access-code">${judge.judge_access_code}</div>
                <div class="instructions">
                  <strong>How to Access</strong>
                  <ol>
                    <li>Visit <strong style="color: #3498db; font-family: 'Courier New', monospace;">judgeme.greycoding.online</strong></li>
                    <li>Click on "Join Event"</li>
                    <li>Enter your access code exactly as shown above</li>
                    <li>Enter your name when prompted</li>
                  </ol>
                </div>
                <div class="footer-line"></div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `

    // Write the content to the new window and print
    printWindow.document.write(printContent)
    printWindow.document.close()
    
    // Wait for content to load then print
    printWindow.onload = function() {
      printWindow.print()
      // Close the window after printing (optional)
      printWindow.onafterprint = function() {
        printWindow.close()
      }
    }
  }

  const handleAddCoAdmin = async () => {
    if (!coAdminEmail.trim()) {
      toast.error('Please enter an email address')
      return
    }

    if (!coAdminEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast.error('Please enter a valid email address')
      return
    }

    setAddingCoAdmin(true)
    try {
      // Check if user exists using RPC function
      const { data: users, error: userError } = await supabase
        .rpc('get_user_id_by_email', { user_email: coAdminEmail.trim().toLowerCase() })

      if (userError) {
        console.error('RPC Error:', userError)
        toast.error('Error looking up user. Please try again.')
        setAddingCoAdmin(false)
        return
      }

      if (!users || users.length === 0) {
        toast.error('User not found. They need to create an account first.')
        setAddingCoAdmin(false)
        return
      }

      const userId = users[0].user_id

      // Check if already a co-admin
      const currentCoAdmins = event.co_admins || []
      if (currentCoAdmins.includes(userId)) {
        toast.error('This user is already a co-admin')
        setAddingCoAdmin(false)
        return
      }

      // Check if trying to add the event owner
      if (userId === event.created_by) {
        toast.error('Event owner is already an admin')
        setAddingCoAdmin(false)
        return
      }

      // Add co-admin
      const { error } = await supabase
        .from(TABLES.EVENTS)
        .update({
          co_admins: [...currentCoAdmins, userId]
        })
        .eq('id', eventId)

      if (error) throw error

      toast.success(`Co-admin added successfully: ${users[0].email_address}`)
      setCoAdminEmail('')
      
      // Log activity
      await logActivity('create', 'co_admin', users[0].id, users[0].email_address, {
        description: `Added co-admin`
      })
      
      await fetchEventData()
    } catch (error) {
      console.error('Error adding co-admin:', error)
      toast.error('Failed to add co-admin')
    } finally {
      setAddingCoAdmin(false)
    }
  }

  const handleRemoveCoAdmin = async (coAdminId) => {
    try {
      const currentCoAdmins = event.co_admins || []
      const { error } = await supabase
        .from(TABLES.EVENTS)
        .update({
          co_admins: currentCoAdmins.filter(id => id !== coAdminId)
        })
        .eq('id', eventId)

      if (error) throw error

      // Get co-admin email before removing
      const removedCoAdmin = coAdminsDetails.find(ca => ca.id === coAdminId)
      
      toast.success('Co-admin removed successfully')
      
      // Log activity
      await logActivity('delete', 'co_admin', coAdminId, removedCoAdmin?.email || 'Unknown', {
        description: `Removed co-admin`
      })
      
      await fetchEventData()
    } catch (error) {
      console.error('Error removing co-admin:', error)
      toast.error('Failed to remove co-admin')
    }
  }

  const handleCastBackgroundUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Check if it's an image or video
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm', 'video/ogg']
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload an image (JPEG, PNG, GIF, WEBP) or video (MP4, WEBM, OGG)')
        return
      }

      // Check file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('File size must be less than 50MB')
        return
      }

      const reader = new FileReader()
      reader.onloadend = () => {
        setCastBackgroundFile(file)
        setCastBackgroundPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSaveCastBackground = async () => {
    if (!castBackgroundFile) {
      toast.error('Please select a file first')
      return
    }

    setUploadingBackground(true)
    try {
      // Delete old file if exists
      if (event.cast_background_url) {
        try {
          const oldUrl = event.cast_background_url
          const oldFileName = oldUrl.split('/').pop().split('?')[0] // Get filename without query params
          await supabase.storage
            .from('event-logos')
            .remove([oldFileName])
        } catch (err) {
          console.log('Could not delete old file:', err)
          // Continue even if deletion fails
        }
      }

      // Upload file to Supabase Storage
      const fileExt = castBackgroundFile.name.split('.').pop()
      const fileName = `cast-bg-${eventId}-${Date.now()}.${fileExt}`

      const { error: uploadError } = await supabase.storage
        .from('event-logos')
        .upload(fileName, castBackgroundFile, {
          cacheControl: '3600',
          upsert: true
        })

      if (uploadError) throw uploadError

      // Get public URL with cache busting
      const { data: { publicUrl } } = supabase.storage
        .from('event-logos')
        .getPublicUrl(fileName)

      // Add cache-busting parameter
      const urlWithCacheBust = `${publicUrl}?t=${Date.now()}`

      // Update event with cast background URL
      const { error: updateError } = await supabase
        .from(TABLES.EVENTS)
        .update({
          cast_background_url: urlWithCacheBust,
          cast_background_type: castBackgroundFile.type.startsWith('video') ? 'video' : 'image'
        })
        .eq('id', eventId)

      if (updateError) throw updateError

      toast.success('Cast background saved successfully')
      setCastBackgroundFile(null)
      setCastBackgroundPreview(null)
      setShowEventSettingsModal(false)
      
      // Log activity
      await logActivity('update', 'cast_background', null, 'Cast Background', {
        description: `Uploaded ${castBackgroundFile.type.startsWith('video') ? 'video' : 'image'} background`,
        file_type: castBackgroundFile.type
      })
      
      // Force a complete refresh by reloading event data
      await fetchEventData()
      
      // Force re-render by updating a timestamp
      setEvent(prev => ({...prev, _updated: Date.now()}))
    } catch (error) {
      console.error('Error saving cast background:', error)
      toast.error('Failed to save cast background')
    } finally {
      setUploadingBackground(false)
    }
  }

  const handleRemoveCastBackground = async () => {
    try {
      // Delete file from storage if exists
      if (event.cast_background_url) {
        try {
          const oldUrl = event.cast_background_url
          const oldFileName = oldUrl.split('/').pop().split('?')[0] // Get filename without query params
          await supabase.storage
            .from('event-logos')
            .remove([oldFileName])
        } catch (err) {
          console.log('Could not delete file from storage:', err)
          // Continue even if deletion fails
        }
      }

      const { error } = await supabase
        .from(TABLES.EVENTS)
        .update({
          cast_background_url: null,
          cast_background_type: null
        })
        .eq('id', eventId)

      if (error) throw error

      toast.success('Cast background removed successfully')
      setShowEventSettingsModal(false)
      
      // Log activity
      await logActivity('delete', 'cast_background', null, 'Cast Background', {
        description: 'Removed cast background'
      })
      
      // Force a complete refresh
      await fetchEventData()
      
      // Force re-render
      setEvent(prev => ({...prev, _updated: Date.now()}))
    } catch (error) {
      console.error('Error removing cast background:', error)
      toast.error('Failed to remove cast background')
    }
  }

  // Activity Logging Functions
  const logActivity = async (actionType, entityType, entityId, entityName, details = {}) => {
    try {
      await supabase.from(TABLES.ACTIVITY_LOGS).insert({
        event_id: eventId,
        user_id: user.id,
        user_email: user.email,
        action_type: actionType,
        entity_type: entityType,
        entity_id: entityId,
        entity_name: entityName,
        details: details
      })
    } catch (error) {
      console.error('Error logging activity:', error)
      // Don't show error to user, just log it
    }
  }

  const fetchActivityLogs = async () => {
    setLoadingLogs(true)
    try {
      const { data, error } = await supabase
        .from(TABLES.ACTIVITY_LOGS)
        .select('*')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) throw error
      setActivityLogs(data || [])
    } catch (error) {
      console.error('Error fetching activity logs:', error)
      toast.error('Failed to load activity logs')
    } finally {
      setLoadingLogs(false)
    }
  }

  const handleCastAward = (award) => {
    // Get winner information based on award type
    let winnerData = null
    let awardTitle = award.name
    let awardDescription = award.description || ''
    
    if (award.award_type === 'assigned' && award.assigned_candidate) {
      winnerData = {
        number: award.assigned_candidate.candidate_number,
        name: award.assigned_candidate.name,
        representation: award.assigned_candidate.representation || ''
      }
    } else if (award.award_type === 'criteria_based' && award.criteria_id) {
      const candidateScores = calculateCandidateScores()
      const scoresWithCandidates = candidateScores.map(cs => ({
        candidate: cs.candidate,
        score: cs.criteriaScores?.[award.criteria_id] || 0
      })).filter(item => item.score > 0)
      
      if (scoresWithCandidates.length > 0) {
        const maxScore = Math.max(...scoresWithCandidates.map(item => item.score))
        const winners = scoresWithCandidates.filter(item => item.score === maxScore)
        
        if (winners.length > 0) {
          winnerData = winners.map(w => ({
            number: w.candidate.candidate_number,
            name: w.candidate.name,
            representation: w.candidate.representation || '',
            score: maxScore.toFixed(2)
          }))
        }
      }
    } else if (award.award_type === 'overall_ranking' && award.overall_ranking_position) {
      const winner = getWinnerForRanking(award.overall_ranking_position)
      if (winner && winner.candidate) {
        winnerData = {
          number: winner.candidate.candidate_number,
          name: winner.candidate.name,
          representation: winner.candidate.representation || '',
          score: winner.totalScore?.toFixed(2) || '0'
        }
      }
    } else if (award.award_type === 'vote') {
      const awardScores = event.special_award_votes?.filter(vote => vote.special_award_id === award.id) || []
      const candidateScores = {}
      awardScores.forEach(score => {
        candidateScores[score.candidate_id] = (candidateScores[score.candidate_id] || 0) + (score.score || 0)
      })
      
      const candidatesWithScores = event.candidates
        .map(candidate => ({
          ...candidate,
          totalScore: candidateScores[candidate.id] || 0
        }))
        .filter(c => c.totalScore > 0)
        .sort((a, b) => b.totalScore - a.totalScore)
      
      if (candidatesWithScores.length > 0) {
        const maxScore = candidatesWithScores[0].totalScore
        const winners = candidatesWithScores.filter(c => c.totalScore === maxScore)
        
        winnerData = winners.map(w => ({
          number: w.candidate_number,
          name: w.name,
          representation: w.representation || '',
          score: maxScore
        }))
      }
    }
    
    if (!winnerData) {
      toast.error('No winner data available for this award')
      return
    }
    
    // Open fullscreen window
    const castWindow = window.open('', '_blank', 'fullscreen=yes')
    
    // Create presentation HTML
    const winnersArray = Array.isArray(winnerData) ? winnerData : [winnerData]
    const winnersHTML = winnersArray.map(winner => `
      <div class="winner-card">
        <div class="winner-number">#${winner.number}</div>
        <div class="winner-name">${winner.name}</div>
        ${winner.representation ? `<div class="winner-representation">${winner.representation}</div>` : ''}
        ${winner.score ? `<div class="winner-score">${winner.score} points</div>` : ''}
      </div>
    `).join('')
    
    const presentationHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${awardTitle} - Winner</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              background: #000000;
              color: #ffd700;
              height: 100vh;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              overflow: hidden;
              position: relative;
            }
            .custom-background {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              object-fit: cover;
              z-index: 0;
            }
            .background-overlay {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background: rgba(0, 0, 0, 0.5);
              z-index: 0;
            }
            .bg-pattern {
              position: absolute;
              width: 100%;
              height: 100%;
              background: 
                radial-gradient(circle at 20% 50%, rgba(255, 215, 0, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 50%, rgba(255, 215, 0, 0.1) 0%, transparent 50%);
              animation: bgPulse 4s ease-in-out infinite;
              z-index: 0;
            }
            @keyframes bgPulse {
              0%, 100% { opacity: 0.3; }
              50% { opacity: 0.6; }
            }
            .container {
              text-align: center;
              padding: 20px;
              max-width: 95vw;
              width: 100%;
              z-index: 1;
              position: relative;
            }
            .event-logo {
              margin-bottom: 20px;
              animation: logoFloat 3s ease-in-out infinite;
            }
            @keyframes logoFloat {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-10px); }
            }
            .event-logo img {
              max-width: 100px;
              max-height: 100px;
              object-fit: contain;
              filter: drop-shadow(0 0 20px rgba(255, 215, 0, 0.5));
            }
            .award-title {
              font-size: clamp(2rem, 5vw, 4rem);
              font-weight: 900;
              margin-bottom: 15px;
              text-shadow: 0 0 30px rgba(255, 215, 0, 0.8), 0 0 60px rgba(255, 215, 0, 0.4);
              animation: titleGlow 2s ease-in-out infinite, fadeInDown 0.8s ease-out;
              letter-spacing: 2px;
              background: linear-gradient(to right, #ffd700, #ffed4e, #ffd700);
              -webkit-background-clip: text;
              background-clip: text;
              -webkit-text-fill-color: transparent;
              background-size: 200% auto;
            }
            @keyframes titleGlow {
              0%, 100% { text-shadow: 0 0 30px rgba(255, 215, 0, 0.8), 0 0 60px rgba(255, 215, 0, 0.4); }
              50% { text-shadow: 0 0 40px rgba(255, 215, 0, 1), 0 0 80px rgba(255, 215, 0, 0.6); }
            }
            .award-description {
              font-size: clamp(1rem, 2vw, 1.5rem);
              margin-bottom: 30px;
              opacity: 0.85;
              color: #ffffff;
              animation: fadeInDown 0.8s ease-out 0.2s backwards;
            }
            .winner-section {
              opacity: 0;
              transform: scale(0.5) rotateY(90deg);
              transition: all 1.2s cubic-bezier(0.68, -0.55, 0.265, 1.55);
            }
            .winner-section.revealed {
              opacity: 1;
              transform: scale(1) rotateY(0deg);
            }
            .and-the-winner-is {
              font-size: clamp(1.5rem, 3vw, 3rem);
              font-weight: 700;
              margin-bottom: 30px;
              text-shadow: 0 0 20px rgba(255, 215, 0, 0.6);
              animation: winnerTextPulse 1.5s ease-in-out infinite;
              color: #ffffff;
            }
            @keyframes winnerTextPulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.05); }
            }
            .winners-container {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
              gap: 25px;
              justify-content: center;
              align-items: stretch;
              max-width: 1600px;
              margin: 0 auto;
              padding: 0 20px;
            }
            .winner-card {
              background: linear-gradient(145deg, rgba(255, 215, 0, 0.15), rgba(0, 0, 0, 0.8));
              backdrop-filter: blur(10px);
              border-radius: 20px;
              padding: clamp(30px, 4vw, 60px);
              border: 3px solid #ffd700;
              box-shadow: 
                0 0 30px rgba(255, 215, 0, 0.4),
                inset 0 0 20px rgba(255, 215, 0, 0.1);
              animation: cardEntrance 0.8s ease-out backwards, cardFloat 3s ease-in-out infinite;
              position: relative;
              overflow: hidden;
            }
            .winner-card::before {
              content: '';
              position: absolute;
              top: -50%;
              left: -50%;
              width: 200%;
              height: 200%;
              background: linear-gradient(
                45deg,
                transparent,
                rgba(255, 215, 0, 0.1),
                transparent
              );
              animation: cardShine 3s ease-in-out infinite;
            }
            @keyframes cardShine {
              0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
              100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
            }
            @keyframes cardEntrance {
              from {
                opacity: 0;
                transform: translateY(50px) scale(0.8);
              }
              to {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }
            @keyframes cardFloat {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-10px); }
            }
            .winner-card:nth-child(1) { animation-delay: 0s, 0s; }
            .winner-card:nth-child(2) { animation-delay: 0.2s, 0.5s; }
            .winner-card:nth-child(3) { animation-delay: 0.4s, 1s; }
            .winner-card:nth-child(4) { animation-delay: 0.6s, 1.5s; }
            .winner-number {
              font-size: clamp(4rem, 8vw, 8rem);
              font-weight: 900;
              color: #ffd700;
              text-shadow: 0 0 30px rgba(255, 215, 0, 1), 0 0 60px rgba(255, 215, 0, 0.6);
              margin-bottom: 20px;
              animation: numberPulse 2s ease-in-out infinite;
              position: relative;
              z-index: 1;
            }
            @keyframes numberPulse {
              0%, 100% { transform: scale(1); text-shadow: 0 0 30px rgba(255, 215, 0, 1), 0 0 60px rgba(255, 215, 0, 0.6); }
              50% { transform: scale(1.1); text-shadow: 0 0 40px rgba(255, 215, 0, 1), 0 0 80px rgba(255, 215, 0, 0.8); }
            }
            .winner-name {
              font-size: clamp(2rem, 4vw, 4rem);
              font-weight: 700;
              margin-bottom: 15px;
              color: #ffffff;
              text-shadow: 0 2px 10px rgba(0, 0, 0, 0.8);
              position: relative;
              z-index: 1;
              line-height: 1.2;
            }
            .winner-representation {
              font-size: clamp(1.3rem, 2.5vw, 2.2rem);
              color: #cccccc;
              margin-bottom: 15px;
              position: relative;
              z-index: 1;
            }
            .winner-score {
              font-size: clamp(1.5rem, 3vw, 2.5rem);
              color: #ffd700;
              font-weight: 700;
              margin-top: 20px;
              text-shadow: 0 0 20px rgba(255, 215, 0, 0.8);
              position: relative;
              z-index: 1;
            }
            .instruction {
              position: fixed;
              bottom: 20px;
              left: 50%;
              transform: translateX(-50%);
              font-size: 0.75rem;
              background: rgba(255, 215, 0, 0.05);
              padding: 6px 12px;
              border-radius: 20px;
              border: 1px solid rgba(255, 215, 0, 0.3);
              color: rgba(255, 215, 0, 0.5);
              z-index: 10;
              opacity: 0.4;
            }
            .instruction.hidden {
              display: none;
            }
            @keyframes fadeInDown {
              from {
                opacity: 0;
                transform: translateY(-30px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            .confetti {
              position: fixed;
              width: 12px;
              height: 12px;
              animation: confetti-fall 4s linear forwards;
              z-index: 100;
            }
            @keyframes confetti-fall {
              to {
                transform: translateY(100vh) rotate(720deg);
                opacity: 0;
              }
            }
            .star {
              position: fixed;
              font-size: 30px;
              animation: starBurst 2s ease-out forwards;
              pointer-events: none;
              z-index: 100;
            }
            @keyframes starBurst {
              0% {
                transform: translate(0, 0) scale(0);
                opacity: 1;
              }
              100% {
                transform: translate(var(--tx), var(--ty)) scale(1);
                opacity: 0;
              }
            }
          </style>
        </head>
        <body>
          ${event.cast_background_url ? `
            ${event.cast_background_type === 'video' ? `
              <video class="custom-background" autoplay loop muted playsinline>
                <source src="${event.cast_background_url}" type="video/mp4">
              </video>
            ` : `
              <img class="custom-background" src="${event.cast_background_url}" alt="Background" />
            `}
            <div class="background-overlay"></div>
          ` : '<div class="bg-pattern"></div>'}
          <div class="container">
            ${event.logo_url ? `
            <div class="event-logo">
              <img src="${event.logo_url}" alt="Event Logo" />
            </div>` : ''}
            <div class="award-title">${awardTitle}</div>
            ${awardDescription ? `<div class="award-description">${awardDescription}</div>` : ''}
            <div class="winner-section" id="winnerSection">
              <div class="and-the-winner-is">🏆 And the winner is... 🏆</div>
              <div class="winners-container">
                ${winnersHTML}
              </div>
            </div>
          </div>
          <div class="instruction" id="instruction">Press SPACE to reveal</div>
          <script>
            let revealed = false;
            let confettiCreated = false;
            
            document.addEventListener('keydown', function(e) {
              if (e.code === 'Space') {
                e.preventDefault();
                revealed = !revealed;
                
                const winnerSection = document.getElementById('winnerSection');
                const instruction = document.getElementById('instruction');
                
                if (revealed) {
                  winnerSection.classList.add('revealed');
                  instruction.textContent = 'Press SPACE to hide';
                  if (!confettiCreated) {
                    createConfetti();
                    createStarBurst();
                    confettiCreated = true;
                  }
                } else {
                  winnerSection.classList.remove('revealed');
                  instruction.textContent = 'Press SPACE to reveal';
                }
              }
            });
            
            function createConfetti() {
              const colors = ['#ffd700', '#ffed4e', '#ffc107', '#ffb300', '#ff8f00'];
              for (let i = 0; i < 100; i++) {
                setTimeout(() => {
                  const confetti = document.createElement('div');
                  confetti.className = 'confetti';
                  confetti.style.left = Math.random() * 100 + 'vw';
                  confetti.style.top = '-20px';
                  confetti.style.background = colors[Math.floor(Math.random() * colors.length)];
                  confetti.style.animationDelay = Math.random() * 2 + 's';
                  confetti.style.animationDuration = (Math.random() * 2 + 3) + 's';
                  confetti.style.transform = 'rotate(' + (Math.random() * 360) + 'deg)';
                  document.body.appendChild(confetti);
                  
                  setTimeout(() => confetti.remove(), 6000);
                }, i * 20);
              }
            }
            
            function createStarBurst() {
              const centerX = window.innerWidth / 2;
              const centerY = window.innerHeight / 2;
              
              for (let i = 0; i < 20; i++) {
                setTimeout(() => {
                  const star = document.createElement('div');
                  star.className = 'star';
                  star.textContent = '⭐';
                  star.style.left = centerX + 'px';
                  star.style.top = centerY + 'px';
                  
                  const angle = (Math.PI * 2 * i) / 20;
                  const distance = 150 + Math.random() * 200;
                  const tx = Math.cos(angle) * distance;
                  const ty = Math.sin(angle) * distance;
                  
                  star.style.setProperty('--tx', tx + 'px');
                  star.style.setProperty('--ty', ty + 'px');
                  
                  document.body.appendChild(star);
                  
                  setTimeout(() => star.remove(), 2000);
                }, i * 50);
              }
            }
            
            // Request fullscreen
            if (document.documentElement.requestFullscreen) {
              document.documentElement.requestFullscreen().catch(err => {
                console.log('Fullscreen request failed:', err);
              });
            }
          </script>
        </body>
      </html>
    `
    
    castWindow.document.write(presentationHTML)
    castWindow.document.close()
  }

  const handlePrintDetailedScoring = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank')
    
    // Generate the print content with styling
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Detailed Scoring Progress - ${event.name}</title>
          <style>
            @page {
              size: letter portrait;
              margin: 0.5in;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0;
              line-height: 1.4;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
            }
            .event-name {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .event-date {
              font-size: 14px;
              color: #666;
            }
            .criteria-section {
              margin-bottom: 30px;
              page-break-inside: avoid;
            }
            .criteria-header {
              background: #f3f4f6;
              padding: 10px;
              border: 1px solid #d1d5db;
              border-radius: 5px;
              margin-bottom: 15px;
            }
            .criteria-name {
              font-size: 18px;
              font-weight: bold;
              color: #000;
            }
            .max-score {
              font-size: 14px;
              color: #666;
              margin-top: 5px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 15px;
              font-size: 12px;
            }
            th, td {
              border: 1px solid #d1d5db;
              padding: 8px;
              text-align: center;
            }
            th {
              background: #f9fafb;
              font-weight: bold;
              color: #000;
            }
            .judge-name {
              text-align: left;
              font-weight: 500;
            }
            .score {
              font-weight: 500;
            }
            .score-complete {
              color: #059669;
            }
            .score-pending {
              color: #dc2626;
            }
            .average-score {
              font-weight: bold;
              color: #2563eb;
            }
            .status-complete {
              background: #dcfce7;
              color: #166534;
              padding: 4px 8px;
              border-radius: 12px;
              font-size: 11px;
            }
            .status-pending {
              background: #fef3c7;
              color: #92400e;
              padding: 4px 8px;
              border-radius: 12px;
              font-size: 11px;
            }
            .summary {
              margin-top: 20px;
              padding: 15px;
              background: #f9fafb;
              border: 1px solid #d1d5db;
              border-radius: 5px;
            }
            .summary-title {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .summary-stats {
              display: flex;
              justify-content: space-between;
              flex-wrap: wrap;
            }
            .summary-stat {
              margin: 5px 0;
            }
            @media print {
              .criteria-section {
                page-break-inside: avoid;
              }
              th, td {
                border: 1px solid #000 !important;
                -webkit-print-color-adjust: exact;
              }
              .status-complete, .status-pending {
                -webkit-print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="event-name">${event.name}</div>
            <div class="event-date">Detailed Scoring Progress Report</div>
            <div class="event-date">Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</div>
          </div>

          ${event.criteria.map(criteria => {
            const criteriaScores = event.scores.filter(s => s.criteria_id === criteria.id)
            
            return `
              <div class="criteria-section">
                <div class="criteria-header">
                  <div class="criteria-name">${criteria.name}</div>
                  ${criteria.description ? `<div style="font-size: 14px; color: #666; margin-top: 5px;">${criteria.description}</div>` : ''}
                  <div class="max-score">Maximum Score: ${criteria.percentage} points</div>
                </div>
                
                <table>
                  <thead>
                    <tr>
                      <th class="judge-name">Judge</th>
                      ${event.candidates.map(candidate => 
                        `<th>#${candidate.candidate_number}<br>${candidate.name}</th>`
                      ).join('')}
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${event.judges.filter(j => j.name).sort((a, b) => a.judge_number - b.judge_number).map(judge => {
                      const judgeScores = criteriaScores.filter(s => s.judge_id === judge.id)
                      const allCandidatesScored = event.candidates.every(candidate => 
                        judgeScores.some(s => s.candidate_id === candidate.id)
                      )
                      
                      return `
                        <tr>
                          <td class="judge-name">Judge ${judge.judge_number} - ${judge.name}</td>
                          ${event.candidates.map(candidate => {
                            const score = judgeScores.find(s => s.candidate_id === candidate.id)
                            return score ? 
                              '<td class="score score-complete">' + score.score + '</td>' : 
                              '<td class="score score-pending">—</td>'
                          }).join('')}
                          <td>
                            <span class="${allCandidatesScored ? 'status-complete' : 'status-pending'}">
                              ${allCandidatesScored ? 'Complete' : 'Pending'}
                            </span>
                          </td>
                        </tr>
                      `
                    }).join('')}
                  </tbody>
                </table>
                
              </div>
            `
          }).join('')}

          <div class="signatures" style="margin-top: 24px; page-break-inside: avoid;">
            <div style="font-size: 16px; font-weight: bold; margin-bottom: 10px;">Certification and Signatures</div>
            <p style="font-size: 12px; color: #444; margin-bottom: 16px;">
              We certify that the scores recorded herein are true and correct and reflect our evaluation.
            </p>
            <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
              <tbody>
                ${
                  (event.judges && event.judges.length > 0
                    ? event.judges
                        .filter(j => j.name)
                        .map(judge => `
                          <tr>
                            <td style="padding: 12px 8px; width: 60%;">
                              <div style="border-bottom: 1px solid #999; height: 20px;"></div>
                              <div style="margin-top: 6px; color: #666;">Judge ${judge.judge_number} - ${judge.name}</div>
                            </td>
                            <td style="padding: 12px 8px; width: 40%; vertical-align: bottom;">
                              <div style="border-bottom: 1px solid #999; height: 20px;"></div>
                              <div style="margin-top: 6px; color: #666;">Date</div>
                            </td>
                          </tr>
                        `)
                        .join('')
                    : '')
                }
              </tbody>
            </table>
          </div>
        </body>
      </html>
    `

    // Write the content to the new window and print
    printWindow.document.write(printContent)
    printWindow.document.close()
    
    // Wait for content to load then print
    printWindow.onload = function() {
      printWindow.print()
      // Close the window after printing (optional)
      printWindow.onafterprint = function() {
        printWindow.close()
      }
    }
  }

  const handlePrintSpecialAwardsResults = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank')
    
    // Generate the print content with styling
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Special Awards Voting Results - ${event.name}</title>
          <style>
            @page {
              size: letter portrait;
              margin: 0.5in;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0;
              line-height: 1.4;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
            }
            .event-name {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .event-date {
              font-size: 14px;
              color: #666;
            }
            .award-section {
              margin-bottom: 40px;
              page-break-inside: avoid;
            }
            .award-header {
              background: #f3f4f6;
              padding: 15px;
              border: 1px solid #d1d5db;
              border-radius: 5px;
              margin-bottom: 20px;
            }
            .award-name {
              font-size: 20px;
              font-weight: bold;
              color: #000;
              margin-bottom: 5px;
            }
            .award-description {
              font-size: 14px;
              color: #666;
              margin-bottom: 10px;
            }
            .award-stats {
              font-size: 14px;
              color: #666;
            }
            .winner-section {
              background: #dcfce7;
              border: 1px solid #16a34a;
              border-radius: 5px;
              padding: 15px;
              margin-bottom: 20px;
            }
            .winner-title {
              font-size: 16px;
              font-weight: bold;
              color: #166534;
              margin-bottom: 10px;
            }
            .winner-candidate {
              display: flex;
              align-items: center;
              gap: 10px;
              margin-bottom: 5px;
            }
            .candidate-number {
              background: #16a34a;
              color: white;
              padding: 4px 8px;
              border-radius: 12px;
              font-size: 12px;
              font-weight: bold;
            }
            .candidate-name {
              font-weight: 500;
              color: #166534;
            }
            .candidate-representation {
              font-size: 12px;
              color: #16a34a;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 15px;
              font-size: 12px;
            }
            th, td {
              border: 1px solid #d1d5db;
              padding: 10px;
              text-align: center;
            }
            th {
              background: #f9fafb;
              font-weight: bold;
              color: #000;
            }
            .rank {
              text-align: center;
              font-weight: 500;
            }
            .candidate-info {
              text-align: left;
            }
            .votes {
              font-weight: 500;
              color: #2563eb;
            }
            .percentage {
              font-weight: 500;
              color: #059669;
            }
            .winner-row {
              background: #dcfce7;
            }
            .trophy {
              font-size: 16px;
              margin-left: 5px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="event-name">${event.name}</div>
            <div class="event-date">Special Awards Voting Results</div>
          </div>

          ${specialAwards
            .filter(award => award.award_type === 'vote')
            .map(award => {
              const awardVotes = event.special_award_votes?.filter(vote => 
                vote.special_award_id === award.id
              ) || []
              
              const voteCounts = {}
              awardVotes.forEach(vote => {
                voteCounts[vote.candidate_id] = (voteCounts[vote.candidate_id] || 0) + 1
              })
              
              const sortedCandidates = event.candidates
                .map(candidate => ({
                  ...candidate,
                  voteCount: voteCounts[candidate.id] || 0
                }))
                .sort((a, b) => b.voteCount - a.voteCount)
              
              const maxVotes = sortedCandidates[0]?.voteCount || 0
              const winners = sortedCandidates.filter(c => c.voteCount === maxVotes && maxVotes > 0)
              
              return `
                <div class="award-section">
                  <div class="award-header">
                    <div class="award-name">${award.name}</div>
                    ${award.description ? `<div class="award-description">${award.description}</div>` : ''}
                    <div class="award-stats">${awardVotes.length} judge(s) voted</div>
                  </div>
                  
                  ${winners.length > 0 ? `
                    <div class="winner-section">
                      <div class="winner-title">
                        ${winners.length === 1 ? 'Winner' : 'Winners'} (${maxVotes} vote${maxVotes !== 1 ? 's' : ''})
                      </div>
                      ${winners.map(winner => `
                        <div class="winner-candidate">
                          <span class="candidate-number">#${winner.candidate_number}</span>
                          <span class="candidate-name">${winner.name}</span>
                          <span class="candidate-representation">${winner.representation}</span>
                        </div>
                      `).join('')}
                    </div>
                  ` : ''}
                  
                  <table>
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Candidate</th>
                        <th>Votes</th>
                        <th>Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${sortedCandidates.map((candidate, index) => {
                        const percentage = awardVotes.length > 0 
                          ? ((candidate.voteCount / awardVotes.length) * 100).toFixed(1)
                          : '0.0'
                        const isWinner = winners.some(w => w.id === candidate.id)
                        
                        return `
                          <tr class="${isWinner ? 'winner-row' : ''}">
                            <td class="rank">
                              #${index + 1}
                              ${isWinner ? '<span class="trophy">🏆</span>' : ''}
                            </td>
                            <td class="candidate-info">
                              #${candidate.candidate_number} - ${candidate.name}<br>
                              <span style="color: #666; font-size: 11px;">${candidate.representation}</span>
                            </td>
                            <td class="votes">${candidate.voteCount}</td>
                            <td class="percentage">${percentage}%</td>
                          </tr>
                        `
                      }).join('')}
                    </tbody>
                  </table>
                </div>
              `
            }).join('')}
        </body>
      </html>
    `

    // Write the content to the new window and print
    printWindow.document.write(printContent)
    printWindow.document.close()
    
    // Wait for content to load then print
    printWindow.onload = function() {
      printWindow.print()
      // Close the window after printing (optional)
      printWindow.onafterprint = function() {
        printWindow.close()
      }
    }
  }

  const handlePrintCandidates = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank')
    
    // Generate the print content with styling
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Candidates - ${event.name}</title>
          <style>
            @page {
              size: letter portrait;
              margin: 0.5in;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0;
              line-height: 1.4;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
            }
            .event-name {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .event-date {
              font-size: 14px;
              color: #666;
            }
            .candidates-section {
              margin-bottom: 30px;
            }
            .candidates-title {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 20px;
              color: #000;
            }
            .candidates-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
              gap: 20px;
              margin-bottom: 30px;
            }
            .candidate-card {
              border: 1px solid #d1d5db;
              border-radius: 8px;
              padding: 20px;
              background: #f9fafb;
            }
            .candidate-header {
              display: flex;
              align-items: center;
              gap: 15px;
              margin-bottom: 15px;
            }
            .candidate-number {
              width: 40px;
              height: 40px;
              background: #3b82f6;
              color: white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: bold;
              font-size: 16px;
            }
            .candidate-info h4 {
              font-size: 18px;
              font-weight: bold;
              color: #000;
              margin: 0 0 5px 0;
            }
            .candidate-info p {
              font-size: 14px;
              color: #666;
              margin: 0;
            }
            .candidate-details {
              display: grid;
              gap: 8px;
            }
            .detail-row {
              display: flex;
              justify-content: space-between;
              font-size: 14px;
            }
            .detail-label {
              color: #666;
            }
            .detail-value {
              font-weight: 500;
              color: #000;
            }
            .signatures-section {
              margin-top: 40px;
              border-top: 1px solid #d1d5db;
              padding-top: 20px;
            }
            .signatures-title {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 20px;
              text-align: center;
            }
            .signature-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 20px;
              margin-bottom: 20px;
            }
            .signature-line {
              text-align: center;
            }
            .signature-name {
              border-bottom: 1px solid #000;
              height: 40px;
              margin-bottom: 5px;
            }
            .signature-label {
              font-size: 12px;
              color: #666;
            }
            .print-date {
              text-align: center;
              font-size: 12px;
              color: #666;
              margin-top: 20px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="event-name">${event.name}</div>
            <div class="event-date">Candidates List</div>
          </div>

          <div class="candidates-section">
            <div class="candidates-title">Registered Candidates (${event.candidates.length})</div>
            
            ${event.candidates.length === 0 ? `
              <div style="text-align: center; padding: 40px; color: #666;">
                <p>No candidates registered for this event.</p>
              </div>
            ` : `
              <div class="candidates-grid">
                ${event.candidates.map(candidate => `
                  <div class="candidate-card">
                    <div class="candidate-header">
                      <div class="candidate-number">#${candidate.candidate_number}</div>
                      <div class="candidate-info">
                        <h4>${candidate.name}</h4>
                        <p>${candidate.representation}</p>
                      </div>
                    </div>
                    <div class="candidate-details">
                      <div class="detail-row">
                        <span class="detail-label">Candidate Number:</span>
                        <span class="detail-value">#${candidate.candidate_number}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Name:</span>
                        <span class="detail-value">${candidate.name}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Representation:</span>
                        <span class="detail-value">${candidate.representation}</span>
                      </div>
                    </div>
                  </div>
                `).join('')}
              </div>
            `}
          </div>

          <div class="signatures-section">
            <div class="signatures-title">Certification and Signatures</div>
            <div class="signature-grid">
              <div class="signature-line">
                <div class="signature-name"></div>
                <div class="signature-label">Event Organizer</div>
              </div>
              <div class="signature-line">
                <div class="signature-name"></div>
                <div class="signature-label">Head Judge</div>
              </div>
              <div class="signature-line">
                <div class="signature-name"></div>
                <div class="signature-label">Date</div>
              </div>
            </div>
            <div class="print-date">
              Printed on ${new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
        </body>
      </html>
    `

    // Write the content to the new window and print
    printWindow.document.write(printContent)
    printWindow.document.close()
    
    // Wait for content to load then print
    printWindow.onload = function() {
      printWindow.print()
      // Close the window after printing (optional)
      printWindow.onafterprint = function() {
        printWindow.close()
      }
    }
  }

  const handlePrintSpecialAwards = async (categoryId = null) => {
    try {
      // Fetch all necessary data for printing
      const [candidatesData, judgesData, votesData] = await Promise.all([
        supabase.from(TABLES.CANDIDATES).select('*').eq('event_id', eventId),
        supabase.from(TABLES.JUDGES).select('*').eq('event_id', eventId),
        supabase.from(TABLES.SPECIAL_AWARD_VOTES).select('*')
      ])

      const candidates = candidatesData.data || []
      const judges = judgesData.data || []
      const votes = votesData.data || []

      // Filter awards by category if specified
      let awardsToPrint
      let printTitle = 'Special Awards Results'
      
      if (categoryId === 'uncategorized') {
        // Print only uncategorized awards
        awardsToPrint = specialAwards.filter(a => !a.category_id)
        printTitle = 'Uncategorized Awards'
      } else if (categoryId) {
        // Print specific category
        awardsToPrint = specialAwards.filter(a => a.category_id === categoryId)
        const categoryName = awardCategories.find(c => c.id === categoryId)?.name
        printTitle = categoryName ? `${categoryName} - Awards` : 'Special Awards'
      } else {
        // Print all awards
        awardsToPrint = specialAwards
      }

      // Group awards by category for printing
      const uncategorized = awardsToPrint.filter(award => !award.category_id)
      const categorizedAwards = awardCategories
        .map(category => ({
          category,
          awards: awardsToPrint.filter(award => award.category_id === category.id)
        }))
        .filter(group => group.awards.length > 0)

      // Calculate winners for vote-based awards
      const calculateVoteWinners = (awardId) => {
        const awardVotes = votes.filter(vote => vote.special_award_id === awardId)
        
        if (awardVotes.length === 0) return { winners: [], totalVotes: 0, maxScore: 0 }
        
        const candidateScores = {}
        awardVotes.forEach(vote => {
          candidateScores[vote.candidate_id] = (candidateScores[vote.candidate_id] || 0) + (vote.score || 0)
        })
        
        const sortedCandidates = candidates
          .map(candidate => ({
            ...candidate,
            totalScore: candidateScores[candidate.id] || 0
          }))
          .sort((a, b) => b.totalScore - a.totalScore)
        
        const maxScore = sortedCandidates[0]?.totalScore || 0
        const winners = sortedCandidates.filter(c => c.totalScore === maxScore && maxScore > 0)
        
        return { winners, totalVotes: awardVotes.length, maxScore }
      }

      // Calculate winner for criteria-based awards
      const calculateCriteriaWinner = (criteriaId) => {
        const candidateScoresData = calculateCandidateScores()
        if (!candidateScoresData.length) return null
        
        const criteriaWinner = candidateScoresData.reduce((max, current) => {
          const currentScore = current.criteriaScores?.[criteriaId] || 0
          const maxScore = max.criteriaScores?.[criteriaId] || 0
          return currentScore > maxScore ? current : max
        }, candidateScoresData[0] || {})
        
        const score = criteriaWinner?.criteriaScores?.[criteriaId] || 0
        return score > 0 ? { candidate: criteriaWinner.candidate, score } : null
      }

      // Calculate winner for overall ranking awards
      const calculateOverallWinner = (rankingPosition) => {
        return getWinnerForRanking(rankingPosition)
      }

      // Get winner info for any award type
      const getWinnerInfo = (award) => {
        if (award.award_type === 'assigned' && award.assigned_candidate) {
          return {
            type: 'assigned',
            winner: award.assigned_candidate,
            score: null
          }
        } else if (award.award_type === 'criteria_based' && award.criteria_id) {
          const result = calculateCriteriaWinner(award.criteria_id)
          const criteria = event?.criteria?.find(c => c.id === award.criteria_id)
          return {
            type: 'criteria',
            winner: result?.candidate,
            score: result?.score,
            criteriaName: criteria?.name
          }
        } else if (award.award_type === 'overall_ranking' && award.overall_ranking_position) {
          const result = calculateOverallWinner(award.overall_ranking_position)
          const rankingLabel = getRankingOptions().find(r => r.value === award.overall_ranking_position)?.label
          return {
            type: 'overall_ranking',
            winner: result?.candidate,
            score: result?.averageScore,
            rankingLabel
          }
        } else if (award.award_type === 'vote') {
          const result = calculateVoteWinners(award.id)
          return {
            type: 'vote',
            winners: result.winners,
            maxScore: result.maxScore,
            totalVotes: result.totalVotes
          }
        }
        return { type: 'unknown', winner: null }
      }

      // Generate award HTML
      const generateAwardHTML = (award) => {
        const winnerInfo = getWinnerInfo(award)
        const typeLabels = {
          'assigned': 'Assigned',
          'criteria_based': 'Criteria Winner',
          'overall_ranking': 'Overall Ranking',
          'vote': 'Judge Voting'
        }
        const typeColors = {
          'assigned': 'type-assigned',
          'criteria_based': 'type-criteria',
          'overall_ranking': 'type-overall',
          'vote': 'type-vote'
        }

        let winnerHTML = ''
        if (winnerInfo.type === 'assigned' || winnerInfo.type === 'criteria' || winnerInfo.type === 'overall_ranking') {
          if (winnerInfo.winner) {
            winnerHTML = `
              <div class="winner-section">
                <div class="winner-title">🏆 Winner</div>
                <div class="winner-info">
                  <div class="winner-name">#${winnerInfo.winner.candidate_number} - ${winnerInfo.winner.name}</div>
                  <div class="winner-details">${winnerInfo.winner.representation || ''}</div>
                  ${winnerInfo.score ? `<div class="winner-score">${winnerInfo.score.toFixed(2)} points</div>` : ''}
                </div>
              </div>
            `
          } else {
            winnerHTML = `<div class="no-winner"><div class="no-winner-text">No winner determined yet</div></div>`
          }
        } else if (winnerInfo.type === 'vote') {
          if (winnerInfo.winners && winnerInfo.winners.length > 0) {
            winnerHTML = `
              <div class="winner-section">
                <div class="winner-title">🏆 Winner${winnerInfo.winners.length > 1 ? 's' : ''}</div>
                ${winnerInfo.winners.map(w => `
                  <div class="winner-info">
                    <div class="winner-name">#${w.candidate_number} - ${w.name}</div>
                    <div class="winner-details">${w.representation || ''}</div>
                    <div class="winner-score">${w.totalScore} points (${winnerInfo.totalVotes} votes)</div>
                  </div>
                `).join('')}
              </div>
            `
          } else {
            winnerHTML = `<div class="no-winner"><div class="no-winner-text">No votes cast yet</div></div>`
          }
        }

        return `
          <div class="award-card">
            <div class="award-header">
              <div>
                <div class="award-name">${award.name}</div>
                ${award.description ? `<div class="award-description">${award.description}</div>` : ''}
              </div>
              <span class="award-type ${typeColors[award.award_type] || 'type-assigned'}">
                ${typeLabels[award.award_type] || 'Award'}
              </span>
            </div>
            <div class="assignment-section">
              ${winnerHTML}
            </div>
          </div>
        `
      }

      // Generate category section HTML
      const generateCategoryHTML = (categoryName, awards) => {
        if (awards.length === 0) return ''
        return `
          <div class="category-section">
            <div class="category-header">${categoryName}</div>
            ${awards.map(award => generateAwardHTML(award)).join('')}
          </div>
        `
      }

    // Create a new window for printing
    const printWindow = window.open('', '_blank')
    
    // Generate the print content with styling
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Special Awards - ${event.name}</title>
          <style>
            @page {
              size: letter portrait;
              margin: 0.5in;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0;
              line-height: 1.4;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
            }
            .event-name {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .event-date {
              font-size: 14px;
              color: #666;
            }
            .award-section {
              margin-bottom: 30px;
              page-break-inside: avoid;
            }
            .award-card {
              border: 1px solid #d1d5db;
              border-radius: 8px;
              padding: 20px;
              margin-bottom: 20px;
              background: #f9fafb;
            }
            .award-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 15px;
            }
            .award-name {
              font-size: 18px;
              font-weight: bold;
              color: #000;
              margin-bottom: 5px;
            }
            .award-description {
              font-size: 14px;
              color: #666;
              margin-bottom: 10px;
            }
            .award-type {
              padding: 4px 8px;
              border-radius: 12px;
              font-size: 12px;
              font-weight: bold;
            }
            .type-assigned {
              background: #dbeafe;
              color: #1e40af;
            }
            .type-vote {
              background: #e9d5ff;
              color: #7c3aed;
            }
            .assignment-section {
              border-top: 1px solid #e5e7eb;
              padding-top: 15px;
              margin-top: 15px;
            }
            .assignment-label {
              font-size: 14px;
              font-weight: 500;
              color: #374151;
              margin-bottom: 8px;
            }
              .winner-section {
                background: #dcfce7;
                border: 2px solid #16a34a;
                border-radius: 8px;
                padding: 15px;
                margin-bottom: 10px;
              }
              .winner-title {
                font-size: 16px;
                font-weight: bold;
                color: #166534;
                margin-bottom: 10px;
                text-align: center;
              }
              .winner-info {
                text-align: center;
              }
              .winner-name {
                font-size: 18px;
                font-weight: bold;
                color: #166534;
                margin-bottom: 5px;
              }
              .winner-details {
                font-size: 14px;
                color: #166534;
              }
              .winner-score {
                font-size: 12px;
                color: #6b7280;
                margin-top: 5px;
              }
            .assigned-candidate {
              background: #dcfce7;
              border: 1px solid #16a34a;
              border-radius: 6px;
              padding: 12px;
            }
            .candidate-info {
              font-size: 14px;
              color: #166534;
            }
            .candidate-number {
              background: #16a34a;
              color: white;
              padding: 2px 6px;
              border-radius: 8px;
              font-size: 12px;
              font-weight: bold;
              margin-right: 8px;
            }
            .unassigned {
              background: #fef3c7;
              border: 1px solid #f59e0b;
              border-radius: 6px;
              padding: 12px;
            }
            .unassigned-text {
              font-size: 14px;
              color: #92400e;
            }
              .no-votes {
                background: #fef3c7;
                border: 1px solid #f59e0b;
              border-radius: 6px;
              padding: 12px;
                text-align: center;
            }
              .no-votes-text {
              font-size: 14px;
                color: #92400e;
            }
            .signatures-section {
              margin-top: 40px;
              border-top: 1px solid #d1d5db;
              padding-top: 20px;
            }
            .signatures-title {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 20px;
              text-align: center;
            }
              .signature-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                gap: 20px;
              margin-bottom: 20px;
            }
            .signature-line {
              text-align: center;
            }
            .signature-name {
              border-bottom: 1px solid #000;
                height: 40px;
              margin-bottom: 5px;
            }
            .signature-label {
              font-size: 12px;
              color: #666;
            }
            .print-date {
              text-align: center;
              font-size: 12px;
              color: #666;
              margin-top: 20px;
            }
            .category-section {
              margin-bottom: 30px;
              page-break-inside: avoid;
            }
            .category-header {
              font-size: 18px;
              font-weight: bold;
              color: #1f2937;
              padding: 10px 15px;
              background: #f3f4f6;
              border-left: 4px solid #10b981;
              margin-bottom: 15px;
            }
            .type-criteria {
              background: #dcfce7;
              color: #166534;
            }
            .type-overall {
              background: #fef3c7;
              color: #92400e;
            }
            .no-winner {
              background: #fef3c7;
              border: 1px solid #f59e0b;
              border-radius: 6px;
              padding: 12px;
              text-align: center;
            }
            .no-winner-text {
              font-size: 14px;
              color: #92400e;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="event-name">${event.name}</div>
            <div class="event-date">${printTitle}</div>
          </div>

          ${awardsToPrint.length === 0 ? `
            <div class="award-section">
              <div class="award-card">
                <div class="award-name">No Special Awards</div>
                <div class="award-description">No special awards have been found.</div>
              </div>
            </div>
          ` : `
            ${categorizedAwards.map(group => generateCategoryHTML(group.category.name, group.awards)).join('')}
            ${uncategorized.length > 0 ? generateCategoryHTML('Uncategorized', uncategorized) : ''}
          `}

          <div class="signatures-section">
            <div class="signatures-title">Certification and Signatures</div>
              <div class="signature-grid">
              <div class="signature-line">
                <div class="signature-name"></div>
                <div class="signature-label">Event Organizer</div>
              </div>
                ${judges.map(judge => `
              <div class="signature-line">
                <div class="signature-name"></div>
                    <div class="signature-label">${judge.name || 'Judge'}</div>
              </div>
                `).join('')}
              <div class="signature-line">
                <div class="signature-name"></div>
                <div class="signature-label">Date</div>
              </div>
            </div>
            <div class="print-date">
              Printed on ${new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
        </body>
      </html>
    `

    // Write the content to the new window and print
    printWindow.document.write(printContent)
    printWindow.document.close()
    
    // Wait for content to load then print
    printWindow.onload = function() {
      printWindow.print()
      // Close the window after printing (optional)
      printWindow.onafterprint = function() {
        printWindow.close()
      }
      }
    } catch (error) {
      console.error('Error generating print content:', error)
      toast.error('Failed to generate print content')
    }
  }

  const handleEditJudge = async () => {
    if (!editingJudge || !editJudgeName.trim()) {
      toast.error('Please enter a name for the judge')
      return
    }

    try {
      const { error } = await supabase
        .from(TABLES.JUDGES)
        .update({ name: editJudgeName.trim() })
        .eq('id', editingJudge.id)

      if (error) throw error

      // Log activity
      await logActivity('update', 'judge', editingJudge.id, `Judge #${editingJudge.judge_number}`, {
        description: `Updated judge name to "${editJudgeName.trim()}"`,
        judge_number: editingJudge.judge_number,
        old_name: editingJudge.name || 'Unnamed',
        new_name: editJudgeName.trim()
      })

      toast.success('Judge name updated successfully')
      setEditingJudge(null)
      setEditJudgeName('')
      await fetchEventData()
    } catch (error) {
      console.error('Error updating judge:', error)
      toast.error('Failed to update judge name')
    }
  }

  // Add Judge with Password Verification
  const handleAddJudge = async () => {
    if (!judgeActionPassword) {
      setJudgeActionError('Please enter your password to confirm')
      return
    }

    setIsProcessingJudgeAction(true)
    setJudgeActionError('')

    try {
      // Verify password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: judgeActionPassword,
      })

      if (signInError) {
        setJudgeActionError('Incorrect password. Please try again.')
        setIsProcessingJudgeAction(false)
        return
      }

      // Generate new access code
      const newAccessCode = Math.random().toString(36).substring(2, 10).toUpperCase()
      const newJudgeNumber = event.num_judges + 1

      // Create new judge
      const { error: insertError } = await supabase
        .from(TABLES.JUDGES)
        .insert({
          event_id: eventId,
          judge_number: newJudgeNumber,
          judge_access_code: newAccessCode,
          name: '',
        })

      if (insertError) throw insertError

      // Update event num_judges
      const { error: updateError } = await supabase
        .from(TABLES.EVENTS)
        .update({ num_judges: newJudgeNumber })
        .eq('id', eventId)

      if (updateError) throw updateError

      // Log activity
      await logActivity('create', 'judge', null, `Judge #${newJudgeNumber}`, {
        description: `Added new judge with access code`,
        judge_number: newJudgeNumber,
        access_code: newAccessCode.substring(0, 3) + '***' // Partial code for security
      })

      toast.success(
        <div>
          <div className="font-semibold mb-1">Judge added successfully!</div>
          <div className="text-sm">Access Code: <span className="font-mono font-bold">{newAccessCode}</span></div>
        </div>,
        { duration: 6000 }
      )

      setShowAddJudgeModal(false)
      setJudgeActionPassword('')
      await fetchEventData()
    } catch (error) {
      console.error('Error adding judge:', error)
      toast.error('Failed to add judge')
    } finally {
      setIsProcessingJudgeAction(false)
    }
  }

  // Remove Judge with Password Verification
  const handleRemoveJudge = async () => {
    if (!judgeActionPassword) {
      setJudgeActionError('Please enter your password to confirm')
      return
    }

    if (!judgeToRemove) return

    setIsProcessingJudgeAction(true)
    setJudgeActionError('')

    try {
      // Verify password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: judgeActionPassword,
      })

      if (signInError) {
        setJudgeActionError('Incorrect password. Please try again.')
        setIsProcessingJudgeAction(false)
        return
      }

      // Delete all scores by this judge
      const { error: scoresError } = await supabase
        .from(TABLES.SCORES)
        .delete()
        .eq('judge_id', judgeToRemove.id)

      if (scoresError) throw scoresError

      // Delete all special award votes by this judge
      const { error: votesError } = await supabase
        .from(TABLES.SPECIAL_AWARD_VOTES)
        .delete()
        .eq('judge_id', judgeToRemove.id)

      if (votesError) throw votesError

      // Delete the judge
      const { error: deleteError } = await supabase
        .from(TABLES.JUDGES)
        .delete()
        .eq('id', judgeToRemove.id)

      if (deleteError) throw deleteError

      // Note: We don't decrement num_judges because judge numbers are never reused
      // num_judges represents the highest judge number that has been assigned

      // Log activity
      await logActivity('delete', 'judge', judgeToRemove.id, `Judge #${judgeToRemove.judge_number}`, {
        description: `Removed judge and deleted all associated scores`,
        judge_number: judgeToRemove.judge_number,
        judge_name: judgeToRemove.name || 'Unnamed'
      })

      toast.success('Judge removed successfully. All associated scores have been deleted.')
      setShowRemoveJudgeModal(false)
      setJudgeActionPassword('')
      setJudgeToRemove(null)
      await fetchEventData()
    } catch (error) {
      console.error('Error removing judge:', error)
      toast.error('Failed to remove judge')
    } finally {
      setIsProcessingJudgeAction(false)
    }
  }

  const resetScores = () => {
    setResetPassword('');
    setResetPasswordError('');
    setShowResetConfirm(true);
  };

  const confirmResetScores = async () => {
    if (!resetPassword) {
      setResetPasswordError('Please enter your password');
      return;
    }

    try {
      // Verify the user's password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: resetPassword,
      });

      if (signInError) {
        console.error('Password verification failed:', signInError);
        setResetPasswordError('Incorrect password. Please try again.');
        return;
      }

      setResetPasswordError('');
      setShowResetConfirm(false);
      setIsResetting(true);
      setResettingScores(true);
      
      console.log('Starting score reset for event:', eventId);

      // First, get all special award IDs for this event
      console.log('Fetching special awards for event...');
      const { data: eventAwards, error: awardsError } = await supabase
        .from(TABLES.SPECIAL_AWARDS)
        .select('id')
        .eq('event_id', eventId);
        
      if (awardsError) throw awardsError;
      
      if (awardsError) {
        console.error('Error fetching special awards:', awardsError)
        throw awardsError
      }
      
      const awardIds = eventAwards?.map(a => a.id) || []
      console.log('Found special award IDs:', awardIds)
      
      // Execute operations sequentially to ensure proper error handling
      console.log('Starting deletion process...')
      
      // 1. First delete special award votes
      if (awardIds.length > 0) {
        console.log('Deleting special award votes...')
        const { error: votesError } = await supabase
          .from(TABLES.SPECIAL_AWARD_VOTES)
          .delete()
          .in('special_award_id', awardIds)
        
        if (votesError) {
          console.error('Error deleting award votes:', votesError)
          throw votesError
        }
        console.log('Award votes deleted')
      } else {
        console.log('No award votes to delete')
      }
      
      // 2. Delete all scores for this event
      console.log('Deleting scores...')
      const { error: scoresError } = await supabase
        .from(TABLES.SCORES)
        .delete()
        .eq('event_id', eventId)
      
      if (scoresError) {
        console.error('Error deleting scores:', scoresError)
        throw scoresError
      }
      console.log('Scores deleted')
      
      // 3. Reset judge completion status and certification
      console.log('Resetting judge completion status and certification...')
      const { error: judgesError } = await supabase
        .from(TABLES.JUDGES)
        .update({ 
          completed_criteria: [],
          submitted_special_awards: [],
          submitted_candidates: [],
          scores_certified: false,
          certification_timestamp: null
        })
        .eq('event_id', eventId)
      
      if (judgesError) {
        console.error('Error resetting judge status:', judgesError)
        throw judgesError
      }
      console.log('Judge status reset')

      console.log('All deletions completed successfully')
      
      // Log activity
      await logActivity('delete', 'score', null, 'All Scores', {
        description: `Reset all scores and award votes for the event`,
        scores_count: event.scores?.length || 0,
        judges_count: event.judges?.length || 0
      })
      
      // Refresh event data
      console.log('Refreshing event data...')
      await fetchEventData()
      
      console.log('Score reset completed successfully')
      toast.success('All scores have been reset successfully')
    } catch (error) {
      console.error('Error in resetScores:', {
        error,
        message: error.message,
        stack: error.stack
      })
      toast.error(`Failed to reset scores: ${error.message}`)
    } finally {
      console.log('Resetting loading state')
      setResettingScores(false)
      setIsResetting(false)
    }
  }

  const handleDeleteEvent = async () => {
    if (!deletePassword.trim()) {
      toast.error('Please enter your password to confirm')
      return
    }

    setDeleting(true)

    try {
      // Verify password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: deletePassword
      })

      if (signInError) {
        throw signInError
      }

      // Delete all related data in the correct order
      await Promise.all([
        supabase.from(TABLES.SCORES).delete().eq('event_id', eventId),
        supabase.from(TABLES.SPECIAL_AWARD_VOTES).delete().in('special_award_id', 
          event?.special_awards?.map(a => a.id) || []
        ),
        supabase.from(TABLES.JUDGES).delete().eq('event_id', eventId),
        supabase.from(TABLES.CRITERIA).delete().eq('event_id', eventId),
        supabase.from(TABLES.SPECIAL_AWARDS).delete().eq('event_id', eventId),
        supabase.from(TABLES.CANDIDATES).delete().eq('event_id', eventId)
      ])

      // Finally delete the event
      const { error: deleteError } = await supabase
        .from(TABLES.EVENTS)
        .delete()
        .eq('id', eventId)

      if (deleteError) throw deleteError

      toast.success('Event deleted successfully')
      navigate('/admin')
    } catch (error) {
      console.error('Error deleting event:', error)
      toast.error(error.message || 'Failed to delete event')
    } finally {
      setDeleting(false)
      setShowDeleteModal(false)
      setDeletePassword('')
    }
  }

  // Edit mode handler functions
  const handleEditLogoUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setEditData(prev => ({
          ...prev,
          logo: file,
          logoPreview: reader.result
        }))
      }
      reader.readAsDataURL(file)
    }
  }

  const updateEditCriteria = (index, field, value) => {
    setEditData(prev => ({
      ...prev,
      criteria: prev.criteria.map((c, i) => 
        i === index ? { ...c, [field]: value } : c
      )
    }))
  }

  const addEditCriteria = () => {
    setEditData(prev => ({
      ...prev,
      criteria: [...prev.criteria, { name: '', description: '', percentage: 0 }]
    }))
  }

  const removeEditCriteria = (index) => {
    setEditData(prev => ({
      ...prev,
      criteria: prev.criteria.filter((_, i) => i !== index)
    }))
  }

  const updateEditCandidate = (index, field, value) => {
    setEditData(prev => ({
      ...prev,
      candidates: prev.candidates.map((c, i) => 
        i === index ? { ...c, [field]: value } : c
      )
    }))
  }

  const addEditCandidate = () => {
    setEditData(prev => ({
      ...prev,
      candidates: [...prev.candidates, { 
        name: '', 
        representation: '', 
        candidate_number: prev.candidates.length + 1 
      }]
    }))
  }

  const removeEditCandidate = (index) => {
    setEditData(prev => {
      const newCandidates = prev.candidates.filter((_, i) => i !== index)
      return {
        ...prev,
        candidates: newCandidates.map((candidate, i) => ({
          ...candidate,
          candidate_number: i + 1
        }))
      }
    })
  }

  const validateEditData = () => {
    if (!editData.name.trim()) {
      toast.error('Event name is required')
      return false
    }

    if (editData.criteria.length === 0) {
      toast.error('At least one criterion is required')
      return false
    }

    if (!editData.criteria.every(c => c.name.trim() && c.percentage > 0)) {
      toast.error('All criteria must have a name and percentage greater than 0')
      return false
    }

    const totalPercentage = editData.criteria.reduce((sum, c) => sum + c.percentage, 0)
    if (totalPercentage !== 100) {
      toast.error(`Criteria percentages must total 100% (currently ${totalPercentage}%)`)
      return false
    }

    if (editData.candidates.length === 0) {
      toast.error('At least one candidate is required')
      return false
    }

    if (!editData.candidates.every(c => c.name.trim() && c.representation.trim() && c.candidate_number > 0)) {
      toast.error('All candidates must have a name, representation, and number')
      return false
    }

    return true
  }

  const handleSaveEdit = async () => {
    if (!validateEditData()) return

    // Check if there are existing scores
    const hasScores = event.scores && event.scores.length > 0

    // Identify criteria and candidates to be deleted
    const existingCriteriaIds = editData.criteria.filter(c => c.id).map(c => c.id)
    const allCriteriaIds = event.criteria.map(c => c.id)
    const criteriaToDelete = allCriteriaIds.filter(id => !existingCriteriaIds.includes(id))

    const existingCandidateIds = editData.candidates.filter(c => c.id).map(c => c.id)
    const allCandidateIds = event.candidates.map(c => c.id)
    const candidatesToDelete = allCandidateIds.filter(id => !existingCandidateIds.includes(id))

    // Warn if trying to delete criteria/candidates when scores exist
    if (hasScores && (criteriaToDelete.length > 0 || candidatesToDelete.length > 0)) {
      const deletingCriteria = criteriaToDelete.length > 0
      const deletingCandidates = candidatesToDelete.length > 0
      
      let warningMessage = '⚠️ WARNING: This event has existing scores!\n\n'
      
      if (deletingCriteria) {
        const criteriaNames = event.criteria
          .filter(c => criteriaToDelete.includes(c.id))
          .map(c => `• ${c.name}`)
          .join('\n')
        warningMessage += `Removing criteria will DELETE all scores for:\n${criteriaNames}\n\n`
      }
      
      if (deletingCandidates) {
        const candidateNames = event.candidates
          .filter(c => candidatesToDelete.includes(c.id))
          .map(c => `• ${c.name}`)
          .join('\n')
        warningMessage += `Removing candidates will DELETE all scores for:\n${candidateNames}\n\n`
      }
      
      warningMessage += 'This action CANNOT be undone!\n\nAre you absolutely sure you want to continue?'
      
      if (!window.confirm(warningMessage)) {
        setSaving(false)
        return
      }
    }

    setSaving(true)
    try {
      // Upload new logo if provided
      let logoUrl = editData.logoPreview
      if (editData.logo) {
        const fileExt = editData.logo.name.split('.').pop()
        const fileName = `${Date.now()}.${fileExt}`
        const { error: uploadError } = await supabase.storage
          .from('event-logos')
          .upload(fileName, editData.logo)
        
        if (uploadError) throw uploadError
        
        const { data: { publicUrl } } = supabase.storage
          .from('event-logos')
          .getPublicUrl(fileName)
        logoUrl = publicUrl
      }

      // Update event basic info (safe - doesn't affect scores)
      const { error: eventError } = await supabase
        .from(TABLES.EVENTS)
        .update({
          name: editData.name,
          logo_url: logoUrl
        })
        .eq('id', eventId)

      if (eventError) throw eventError

      // Log event updates
      if (event.name !== editData.name) {
        await logActivity('update', 'event', eventId, event.name, {
          description: `Changed event name from "${event.name}" to "${editData.name}"`,
          old_name: event.name,
          new_name: editData.name
        })
      }
      if (editData.logo) {
        await logActivity('update', 'event', eventId, editData.name, {
          description: 'Updated event logo'
        })
      }

      // Delete removed criteria (will cascade delete related scores - user confirmed above)
      if (criteriaToDelete.length > 0) {
        const deletedCriteria = event.criteria.filter(c => criteriaToDelete.includes(c.id))
        const { error: deleteError } = await supabase
          .from(TABLES.CRITERIA)
          .delete()
          .in('id', criteriaToDelete)
        if (deleteError) throw deleteError
        
        // Log deletions
        for (const criterion of deletedCriteria) {
          await logActivity('delete', 'criteria', criterion.id, criterion.name, {
            description: `Deleted criteria (${criterion.percentage}%)`,
            percentage: criterion.percentage
          })
        }
      }

      // Update or insert criteria
      for (const criterion of editData.criteria) {
        if (criterion.id) {
          // Check if it was actually modified
          const original = event.criteria.find(c => c.id === criterion.id)
          const wasModified = original && (
            original.name !== criterion.name ||
            original.percentage !== criterion.percentage ||
            (original.description || '') !== (criterion.description || '')
          )
          
          // Update existing
          const { error } = await supabase
            .from(TABLES.CRITERIA)
            .update({
              name: criterion.name,
              description: criterion.description || '',
              percentage: criterion.percentage
            })
            .eq('id', criterion.id)
          if (error) throw error
          
          // Log update if changed
          if (wasModified) {
            await logActivity('update', 'criteria', criterion.id, criterion.name, {
              description: `Updated criteria (${criterion.percentage}%)`,
              percentage: criterion.percentage
            })
          }
        } else {
          // Insert new
          const { data, error } = await supabase
            .from(TABLES.CRITERIA)
            .insert({
              event_id: eventId,
              name: criterion.name,
              description: criterion.description || '',
              percentage: criterion.percentage
            })
            .select()
          if (error) throw error
          
          // Log creation
          await logActivity('create', 'criteria', data[0]?.id, criterion.name, {
            description: `Added new criteria (${criterion.percentage}%)`,
            percentage: criterion.percentage
          })
        }
      }

      // Delete removed candidates (will cascade delete related scores - user confirmed above)
      if (candidatesToDelete.length > 0) {
        const deletedCandidates = event.candidates.filter(c => candidatesToDelete.includes(c.id))
        const { error: deleteError } = await supabase
          .from(TABLES.CANDIDATES)
          .delete()
          .in('id', candidatesToDelete)
        if (deleteError) throw deleteError
        
        // Log deletions
        for (const candidate of deletedCandidates) {
          await logActivity('delete', 'candidate', candidate.id, candidate.name, {
            description: `Deleted candidate #${candidate.candidate_number}`,
            candidate_number: candidate.candidate_number,
            representation: candidate.representation
          })
        }
      }

      // Update or insert candidates
      for (const candidate of editData.candidates) {
        if (candidate.id) {
          // Check if it was actually modified
          const original = event.candidates.find(c => c.id === candidate.id)
          const wasModified = original && (
            original.name !== candidate.name ||
            original.representation !== candidate.representation ||
            original.candidate_number !== candidate.candidate_number
          )
          
          // Update existing
          const { error } = await supabase
            .from(TABLES.CANDIDATES)
            .update({
              name: candidate.name,
              representation: candidate.representation,
              candidate_number: candidate.candidate_number
            })
            .eq('id', candidate.id)
          if (error) throw error
          
          // Log update if changed
          if (wasModified) {
            await logActivity('update', 'candidate', candidate.id, candidate.name, {
              description: `Updated candidate #${candidate.candidate_number}`,
              candidate_number: candidate.candidate_number,
              representation: candidate.representation
            })
          }
        } else {
          // Insert new
          const { data, error } = await supabase
            .from(TABLES.CANDIDATES)
            .insert({
              event_id: eventId,
              name: candidate.name,
              representation: candidate.representation,
              candidate_number: candidate.candidate_number
            })
            .select()
          if (error) throw error
          
          // Log creation
          await logActivity('create', 'candidate', data[0]?.id, candidate.name, {
            description: `Added new candidate #${candidate.candidate_number}`,
            candidate_number: candidate.candidate_number,
            representation: candidate.representation
          })
        }
      }

      toast.success('Event updated successfully!')
      await fetchEventData()
      setActiveTab('overview')
    } catch (error) {
      console.error('Error updating event:', error)
      toast.error('Failed to update event. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleCancelEdit = () => {
    setActiveTab('overview')
  }

  if (loading) {
    return (
      <div class="min-h-screen bg-white flex items-center justify-center">
        <div class="text-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p class="text-gray-600">Loading event details...</p>
        </div>
      </div>
    )
  }

  if (!event) {
    return (
      <div class="min-h-screen bg-white flex items-center justify-center">
        <div class="text-center">
          <p class="text-red-600 mb-4">Event not found</p>
          <button onClick={() => navigate('/admin')} class="btn-primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  const candidateScores = calculateCandidateScores()
  const totalExpectedScores = event && event.judges && event.candidates && event.criteria
    ? (event.judges.length * event.candidates.length * event.criteria.length)
    : 0
  const submittedScores = event?.scores?.length || 0
  const percentComplete = totalExpectedScores > 0
    ? Math.round((submittedScores / totalExpectedScores) * 100)
    : 0
  const isFinal = percentComplete === 100

  // Add this before the main return
  const renderResetConfirmationModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
        <div className="flex items-center mb-4">
          <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
            <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="ml-4 text-lg leading-6 font-medium text-gray-900">
            Reset All Scores
          </h3>
        </div>
        <div className="mt-2">
          <p className="text-sm text-gray-500 mb-4">
            Are you sure you want to reset all scores for this event? This action cannot be undone and will permanently delete all scoring data.
          </p>
          <div className="mt-4">
            <label htmlFor="reset-password" className="block text-sm font-medium text-gray-700 mb-1">
              Enter your password to confirm
            </label>
            <input
              type="password"
              id="reset-password"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              className={`w-full px-3 py-2 border ${resetPasswordError ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500`}
              placeholder="Your password"
              disabled={isResetting}
              onKeyDown={(e) => e.key === 'Enter' && confirmResetScores()}
            />
            {resetPasswordError && (
              <p className="mt-1 text-sm text-red-600">{resetPasswordError}</p>
            )}
          </div>
        </div>
        <div className="mt-5 sm:mt-6 flex flex-col sm:flex-row-reverse gap-3">
          <button
            type="button"
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-yellow-600 text-base font-medium text-white hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-wait"
            onClick={confirmResetScores}
            disabled={isResetting}
          >
            {isResetting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Resetting...
              </>
            ) : 'Reset Scores'}
          </button>
          <button
            type="button"
            className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={() => setShowResetConfirm(false)}
            disabled={isResetting}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      {showResetConfirm && renderResetConfirmationModal()}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.8); }
            to { opacity: 1; transform: scale(1); }
          }
          .header-gradient {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          }
          .status-indicator {
            display: inline-flex;
            align-items: center;
            padding: 0.25rem 0.5rem;
            border-radius: 9999px;
            font-size: 0.7rem;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }
          .status-online {
            background-color: rgba(74, 222, 128, 0.1);
            color: #34d399;
          }
          .status-offline {
            background-color: rgba(248, 113, 113, 0.1);
            color: #f87171;
          }
        `}
      </style>
      
      {/* Enhanced Header */}
      <header className="header-gradient text-white py-3 sm:py-4">
        <div className="container mx-auto px-4">
          {/* Mobile Layout */}
          <div className="block sm:hidden">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center text-green-100 hover:text-white transition-colors text-sm font-medium"
              >
                <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Dashboard
              </button>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleManualRefresh}
                  disabled={isRefreshing}
                  className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-50"
                  title="Refresh data"
                >
                  <svg className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                
                <div className={`status-indicator ${isConnected ? 'status-online' : 'status-offline'}`}>
                  <span className="flex w-2 h-2 rounded-full mr-1.5 bg-current"></span>
                  {isConnected ? 'Live' : 'Offline'}
                </div>
              </div>
            </div>
            
            <div className="mb-3">
              <h1 className="text-xl font-bold text-white mb-1 truncate">{event.name}</h1>
              <div className="flex items-center justify-between">
                <span className="text-sm text-green-100/90">Event Dashboard</span>
                <span className="text-xs bg-white/10 px-2 py-0.5 rounded-full text-green-100/90">
                  {event.judges.length} Judge {event.judges.length === 1 ? 'Code' : 'Codes'}
                </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 overflow-x-auto pb-2 -mx-1">
              <button className="px-3 py-1.5 text-xs font-medium rounded-full bg-white/10 hover:bg-white/20 transition-colors whitespace-nowrap">
                View Judge Codes
              </button>
              <button className="px-3 py-1.5 text-xs font-medium rounded-full bg-white/10 hover:bg-white/20 transition-colors whitespace-nowrap">
                Share Event
              </button>
              <button
                onClick={() => setShowEventSettingsModal(true)}
                className="px-3 py-1.5 text-xs font-medium rounded-full bg-white/10 hover:bg-white/20 transition-colors whitespace-nowrap"
              >
                Settings
              </button>
            </div>
          </div>
          
          {/* Desktop Layout */}
          <div className="hidden sm:block">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => navigate('/admin')}
                    className="flex items-center text-green-100 hover:text-white transition-colors text-sm font-medium mb-1"
                  >
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Dashboard
                  </button>
                  
                  <div className={`status-indicator ${isConnected ? 'status-online' : 'status-offline'}`}>
                    <span className="flex w-2 h-2 rounded-full mr-1.5 bg-current"></span>
                    {isConnected ? 'Live Updates Active' : 'Offline Mode'}
                  </div>
                </div>
                
                <div className="flex items-end space-x-4 mt-1">
                  <h1 className="text-2xl font-bold text-white">{event.name}</h1>
                  <span className="text-sm text-green-100/90 mb-0.5">Event ID: {eventId.substring(0, 8)}</span>
                </div>
                
                <div className="flex items-center space-x-4 mt-2 text-sm">
                  <span className="text-green-100/90 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    {event.judges.length} Judge {event.judges.length === 1 ? 'Code' : 'Codes'}
                  </span>
                  <span className="text-green-100/90 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    {event.candidates.length} Candidates
                  </span>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="relative group">
                  <button
                    onClick={handleManualRefresh}
                    disabled={isRefreshing}
                    className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-50 relative"
                    title="Refresh data"
                  >
                    <svg className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span className="absolute -bottom-7 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      Refresh Data
                    </span>
                  </button>
                </div>
                
                <div className="relative group">
                  <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors relative"
                    title="Notifications"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {(pendingRequestsCount + pendingHelpCount) > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                        {pendingRequestsCount + pendingHelpCount}
                      </span>
                    )}
                    <span className="absolute -bottom-7 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      Notifications
                    </span>
                  </button>
                </div>
                
                <div className="relative group">
                  <button className="px-4 py-2 bg-white/10 hover:bg-white/20 transition-colors rounded-lg text-sm font-medium flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Quick Actions
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-1 z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-1">
                    <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">View All Judge Codes</a>
                    <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Share Event</a>
                    <button
                      onClick={printJudgeInstructions}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Print Judge Instructions
                    </button>
                    <button
                      onClick={() => setShowEventSettingsModal(true)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Event Settings
                    </button>
                    <div className="border-t border-gray-100 my-1"></div>
                    <a href="#" className="block px-4 py-2 text-sm text-red-600 hover:bg-red-50">Export Event Data</a>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 pt-4 border-t border-white/10"></div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Event Info - Mobile Optimized */}
        <div className="mb-6 sm:mb-8">
          {/* Mobile Layout */}
          <div className="block sm:hidden">
            <div className="flex items-start space-x-4 mb-4">
              {event.logo_url && (
                <img 
                  src={event.logo_url} 
                  alt="Event Logo" 
                  className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-black mb-2 truncate">{event.name}</h2>
                <div className="space-y-1 text-xs text-gray-600">
                  <div>{event.candidates.length} Candidates</div>
                  <div>{event.judges.length}/{event.num_judges} Judges</div>
                  <div>{event.scores.length} Scores Submitted</div>
                </div>
              </div>
            </div>
            
            {/* Minimal Actions Toggle for Mobile */}
            <div className="mb-4">
              <button
                onClick={() => setShowMobileActions(!showMobileActions)}
                className="w-full flex items-center justify-between px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors shadow-sm"
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-500 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                  <span>Event Actions</span>
                </div>
                <svg className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${showMobileActions ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showMobileActions && (
                <div className="mt-2 grid grid-cols-3 gap-2 p-3 bg-gray-50/50 border border-gray-100 rounded-xl animate-slide-up">
                  {/* Owner-only buttons */}
                  {event.created_by === user.id && (
                    <>
                      <button
                        onClick={exportToExcel}
                        className="flex flex-col items-center p-3 bg-green-50 hover:bg-green-100 text-green-600 rounded-xl transition-all hover:shadow-md group"
                      >
                        <svg className="w-6 h-6 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-xs font-medium">Export</span>
                      </button>
                      <button
                        onClick={handleBackupEvent}
                        className="flex flex-col items-center p-3 bg-purple-50 hover:bg-purple-100 text-purple-600 rounded-xl transition-all hover:shadow-md group"
                      >
                        <svg className="w-6 h-6 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                        <span className="text-xs font-medium">Backup</span>
                      </button>
                      <button
                        onClick={() => setShowRestoreModal(true)}
                        className="flex flex-col items-center p-3 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-xl transition-all hover:shadow-md group"
                      >
                        <svg className="w-6 h-6 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        <span className="text-xs font-medium">Restore</span>
                      </button>
                      <button
                        onClick={handleCopyEventExport}
                        className="flex flex-col items-center p-3 bg-teal-50 hover:bg-teal-100 text-teal-600 rounded-xl transition-all hover:shadow-md group"
                      >
                        <svg className="w-6 h-6 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs font-medium">Copy</span>
                      </button>
                      <button
                        onClick={printCover}
                        className="flex flex-col items-center p-3 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl transition-all hover:shadow-md group"
                      >
                        <svg className="w-6 h-6 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs font-medium">Print</span>
                      </button>
                      <button
                        onClick={printJudgeInstructions}
                        className="flex flex-col items-center p-3 bg-cyan-50 hover:bg-cyan-100 text-cyan-600 rounded-xl transition-all hover:shadow-md group"
                      >
                        <svg className="w-6 h-6 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-xs font-medium">Instructions</span>
                      </button>
                    </>
                  )}
                  {/* Settings - Available to all */}
                  <button
                    onClick={() => setShowEventSettingsModal(true)}
                    className="flex flex-col items-center p-3 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-xl transition-all hover:shadow-md group"
                  >
                    <svg className="w-6 h-6 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-xs font-medium">Settings</span>
                  </button>
                  {/* Owner-only destructive actions */}
                  {event.created_by === user.id && (
                    <>
                      <button
                        onClick={resetScores}
                        disabled={resettingScores}
                        className="flex flex-col items-center p-3 bg-yellow-50 hover:bg-yellow-100 text-yellow-600 rounded-xl transition-all hover:shadow-md group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {resettingScores ? (
                          <>
                            <svg className="animate-spin h-6 w-6 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-xs font-medium">Resetting...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-6 h-6 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            <span className="text-xs font-medium">Reset</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setShowDeleteModal(true)}
                        className="flex flex-col items-center p-3 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition-all hover:shadow-md group"
                      >
                        <svg className="w-6 h-6 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <span className="text-xs font-medium">Delete</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Desktop Layout */}
          <div className="hidden sm:flex items-center">
            {event.logo_url && (
              <img 
                src={event.logo_url} 
                alt="Event Logo" 
                className="w-20 h-20 rounded-lg object-cover mr-6"
              />
            )}
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-black mb-2">{event.name}</h2>
              <div className="flex space-x-6 text-sm text-gray-600">
                <span>{event.candidates.length} Candidates</span>
                <span>{event.judges.length}/{event.num_judges} Judges</span>
                <span>{event.scores.length} Scores Submitted</span>
              </div>
            </div>
            {event.created_by === user.id && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={exportToExcel}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg transition-all hover:shadow-md group"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="font-medium">Export</span>
                </button>
                <button
                  onClick={handleBackupEvent}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-50 hover:bg-purple-100 text-purple-600 rounded-lg transition-all hover:shadow-md group"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                  <span className="font-medium">Backup</span>
                </button>
                <button
                  onClick={() => setShowRestoreModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg transition-all hover:shadow-md group"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  <span className="font-medium">Restore</span>
                </button>
                <button
                  onClick={handleCopyEventExport}
                  className="flex items-center space-x-2 px-4 py-2 bg-teal-50 hover:bg-teal-100 text-teal-600 rounded-lg transition-all hover:shadow-md group"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium">Copy Event</span>
                </button>
                <button
                  onClick={printCover}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-all hover:shadow-md group"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium">Print</span>
                </button>
                <button
                  onClick={resetScores}
                  disabled={resettingScores}
                  className="flex items-center space-x-2 px-4 py-2 bg-yellow-50 hover:bg-yellow-100 text-yellow-600 rounded-lg transition-all hover:shadow-md group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resettingScores ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="font-medium">Resetting...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span className="font-medium">Reset</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-all hover:shadow-md group"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span className="font-medium">Delete</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tabs - Mobile Optimized */}
        <div className="border-b border-gray-200 mb-6 sm:mb-8">
          {/* Mobile Tabs - Horizontal Scroll */}
          <div className="block sm:hidden">
            <nav className="flex space-x-4 overflow-x-auto pb-2 -mb-px">
              {['overview', 'candidates', 'judges', 'criteria', 'awards', ...(event.created_by === user.id ? ['edit'] : [])].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-2 px-3 border-b-2 font-medium text-xs capitalize whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab === 'awards' ? 'Awards' : tab === 'edit' ? 'Edit Event' : tab}
                </button>
              ))}
            </nav>
          </div>
          
          {/* Desktop Tabs */}
          <div className="hidden sm:block">
            <nav className="flex space-x-8">
              {['overview', 'candidates', 'judges', 'criteria', 'awards', ...(event.created_by === user.id ? ['edit'] : [])].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                    activeTab === tab
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab === 'awards' ? 'Awards' : tab === 'edit' ? 'Edit Event' : tab}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Scoring Sequence Configuration Card */}
            {!isReorderingScoringSequence ? (
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-indigo-900">Judge Scoring Sequence</h3>
                      <p className="text-sm text-indigo-700 mt-1">
                        {event.scoring_sequence && event.scoring_sequence.length > 0 
                          ? `${event.scoring_sequence.length} items in custom sequence`
                          : 'Configure the order judges will be recommended to score criteria and awards'
                        }
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleStartReorderScoringSequence}
                    className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-lg transition-colors font-medium text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                    <span>{event.scoring_sequence && event.scoring_sequence.length > 0 ? 'Edit Sequence' : 'Configure Sequence'}</span>
                  </button>
                </div>
              </div>
            ) : (
              /* Scoring Sequence Reorder Mode */
              <div className="bg-white border-2 border-indigo-300 rounded-xl overflow-hidden shadow-lg">
                <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-4 sm:px-6 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="text-white">
                      <h3 className="text-lg font-bold">Configure Scoring Sequence</h3>
                      <p className="text-indigo-100 text-sm mt-1">Drag and drop to set the order judges will score</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveScoringSequence}
                        className="flex items-center gap-2 bg-white text-indigo-600 hover:bg-indigo-50 px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Save Sequence
                      </button>
                      <button
                        onClick={handleCancelReorderScoringSequence}
                        className="flex items-center gap-2 bg-indigo-400 hover:bg-indigo-300 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 sm:p-6">
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Tip:</strong> Drag items to reorder. For example: Criteria 1 → Award 1 → Criteria 2 → Award 2
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    {scoringSequenceOrder.map((item, index) => (
                      <div
                        key={`${item.type}-${item.id}`}
                        draggable
                        onDragStart={(e) => handleSequenceDragStart(e, item, index)}
                        onDragOver={(e) => handleSequenceDragOver(e, index)}
                        onDragEnd={handleSequenceDragEnd}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-move transition-all ${
                          item.type === 'criteria' 
                            ? 'bg-blue-50 border-blue-300 hover:border-blue-400' 
                            : 'bg-purple-50 border-purple-300 hover:border-purple-400'
                        } ${draggedSequenceItem?.index === index ? 'opacity-50' : ''}`}
                      >
                        {/* Drag Handle */}
                        <div className="flex-shrink-0 text-gray-400">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                          </svg>
                        </div>
                        
                        {/* Order Number */}
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0 ${
                          item.type === 'criteria' ? 'bg-blue-500' : 'bg-purple-500'
                        }`}>
                          {index + 1}
                        </div>
                        
                        {/* Item Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              item.type === 'criteria' 
                                ? 'bg-blue-100 text-blue-700' 
                                : 'bg-purple-100 text-purple-700'
                            }`}>
                              {item.type === 'criteria' ? 'Criteria' : 'Award'}
                            </span>
                            <span className="font-semibold text-gray-900 truncate">{item.name}</span>
                          </div>
                          {item.data?.description && (
                            <p className="text-xs text-gray-500 mt-1 truncate">{item.data.description}</p>
                          )}
                        </div>
                        
                        {/* Percentage/Type indicator */}
                        {item.type === 'criteria' && item.data?.percentage && (
                          <span className="text-sm font-medium text-blue-600 flex-shrink-0">{item.data.percentage}%</span>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {scoringSequenceOrder.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <p>No unlocked criteria or vote-type awards to sequence.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Overall Scores Table - Mobile Optimized */}
            <div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 space-y-3 sm:space-y-0">
                <h3 className="text-base sm:text-lg font-semibold text-black">Overall Candidate Rankings</h3>
                <button
                  onClick={handlePrintRankings}
                  className="flex items-center justify-center space-x-2 text-gray-600 hover:text-gray-700 font-medium border border-gray-300 rounded-lg px-3 sm:px-4 py-2 text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  <span>Print Rankings</span>
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-200 rounded-lg min-w-[600px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 sm:px-4 py-3 text-left text-xs sm:text-sm font-medium text-black">Rank</th>
                      <th className="px-2 sm:px-4 py-3 text-left text-xs sm:text-sm font-medium text-black">Candidate</th>
                      <th className="px-2 sm:px-4 py-3 text-left text-xs sm:text-sm font-medium text-black hidden sm:table-cell">Representation</th>
                      {event.criteria.map(criteria => (
                        <th key={criteria.id} className={`px-1 sm:px-4 py-3 text-center text-xs sm:text-sm font-medium ${
                          criteria.is_locked ? 'bg-purple-100 text-purple-800' : 'text-black'
                        }`}>
                          <div className="hidden sm:block">
                            {criteria.is_locked && (
                              <svg className="w-3 h-3 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                            )}
                            {criteria.name}<br/>
                            <span className="text-xs text-gray-500">({criteria.percentage}%)</span>
                            {criteria.is_locked && (
                              <><br/><span className="text-xs text-purple-600 font-semibold">Admin</span></>
                            )}
                          </div>
                          <div className="block sm:hidden">
                            {criteria.is_locked && '🔒 '}
                            {criteria.name.substring(0, 8)}...
                          </div>
                        </th>
                      ))}
                      <th className="px-2 sm:px-4 py-3 text-center text-xs sm:text-sm font-medium text-black">Total</th>
                      {specialAwards.some(a => a.add_to_overall && a.overall_points > 0) && (
                        <th className="px-2 sm:px-4 py-3 text-center text-xs sm:text-sm font-medium text-black">
                          <div className="hidden sm:block">Award Bonus</div>
                          <div className="block sm:hidden">+Bonus</div>
                        </th>
                      )}
                      <th className="px-2 sm:px-4 py-3 text-center text-xs sm:text-sm font-medium text-black">Judges</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidateScores.map((candidate, index) => (
                      <tr key={candidate.candidate.id} className="border-t border-gray-200">
                        <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium text-black">#{index + 1}</td>
                        <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium text-black">
                          <div className="block sm:hidden">
                            #{candidate.candidate.candidate_number}
                          </div>
                          <div className="hidden sm:block">
                            #{candidate.candidate.candidate_number} - {candidate.candidate.name}
                          </div>
                          <div className="block sm:hidden text-xs text-gray-500 truncate max-w-[120px]">
                            {candidate.candidate.name}
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm text-gray-600 hidden sm:table-cell">{candidate.candidate.representation}</td>
                        {event.criteria.map(criteria => {
                          // Use the pre-calculated criteriaScores which includes admin scores for locked criteria
                          const score = candidate.criteriaScores[criteria.id]
                          const displayScore = score !== undefined && score !== null 
                            ? score.toFixed(1)
                            : 'N/A'
                          const isLocked = criteria.is_locked
                          
                          return (
                            <td key={criteria.id} className={`px-1 sm:px-4 py-3 text-center text-xs sm:text-sm ${
                              isLocked ? 'bg-purple-50 text-purple-700 font-semibold' : 'text-gray-600'
                            }`}>
                              {displayScore}
                              {isLocked && displayScore !== 'N/A' && (
                                <div className="text-xs text-purple-500 font-normal">Admin</div>
                              )}
                            </td>
                          )
                        })}
                        <td className="px-2 sm:px-4 py-3 text-center text-xs sm:text-sm font-bold text-green-600">
                          <div className="flex flex-col items-center">
                            <span>{candidate.averageScore?.toFixed(1) || '0.0'}</span>
                            {candidate.awardBonusPoints > 0 && (
                              <span className="text-xs text-purple-500 font-normal">
                                (incl. +{candidate.awardBonusPoints})
                              </span>
                            )}
                          </div>
                        </td>
                        {specialAwards.some(a => a.add_to_overall && a.overall_points > 0) && (
                          <td className="px-2 sm:px-4 py-3 text-center text-xs sm:text-sm">
                            {candidate.awardBonusPoints > 0 ? (
                              <div className="flex flex-col items-center">
                                <span className="text-purple-600 font-semibold">+{candidate.awardBonusPoints}</span>
                                <div className="hidden sm:block text-xs text-gray-500">
                                  {candidate.awardBonuses?.map((bonus, i) => (
                                    <div key={i} title={bonus.awardName}>
                                      {bonus.awardName.length > 12 ? bonus.awardName.substring(0, 12) + '...' : bonus.awardName}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        )}
                        <td className="px-2 sm:px-4 py-3 text-center text-xs sm:text-sm text-gray-600">
                          {candidate.judgeCount}/{event.num_judges}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Detailed Scoring Progress */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                <h3 className="text-lg font-semibold text-black">Detailed Scoring Progress</h3>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setScoringViewMode('per-criteria')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        scoringViewMode === 'per-criteria'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Per Criteria
                    </button>
                    <button
                      onClick={() => setScoringViewMode('per-judge')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        scoringViewMode === 'per-judge'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Per Judge
                    </button>
                  </div>
                  <button
                    onClick={() => handlePrintDetailedScoring()}
                    className="btn-secondary inline-flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    <span>Print Scoring</span>
                  </button>
                </div>
              </div>
              {scoringViewMode === 'per-criteria' ? (
                <div className="space-y-6">
                  {event.criteria.map(criteria => {
                    // Get all scores for this criteria
                    const criteriaScores = event.scores.filter(s => s.criteria_id === criteria.id)
                    
                    return (
                      <div key={criteria.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-semibold text-black">
                            {criteria.name}
                          </h4>
                          <span className="text-sm text-gray-600">Max Score: {criteria.percentage}</span>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr>
                                <th className="px-4 py-2 text-left text-sm font-medium text-black">Judge</th>
                                {event.candidates.map(candidate => (
                                  <th key={candidate.id} className="px-4 py-2 text-center text-sm font-medium text-black">
                                    #{candidate.candidate_number} - {candidate.name}
                                  </th>
                                ))}
                                <th className="px-4 py-2 text-center text-sm font-medium text-black">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {event.judges.filter(j => j.name).sort((a, b) => a.judge_number - b.judge_number).map(judge => {
                                const judgeScores = criteriaScores.filter(s => s.judge_id === judge.id)
                                const allCandidatesScored = event.candidates.every(candidate => 
                                  judgeScores.some(s => s.candidate_id === candidate.id)
                                )
                                
                                return (
                                  <tr key={judge.id} className="border-t border-gray-100">
                                    <td className="px-4 py-2 text-sm text-gray-600">
                                      Judge {judge.judge_number} - {judge.name}
                                    </td>
                                    {event.candidates.map(candidate => {
                                      const score = judgeScores.find(s => s.candidate_id === candidate.id)
                                      return (
                                        <td key={candidate.id} className="px-4 py-2 text-center text-sm">
                                          {score ? (
                                            <span className="font-medium text-green-600">{score.score}</span>
                                          ) : (
                                            <span className="text-red-500">—</span>
                                          )}
                                        </td>
                                      )
                                    })}
                                    <td className="px-4 py-2 text-center text-sm">
                                      {allCandidatesScored ? (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                          Complete
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                          Pending
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="space-y-6">
                  {event.judges.filter(j => j.name).sort((a, b) => a.judge_number - b.judge_number).map(judge => {
                    return (
                      <div key={judge.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-semibold text-black">
                            Judge {judge.judge_number} - {judge.name}
                          </h4>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr>
                                <th className="px-4 py-2 text-left text-sm font-medium text-black">Criteria</th>
                                {event.candidates.map(candidate => (
                                  <th key={candidate.id} className="px-4 py-2 text-center text-sm font-medium text-black">
                                    #{candidate.candidate_number} - {candidate.name}
                                  </th>
                                ))}
                                <th className="px-4 py-2 text-center text-sm font-medium text-black">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {event.criteria.map(criteria => {
                                const judgeScores = event.scores.filter(s => 
                                  s.judge_id === judge.id && s.criteria_id === criteria.id
                                )
                                const allCandidatesScored = event.candidates.every(candidate => 
                                  judgeScores.some(s => s.candidate_id === candidate.id)
                                )
                                
                                return (
                                  <tr key={criteria.id} className="border-t border-gray-100">
                                    <td className="px-4 py-2 text-sm text-gray-600">
                                      {criteria.name} <span className="text-xs text-gray-500">(Max: {criteria.percentage})</span>
                                    </td>
                                    {event.candidates.map(candidate => {
                                      const score = judgeScores.find(s => s.candidate_id === candidate.id)
                                      return (
                                        <td key={candidate.id} className="px-4 py-2 text-center text-sm">
                                          {score ? (
                                            <span className="font-medium text-green-600">{score.score}</span>
                                          ) : (
                                            <span className="text-red-500">—</span>
                                          )}
                                        </td>
                                      )
                                    })}
                                    <td className="px-4 py-2 text-center text-sm">
                                      {allCandidatesScored ? (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                          Complete
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                          Pending
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'candidates' && (
          <div className="space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0">
              <h3 className="text-base sm:text-lg font-semibold text-black">Candidates</h3>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <span className="text-xs sm:text-sm text-gray-600">{event.candidates.length} candidates registered</span>
                {event.candidates.length > 0 && (
                  <button
                    onClick={handlePrintCandidates}
                    className="flex items-center justify-center space-x-2 text-gray-600 hover:text-gray-700 font-medium border border-gray-300 rounded-lg px-3 sm:px-4 py-2 hover:bg-gray-50 transition-colors text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    <span>Print Candidates</span>
                  </button>
                )}
              </div>
            </div>
            
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {event.candidates.map((candidate) => (
                <div key={candidate.id} className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-sm sm:text-lg font-bold text-blue-600">#{candidate.candidate_number}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base sm:text-lg font-semibold text-black mb-1 truncate">{candidate.name}</h4>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">{candidate.representation}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">Number:</span>
                      <span className="font-medium text-black">#{candidate.candidate_number}</span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">Name:</span>
                      <span className="font-medium text-black truncate ml-2">{candidate.name}</span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">Representation:</span>
                      <span className="font-medium text-black truncate ml-2">{candidate.representation}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {event.candidates.length === 0 && (
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Candidates Yet</h3>
                <p className="text-gray-600">Candidates will appear here once they are added to the event.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'judges' && (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-black">Judges & Access Codes</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setJudgeActionPassword('')
                    setJudgeActionError('')
                    setShowAddJudgeModal(true)
                  }}
                  className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Add Judge</span>
                </button>
              </div>
            </div>
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {/* First ensure judges array is properly initialized */}
              {Array.from({ length: event.num_judges }, (_, i) => {
                const judgeNumber = i + 1
                // Find existing judge or create a new slot
                const judge = event.judges.find(j => j.judge_number === judgeNumber)
                
                // If no judge record exists for this number, we'll show an empty slot
                const judgeData = judge || {
                  id: `slot-${judgeNumber}`,
                  judge_number: judgeNumber,
                  name: '',
                  judge_access_code: 'Not Available',
                  status: 'pending',
                  event_id: event.id
                }
                
                return (
                <div key={judgeData.id} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs sm:text-sm font-medium text-gray-600">Judge #{judgeData.judge_number}</span>
                    <span className={`text-xs sm:text-sm font-medium ${
                      judgeData.status === 'pending' ? 'text-gray-500' :
                      judgeData.name ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      {judgeData.status === 'pending' ? 'Pending' :
                       judgeData.name ? 'Active' : 'Available'}
                    </span>
                  </div>
                  
                  {judgeData.status === 'pending' ? (
                    <>
                      <div className="bg-gray-50 border border-gray-200 rounded p-2 sm:p-3 mb-3">
                        <p className="text-xs sm:text-sm text-gray-600">
                          This judge slot needs to be set up. Please refresh the page or contact support if this persists.
                        </p>
                      </div>
                      <button
                        onClick={handleManualRefresh}
                        className="w-full text-xs sm:text-sm text-gray-600 hover:text-gray-700 font-medium border border-gray-300 rounded-lg py-2"
                      >
                        Refresh Data
                      </button>
                    </>
                  ) : (
                    <>
                      {editingJudge?.id === judgeData.id ? (
                        <div className="mb-2">
                          <input
                            type="text"
                            value={editJudgeName}
                            onChange={(e) => setEditJudgeName(e.target.value)}
                            className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-xs sm:text-sm"
                            placeholder="Enter judge name"
                          />
                          <div className="flex space-x-2 mt-2">
                            <button
                              onClick={handleEditJudge}
                              className="flex-1 bg-green-500 hover:bg-green-600 text-white px-2 sm:px-3 py-1 rounded text-xs sm:text-sm font-medium"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setEditingJudge(null)
                                setEditJudgeName('')
                              }}
                              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 sm:px-3 py-1 rounded text-xs sm:text-sm font-medium"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <h4 className="font-semibold text-black mb-2 text-sm sm:text-base">
                          {judgeData.name || 'Not assigned'}
                        </h4>
                      )}

                      <div className="mb-3">
                        <div className="text-xs text-gray-500 mb-1">Access Code:</div>
                        <div className={`font-mono text-xs sm:text-sm font-bold tracking-wider px-2 py-1 rounded ${
                          judgeData.status === 'error' 
                            ? 'text-red-600 bg-red-50' 
                            : 'text-green-600 bg-green-50'
                        }`}>
                          {judgeData.judge_access_code}
                        </div>
                      </div>

                      <div className="flex flex-col space-y-2">
                        <p className="text-xs sm:text-sm text-gray-600">
                          {judgeData.name ? (
                            judgeData.created_at ? `Joined ${new Date(judgeData.created_at).toLocaleDateString()}` : 'Date not available'
                          ) : 'Not joined yet'}
                        </p>
                        
                        {/* Certification Status */}
                        {judgeData.name && judgeData.scores_certified && (
                          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-2">
                            <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                            </svg>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-green-800">✓ Scores Certified</p>
                              {judgeData.certification_timestamp && (
                                <p className="text-xs text-green-700">
                                  {new Date(judgeData.certification_timestamp).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div className="flex flex-col space-y-2">
                          <div className="flex space-x-2">
                            {judgeData.name ? (
                              <button
                                onClick={() => {
                                  setEditingJudge(judgeData)
                                  setEditJudgeName(judgeData.name)
                                }}
                                className="flex-1 text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium"
                              >
                                Edit Name
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  setEditingJudge(judgeData)
                                  setEditJudgeName('')
                                }}
                                className="flex-1 text-xs sm:text-sm text-green-600 hover:text-green-700 font-medium"
                              >
                                Assign Name
                              </button>
                            )}
                          </div>
                          
                          <div className="flex flex-col sm:flex-row gap-2">
                            <button
                              onClick={() => handlePrintJudge(judgeData)}
                              className="flex-1 text-xs sm:text-sm text-gray-600 hover:text-gray-700 font-medium border border-gray-300 rounded-lg py-2 flex items-center justify-center space-x-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                              </svg>
                              <span>Print Access Code</span>
                            </button>
                            
                            {judgeData.status !== 'pending' && (
                              <button
                                onClick={() => {
                                  setJudgeToRemove(judgeData)
                                  setJudgeActionPassword('')
                                  setJudgeActionError('')
                                  setShowRemoveJudgeModal(true)
                                }}
                                className="flex-1 text-xs sm:text-sm text-red-600 hover:text-red-700 font-medium border border-red-300 rounded-lg py-2 flex items-center justify-center space-x-2 hover:bg-red-50 transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                <span>Remove Judge</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                )
              })}
            </div>
          </div>
        )}

        {activeTab === 'criteria' && (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
              <h3 className="text-lg font-semibold text-black">Judging Criteria</h3>
              <div className="flex gap-2">
                {!isReorderingCriteria ? (
                  <>
                    <button
                      onClick={handleStartReorderCriteria}
                      className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                      </svg>
                      <span>Reorganize</span>
                    </button>
                    <button
                      onClick={handlePrintCriteria}
                      className="flex items-center space-x-2 text-gray-600 hover:text-gray-700 font-medium border border-gray-300 rounded-lg px-4 py-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      <span>Print Criteria</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleSaveCriteriaOrder}
                      className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Save Order</span>
                    </button>
                    <button
                      onClick={handleCancelReorderCriteria}
                      className="flex items-center space-x-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span>Cancel</span>
                    </button>
                  </>
                )}
              </div>
            </div>

            {isReorderingCriteria && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Drag and drop</strong> criteria cards to reorder them. This order will be reflected in the judge panel.
                </p>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {(isReorderingCriteria ? criteriaOrder : event.criteria).map((criteria, index) => (
                <div
                  key={criteria.id}
                  draggable={isReorderingCriteria}
                  onDragStart={(e) => isReorderingCriteria && handleDragStart(e, criteria, index)}
                  onDragOver={(e) => isReorderingCriteria && handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`border rounded-lg p-4 ${
                    criteria.is_locked 
                      ? 'border-purple-300 bg-purple-50' 
                      : 'border-gray-200'
                  } ${
                    isReorderingCriteria 
                      ? 'cursor-move hover:bg-gray-50 hover:border-blue-400 transition-colors' 
                      : ''
                  } ${
                    draggedCriteria?.index === index 
                      ? 'opacity-50' 
                      : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {isReorderingCriteria && (
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                      )}
                      {criteria.is_locked && (
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      )}
                      <span className="text-sm font-medium text-gray-600">Criteria #{index + 1}</span>
                    </div>
                    <span className="text-sm font-bold text-green-600">{criteria.percentage}%</span>
                  </div>
                  <h4 className="font-semibold text-black">{criteria.name}</h4>
                  {criteria.description && (
                    <p className="text-sm text-gray-600 mt-1">{criteria.description}</p>
                  )}
                  
                  {/* Display Admin Notes if present */}
                  {criteria.admin_notes && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <div className="text-xs text-amber-800">
                          <p className="font-medium mb-1">Admin Note:</p>
                          <p className="whitespace-pre-wrap">{criteria.admin_notes}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Lock/Unlock and Admin Controls */}
                  {!isReorderingCriteria && event.created_by === user.id && (
                    <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200">
                      <button
                        onClick={() => handleToggleCriteriaLock(criteria.id)}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          criteria.is_locked
                            ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {criteria.is_locked ? (
                          <>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                            </svg>
                            <span>Unlock</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            <span>Lock</span>
                          </>
                        )}
                      </button>
                      
                      <button
                        onClick={() => handleOpenAdminNotes(criteria)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <span>{criteria.admin_notes ? 'Edit Note' : 'Add Note'}</span>
                      </button>
                      
                      {criteria.is_locked && (
                        <button
                          onClick={() => handleOpenAdminScoring(criteria)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span>
                            {/* Check if scores exist for this criteria */}
                            {event.candidates.some(c => {
                              const key = `${c.id}-${criteria.id}`
                              return adminScores[key] !== undefined && adminScores[key] !== null && adminScores[key] !== 0
                            }) ? 'Edit Scores' : 'Input Scores'}
                          </span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'awards' && (
          <div className="space-y-6">
            {/* Header with Category and Award Management */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <h3 className="text-lg font-semibold text-black">Awards</h3>
              <div className="flex flex-wrap items-center gap-3">
                {!isReorderingAwards ? (
                  <>
                    {specialAwards.filter(a => a.award_type === 'vote').length > 0 && (
                      <button
                        onClick={handleStartReorderAwards}
                        className="flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                        </svg>
                        <span>Reorder Judge Awards</span>
                      </button>
                    )}
                    <button
                      onClick={() => setShowAddCategoryModal(true)}
                      className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <span>Manage Categories</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleSaveAwardOrder}
                      className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Save Order</span>
                    </button>
                    <button
                      onClick={handleCancelReorderAwards}
                      className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span>Cancel</span>
                    </button>
                  </>
                )}
                {specialAwards.length > 0 && (
                  <button
                    onClick={() => handlePrintSpecialAwards()}
                    className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    <span>Print All Awards</span>
                  </button>
                )}
                <button
                  onClick={() => setShowAddAwardModal(true)}
                  className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Add Award</span>
                </button>
              </div>
            </div>

            {/* Reordering Info Banner */}
            {isReorderingAwards && (
              <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                <p className="text-sm text-indigo-800">
                  <strong>Drag and drop</strong> judge voting awards below to reorder them. This order will be reflected in the judge panel.
                </p>
              </div>
            )}

            {/* Reordering Mode - Show only vote-type awards */}
            {isReorderingAwards && (
              <div className="border border-indigo-300 rounded-lg overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 px-4 py-3 border-b border-indigo-300">
                  <h4 className="text-lg font-bold text-indigo-900">Judge Voting Awards (Reordering)</h4>
                  <p className="text-sm text-indigo-700 mt-1">Drag to reorder these awards as they will appear in the judge panel</p>
                </div>
                <div className="p-4 bg-gray-50">
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {awardsOrder.map((award, index) => (
                      <div
                        key={award.id}
                        draggable
                        onDragStart={(e) => handleAwardDragStart(e, award, index)}
                        onDragOver={(e) => handleAwardDragOver(e, index)}
                        onDragEnd={handleAwardDragEnd}
                        className="border border-indigo-300 rounded-lg p-4 bg-white cursor-move hover:bg-indigo-50 hover:border-indigo-400 transition-all"
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <svg className="w-5 h-5 text-indigo-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                          </svg>
                          <div className="flex-1">
                            <h4 className="font-semibold text-black">{award.name}</h4>
                            {award.description && (
                              <p className="text-sm text-gray-600 mt-1">{award.description}</p>
                            )}
                          </div>
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 flex-shrink-0">
                            #{index + 1}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Awards List - Grouped by Category */}
            {!isReorderingAwards && specialAwards.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">No Awards Yet</h4>
                <p className="text-gray-500 mb-4">Create your first award to recognize outstanding candidates.</p>
                <button
                  onClick={() => setShowAddAwardModal(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                >
                  Create First Award
                </button>
              </div>
            ) : !isReorderingAwards ? (
              <div className="space-y-6">
                {/* Awards grouped by category */}
                {(() => {
                  // Group awards by category
                  const uncategorized = specialAwards.filter(award => !award.category_id)
                  const categorizedAwards = awardCategories.map(category => ({
                    category,
                    awards: specialAwards.filter(award => award.category_id === category.id)
                  })).filter(group => group.awards.length > 0)

                  return (
                    <>
                      {/* Display each category */}
                      {categorizedAwards.map(({ category, awards }) => (
                        <div key={category.id} className="border border-gray-300 rounded-lg overflow-hidden">
                          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-300">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="text-lg font-bold text-gray-900">{category.name}</h4>
                                {category.description && (
                                  <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                                )}
                              </div>
                              <button
                                onClick={() => handlePrintSpecialAwards(category.id)}
                                className="flex items-center space-x-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                                title={`Print ${category.name} awards`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                </svg>
                                <span className="text-xs font-medium">Print</span>
                              </button>
                            </div>
                          </div>
                          <div className="p-4">
                            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                              {awards.map((award) => (
                                <div key={award.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex-1">
                                      <h4 className="font-semibold text-black mb-1">{award.name}</h4>
                                      {award.description && (
                                        <p className="text-sm text-gray-600 mb-2">{award.description}</p>
                                      )}
                                      <div className="flex items-center flex-wrap gap-2">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                          award.award_type === 'assigned' 
                                            ? 'bg-blue-100 text-blue-800' 
                                            : award.award_type === 'criteria_based'
                                            ? 'bg-green-100 text-green-800'
                                            : award.award_type === 'overall_ranking'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-purple-100 text-purple-800'
                                        }`}>
                                          {award.award_type === 'assigned'
                                            ? 'Assigned'
                                            : award.award_type === 'criteria_based'
                                            ? 'Criteria Winner'
                                            : award.award_type === 'overall_ranking'
                                            ? 'Overall Ranking'
                                            : 'Vote'}
                                        </span>
                                        {award.award_type === 'criteria_based' && award.criteria_id && (
                                          <span className="text-xs text-gray-600">
                                            → {event?.criteria?.find(c => c.id === award.criteria_id)?.name || 'Unknown Criteria'}
                                          </span>
                                        )}
                                        {award.award_type === 'overall_ranking' && award.overall_ranking_position && (
                                          <span className="text-xs text-gray-600">
                                            → {getRankingOptions().find(r => r.value === award.overall_ranking_position)?.label || 'Overall Ranking'}
                                          </span>
                                        )}
                                        {award.add_to_overall && award.overall_points > 0 && (
                                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" title="Points added to winner's overall score">
                                            +{award.overall_points} pts to overall
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex space-x-2">
                                      <button
                                        onClick={() => handleCastAward(award)}
                                        className="text-purple-600 hover:text-purple-700 p-1"
                                        title="Cast Award (Fullscreen Presentation)"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                      </button>
                                      <button
                                        onClick={() => handlePrintAward(award)}
                                        className="text-green-600 hover:text-green-700 p-1"
                                        title="Print Award"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                        </svg>
                                      </button>
                                      <button
                                        onClick={() => {
                                          setEditingAward(award)
                                          setShowEditAwardModal(true)
                                        }}
                                        className="text-blue-600 hover:text-blue-700 p-1"
                                        title="Edit Award"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                      </button>
                                      <button
                                        onClick={() => handleDeleteAward(award.id)}
                                        className="text-red-600 hover:text-red-700 p-1"
                                        title="Delete Award"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>

                                  {/* Award Assignment Section */}
                                  {award.award_type === 'assigned' ? (
                                    <div className="border-t border-gray-100 pt-3">
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Currently assigned to:
                                      </label>
                                      {award.assigned_candidate ? (
                                        <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
                                          <p className="text-sm text-green-800">
                                            <strong>#{award.assigned_candidate.candidate_number}</strong> - {award.assigned_candidate.name}
                                            {award.assigned_candidate.representation ? (
                                              <span className="text-green-700"> ({award.assigned_candidate.representation})</span>
                                            ) : null}
                                          </p>
                                        </div>
                                      ) : (
                                        <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                                          <p className="text-sm text-yellow-800">No candidate assigned yet. Click Edit to assign.</p>
                                        </div>
                                      )}
                                    </div>
                                  ) : award.award_type === 'criteria_based' ? (
                                    <div className="border-t border-gray-100 pt-3">
                                      {(() => {
                                        const linkedCriteria = event?.criteria?.find(c => c.id === award.criteria_id)
                                        if (!linkedCriteria) {
                                          return (
                                            <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                                              <p className="text-sm text-yellow-800">Criteria not found. Please edit award.</p>
                                            </div>
                                          )
                                        }
                                        
                                        // Calculate winner based on criteria scores
                                        const candidateScores = calculateCandidateScores()
                                        const criteriaWinner = candidateScores.reduce((max, current) => {
                                          const currentScore = current.criteriaScores?.[linkedCriteria.id] || 0
                                          const maxScore = max.criteriaScores?.[linkedCriteria.id] || 0
                                          return currentScore > maxScore ? current : max
                                        }, candidateScores[0] || {})
                                        
                                        const criteriaWinnerScore = criteriaWinner?.criteriaScores?.[linkedCriteria.id] || 0

                                        // Calculate scoring progress for this criteria
                                        const totalJudges = event?.judges?.length || 0
                                        const totalCandidates = event?.candidates?.length || 0
                                        const criteriaScores = event?.scores?.filter(s => s.criteria_id === linkedCriteria.id) || []
                                        const judgesWhoScored = new Set(criteriaScores.map(s => s.judge_id))
                                        const judgesCompleted = Array.from(judgesWhoScored).filter(judgeId => {
                                          const judgeScoresForCriteria = criteriaScores.filter(s => s.judge_id === judgeId)
                                          return judgeScoresForCriteria.length >= totalCandidates
                                        }).length
                                        const isComplete = judgesCompleted === totalJudges && totalJudges > 0
                                        
                                        return (
                                          <div className="space-y-2">
                                            {/* Scoring Progress */}
                                            <div className={`p-2 rounded-lg ${isComplete ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
                                              <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                  {isComplete ? (
                                                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                  ) : (
                                                    <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                  )}
                                                  <span className={`text-xs font-medium ${isComplete ? 'text-green-700' : 'text-orange-700'}`}>
                                                    {isComplete ? 'All judges scored' : `${judgesCompleted}/${totalJudges} judges completed`}
                                                  </span>
                                                </div>
                                                <button
                                                  onClick={() => {
                                                    setScoringDetailsData({
                                                      award: award,
                                                      type: 'criteria',
                                                      criteria: linkedCriteria
                                                    })
                                                    setShowScoringDetailsModal(true)
                                                  }}
                                                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                                >
                                                  View Details
                                                </button>
                                              </div>
                                            </div>
                                            {criteriaWinnerScore > 0 && criteriaWinner.candidate && (
                                              <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
                                                <div className="flex items-center gap-2">
                                                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                  </svg>
                                                  <span className="text-sm font-medium text-green-800">
                                                    Winner: <strong>#{criteriaWinner.candidate.candidate_number} - {criteriaWinner.candidate.name}</strong>
                                                  </span>
                                                </div>
                                                <p className="text-xs text-green-700 mt-1">
                                                  Score: {criteriaWinnerScore.toFixed(2)} points
                                                </p>
                                              </div>
                                            )}
                                          </div>
                                        )
                                      })()}
                                    </div>
                                  ) : award.award_type === 'overall_ranking' ? (
                                    <div className="border-t border-gray-100 pt-3">
                                      {(() => {
                                        if (!award.overall_ranking_position) {
                                          return (
                                            <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                                              <p className="text-sm text-yellow-800">No ranking position selected. Please edit award.</p>
                                            </div>
                                          )
                                        }

                                        const winner = getWinnerForRanking(award.overall_ranking_position)

                                        // Calculate overall scoring progress (all criteria)
                                        const totalJudges = event?.judges?.length || 0
                                        const totalCandidates = event?.candidates?.length || 0
                                        const totalCriteria = event?.criteria?.length || 0
                                        const judgesCompleted = event?.judges?.filter(judge => {
                                          const judgeScores = event?.scores?.filter(s => s.judge_id === judge.id) || []
                                          return judgeScores.length >= (totalCandidates * totalCriteria)
                                        }).length || 0
                                        const isComplete = judgesCompleted === totalJudges && totalJudges > 0

                                        return (
                                          <div className="space-y-2">
                                            {/* Scoring Progress */}
                                            <div className={`p-2 rounded-lg ${isComplete ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
                                              <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                  {isComplete ? (
                                                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                  ) : (
                                                    <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                  )}
                                                  <span className={`text-xs font-medium ${isComplete ? 'text-green-700' : 'text-orange-700'}`}>
                                                    {isComplete ? 'All judges scored' : `${judgesCompleted}/${totalJudges} judges completed`}
                                                  </span>
                                                </div>
                                                <button
                                                  onClick={() => {
                                                    setScoringDetailsData({
                                                      award: award,
                                                      type: 'overall_ranking',
                                                      allCriteria: event?.criteria || []
                                                    })
                                                    setShowScoringDetailsModal(true)
                                                  }}
                                                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                                >
                                                  View Details
                                                </button>
                                              </div>
                                            </div>
                                            {winner && winner.candidate && (
                                              <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
                                                <p className="text-sm text-green-800">
                                                  <strong>#{winner.candidate.candidate_number}</strong> - {winner.candidate.name}
                                                  {winner.candidate.representation ? (
                                                    <span className="text-green-700"> ({winner.candidate.representation})</span>
                                                  ) : null}
                                                </p>
                                              </div>
                                            )}
                                          </div>
                                        )
                                      })()}
                                    </div>
                                  ) : (
                                    <div className="border-t border-gray-100 pt-3">
                                      {(() => {
                                        // Get all scores for this award
                                        const awardScores = event.special_award_votes?.filter(vote => 
                                          vote.special_award_id === award.id
                                        ) || []
                                        
                                        // Calculate total scores per candidate
                                        const candidateScores = {}
                                        awardScores.forEach(score => {
                                          candidateScores[score.candidate_id] = (candidateScores[score.candidate_id] || 0) + (score.score || 0)
                                        })
                                        
                                        // Sort candidates by total score
                                        const sortedCandidates = event.candidates
                                          .map(candidate => ({
                                            ...candidate,
                                            totalScore: candidateScores[candidate.id] || 0
                                          }))
                                          .sort((a, b) => b.totalScore - a.totalScore)
                                        
                                        // Find winner(s) - candidates with highest total score
                                        const maxScore = sortedCandidates[0]?.totalScore || 0
                                        const winners = sortedCandidates.filter(c => c.totalScore === maxScore && maxScore > 0)

                                        // Calculate voting progress
                                        const totalJudges = event?.judges?.length || 0
                                        const judgesWhoVoted = new Set(awardScores.map(s => s.judge_id)).size
                                        const isComplete = judgesWhoVoted === totalJudges && totalJudges > 0
                                        
                                        return (
                                          <>
                                            {/* Voting Progress */}
                                            <div className={`mb-3 p-2 rounded-lg ${isComplete ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
                                              <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                  {isComplete ? (
                                                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                  ) : (
                                                    <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                  )}
                                                  <span className={`text-xs font-medium ${isComplete ? 'text-green-700' : 'text-orange-700'}`}>
                                                    {isComplete ? 'All judges voted' : `${judgesWhoVoted}/${totalJudges} judges voted`}
                                                  </span>
                                                </div>
                                                <button
                                                  onClick={() => {
                                                    setScoringDetailsData({
                                                      award: award,
                                                      type: 'vote'
                                                    })
                                                    setShowScoringDetailsModal(true)
                                                  }}
                                                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                                >
                                                  View Details
                                                </button>
                                              </div>
                                            </div>
                                            {/* Winner Display */}
                                            {winners.length > 0 && (
                                              <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                                                <div className="flex items-center gap-2 mb-1">
                                                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                  </svg>
                                                  <span className="text-sm font-medium text-green-800">
                                                    {winners.length === 1 ? 'Winner' : 'Winners'} ({maxScore} points)
                                                  </span>
                                                </div>
                                                <div className="max-h-20 overflow-y-auto space-y-1 pr-2">
                                                  {winners.map(winner => (
                                                    <div key={winner.id} className="flex items-center gap-2">
                                                      <span className="bg-green-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                                                        #{winner.candidate_number}
                                                      </span>
                                                      <span className="text-sm font-medium text-green-900">{winner.name}</span>
                                                      {winner.representation && (
                                                        <span className="text-xs text-green-700">({winner.representation})</span>
                                                      )}
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                            
                                            {/* Scoring Instructions */}
                                            <p className="text-sm text-gray-600 mb-2">
                                              <strong>Judges will score candidates for this award</strong>
                                            </p>
                                            <p className="text-xs text-gray-500">
                                              Judges can score each candidate (0-100) when evaluating
                                            </p>
                                          </>
                                        )
                                      })()}
                                    </div>
                                  )}
                                </div>
                ))}
              </div>
            </div>
          </div>
        ))}

                      {/* Uncategorized Awards */}
                      {uncategorized.length > 0 && (
                        <div className="border border-gray-300 rounded-lg overflow-hidden">
                          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-300">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="text-lg font-bold text-gray-900">Uncategorized</h4>
                                <p className="text-sm text-gray-600 mt-1">Awards without a category</p>
                              </div>
                              <button
                                onClick={() => handlePrintSpecialAwards('uncategorized')}
                                className="flex items-center space-x-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                                title="Print Uncategorized awards"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                </svg>
                                <span className="text-xs font-medium">Print</span>
                              </button>
                            </div>
                          </div>
                          <div className="p-4">
                            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                              {uncategorized.map((award) => (
                                <div key={award.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex-1">
                                      <h4 className="font-semibold text-black mb-1">{award.name}</h4>
                                      {award.description && (
                                        <p className="text-sm text-gray-600 mb-2">{award.description}</p>
                                      )}
                                    </div>
                                    <div className="flex space-x-2">
                                      <button
                                        onClick={() => handlePrintAward(award)}
                                        className="text-green-600 hover:text-green-700 p-1"
                                        title="Print Award"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                        </svg>
                                      </button>
                                      <button
                                        onClick={() => {
                                          setEditingAward(award)
                                          setShowEditAwardModal(true)
                                        }}
                                        className="text-blue-600 hover:text-blue-700 p-1"
                                        title="Edit Award"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                      </button>
                                      <button
                                        onClick={() => handleDeleteAward(award.id)}
                                        className="text-red-600 hover:text-red-700 p-1"
                                        title="Delete Award"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                  <div className="flex items-center flex-wrap gap-2 mt-2">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      award.award_type === 'assigned' 
                                        ? 'bg-blue-100 text-blue-800' 
                                        : award.award_type === 'criteria_based'
                                        ? 'bg-green-100 text-green-800'
                                        : award.award_type === 'overall_ranking'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-purple-100 text-purple-800'
                                    }`}>
                                      {award.award_type === 'assigned'
                                        ? 'Assigned'
                                        : award.award_type === 'criteria_based'
                                        ? 'Criteria Winner'
                                        : award.award_type === 'overall_ranking'
                                        ? 'Overall Ranking'
                                        : 'Vote'}
                                    </span>
                                    {award.add_to_overall && award.overall_points > 0 && (
                                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" title="Points added to winner's overall score">
                                        +{award.overall_points} pts to overall
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>
            ) : null}
          </div>
        )}

      {/* Full Screen Display */}
      {isFullScreen && (
          <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
            <div className="text-center text-white">
              {!isAnimating && !showWinner && (
                <div className="space-y-8">
                  <div className="text-6xl font-bold mb-4">
                    {displayMode === 'rankings' ? 'Rankings' : 'Awards'}
                  </div>
                  <div className="text-2xl">
                    {displayMode === 'rankings' 
                      ? getRankingOptions().find(r => r.value === selectedRanking)?.label
                      : specialAwards.find(a => a.id === selectedAward)?.name
                    }
                  </div>
                  <div className="text-lg opacity-75">
                    Press <kbd className="px-3 py-1 bg-white bg-opacity-20 rounded text-lg">Spacebar</kbd> to start
                  </div>
                </div>
              )}

              {isAnimating && countdown > 0 && (
                <div className="space-y-8">
                  <div className="text-8xl font-bold animate-pulse">
                    {countdown}
                  </div>
                  <div className="text-2xl opacity-75">
                    Get ready...
                  </div>
                </div>
              )}

              {showWinner && winnerData && (
                <div className="space-y-8" style={{
                  animation: 'fadeIn 1s ease-in-out'
                }}>
                  <div className="text-6xl font-bold mb-4">
                    🏆
                  </div>
                  <div className="text-4xl font-bold">
                    {winnerData.candidate?.name || winnerData.name}
                  </div>
                  {winnerData.candidate?.representation && (
                    <div className="text-2xl opacity-75">
                      {winnerData.candidate.representation}
                    </div>
                  )}
                  <div className="text-3xl font-semibold">
                    {displayMode === 'rankings' 
                      ? getRankingOptions().find(r => r.value === selectedRanking)?.label
                      : specialAwards.find(a => a.id === selectedAward)?.name
                    }
                  </div>
                  {winnerData.score && (
                    <div className="text-xl opacity-75">
                      Score: {winnerData.score}
                    </div>
                  )}
                </div>
              )}

              {showWinner && !winnerData && (
                <div className="space-y-8">
                  <div className="text-6xl font-bold mb-4">
                    ❌
                  </div>
                  <div className="text-3xl font-bold">
                    No Winner Found
                  </div>
                  <div className="text-xl opacity-75">
                    No data available for this selection
                  </div>
                </div>
              )}

              {/* Exit Button */}
              <button
                onClick={exitFullScreen}
                className="absolute top-4 right-4 p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
      )}

      {/* Removed Reset Judge Confirmation Modal */}

      {/* Add Award Modal */}
      {showAddAwardModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowAddAwardModal(false)}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-black mb-2">Add Award</h3>
                <p className="text-gray-600">Create a new award for your event</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Award Name *
                  </label>
                  <input
                    type="text"
                    value={newAward.name}
                    onChange={(e) => setNewAward({...newAward, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., Best in Talent, Most Promising"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={newAward.description}
                    onChange={(e) => setNewAward({...newAward, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Optional description of the award"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category (Optional)
                  </label>
                  <select
                    value={newAward.category_id}
                    onChange={(e) => setNewAward({...newAward, category_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Uncategorized</option>
                    {awardCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {awardCategories.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">Create categories first to organize your awards</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Award Type *
                  </label>
                  <select
                    value={newAward.award_type}
                    onChange={(e) => setNewAward({
                      ...newAward,
                      award_type: e.target.value,
                      assigned_candidate_id: '',
                      criteria_id: '',
                      overall_ranking_position: ''
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="assigned">Assigned by Admin</option>
                    <option value="criteria_based">Criteria Winner</option>
                    <option value="vote">Judge Voting</option>
                    <option value="overall_ranking">Overall Ranking (Top Candidates)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {newAward.award_type === 'assigned' && 'Manually assign to a specific candidate'}
                    {newAward.award_type === 'criteria_based' && 'Automatically awarded to the winner of a specific criteria'}
                    {newAward.award_type === 'vote' && 'Judges vote for the winner'}
                    {newAward.award_type === 'overall_ranking' && 'Automatically awarded based on overall rankings (Top 1, 1st runner-up, etc.)'}
                  </p>
                </div>

                {newAward.award_type === 'assigned' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assign to Candidate
                    </label>
                    <select
                      value={newAward.assigned_candidate_id}
                      onChange={(e) => setNewAward({...newAward, assigned_candidate_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="">Select a candidate (optional)</option>
                      {event?.candidates?.map((candidate) => (
                        <option key={candidate.id} value={candidate.id}>
                          #{candidate.candidate_number} - {candidate.name} ({candidate.representation})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {newAward.award_type === 'criteria_based' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Link to Criteria *
                    </label>
                    <select
                      value={newAward.criteria_id}
                      onChange={(e) => setNewAward({...newAward, criteria_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select a criteria</option>
                      {event?.criteria?.map((criteria) => (
                        <option key={criteria.id} value={criteria.id}>
                          {criteria.name} ({criteria.percentage}%)
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Winner of this criteria will automatically receive this award
                    </p>
                  </div>
                )}

                {newAward.award_type === 'overall_ranking' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Link to Overall Ranking *
                    </label>
                    <select
                      value={newAward.overall_ranking_position}
                      onChange={(e) => setNewAward({...newAward, overall_ranking_position: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select a ranking position</option>
                      {getRankingOptions().map((ranking) => (
                        <option key={ranking.value} value={ranking.value}>
                          {ranking.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      The current top candidate for this ranking will receive this award
                    </p>
                  </div>
                )}

                {/* Add to Overall Scoring - For vote and assigned types */}
                {(newAward.award_type === 'vote' || newAward.award_type === 'assigned') && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Add to Overall Scoring</label>
                        <p className="text-xs text-gray-500">Winner's points will be added to their overall score</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setNewAward({...newAward, add_to_overall: !newAward.add_to_overall})}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          newAward.add_to_overall ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          newAward.add_to_overall ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                    
                    {newAward.add_to_overall && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Points for Winner *
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          value={newAward.overall_points}
                          onChange={(e) => setNewAward({...newAward, overall_points: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="e.g., 5"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          These points will be added to the winner's total score in the rankings
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowAddAwardModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddAward}
                  disabled={!newAward.name.trim() || (newAward.award_type === 'criteria_based' && !newAward.criteria_id)}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Award
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Event Tab */}
        {activeTab === 'edit' && (
          <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-black">Edit Event Details</h3>
                  <p className="text-sm text-gray-600 mt-1">Update event information, criteria, and candidates</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition-colors font-medium"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={saving}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {saving ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : 'Save Changes'}
                  </button>
                </div>
              </div>

              {/* Safety Warning Banner */}
              {event.scores && event.scores.length > 0 && (
                <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-yellow-800 mb-1">⚠️ Event Has Existing Scores</h4>
                      <p className="text-xs text-yellow-700 mb-2">
                        This event has <strong>{event.scores.length} scores</strong> submitted by judges.
                      </p>
                      <div className="text-xs text-yellow-700 space-y-1">
                        <p className="font-medium">✅ Safe to edit:</p>
                        <ul className="list-disc ml-5 mb-2">
                          <li>Event name and logo</li>
                          <li>Criteria names, descriptions, and percentages</li>
                          <li>Candidate names and representations</li>
                          <li>Adding new criteria or candidates</li>
                        </ul>
                        <p className="font-medium text-red-700">❌ Will DELETE scores if you:</p>
                        <ul className="list-disc ml-5">
                          <li>Remove existing criteria</li>
                          <li>Remove existing candidates</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Event Basic Info */}
              <div className="space-y-6 mb-8">
                <h4 className="text-lg font-semibold text-black border-b pb-2">Basic Information</h4>
                
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Event Name *
                  </label>
                  <input
                    type="text"
                    value={editData.name}
                    onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Enter event name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Event Logo
                  </label>
                  {editData.logoPreview && (
                    <div className="mb-3">
                      <img src={editData.logoPreview} alt="Event Logo Preview" className="w-32 h-32 object-cover rounded-lg border border-gray-200" />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleEditLogoUpload}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <p className="text-sm text-gray-500 mt-1">Upload a new image to replace the current logo</p>
                </div>
              </div>

              {/* Judging Criteria */}
              <div className="space-y-6 mb-8">
                <div className="flex justify-between items-center border-b pb-2">
                  <h4 className="text-lg font-semibold text-black">Judging Criteria</h4>
                  <button
                    type="button"
                    onClick={addEditCriteria}
                    className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Criteria
                  </button>
                </div>

                {editData.criteria.map((criterion, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50">
                    <div className="flex gap-4 items-start">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-black mb-2">
                          Criteria Name *
                        </label>
                        <input
                          type="text"
                          value={criterion.name}
                          onChange={(e) => updateEditCriteria(index, 'name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="e.g., Presentation Skills"
                        />
                      </div>
                      <div className="w-24">
                        <label className="block text-sm font-medium text-black mb-2">
                          % *
                        </label>
                        <input
                          type="number"
                          value={criterion.percentage}
                          onChange={(e) => updateEditCriteria(index, 'percentage', parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                          min="0"
                          max="100"
                        />
                      </div>
                      {editData.criteria.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeEditCriteria(index)}
                          className="text-red-600 hover:text-red-700 p-2 mt-6"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-black mb-2">
                        Description (Optional)
                      </label>
                      <textarea
                        value={criterion.description}
                        onChange={(e) => updateEditCriteria(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                        rows="2"
                        placeholder="Briefly describe what this criteria evaluates..."
                      />
                    </div>
                  </div>
                ))}

                <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <strong>Total: {editData.criteria.reduce((sum, c) => sum + c.percentage, 0)}%</strong>
                  {editData.criteria.reduce((sum, c) => sum + c.percentage, 0) !== 100 && (
                    <span className="text-red-600 ml-2">(Must equal 100%)</span>
                  )}
                  {editData.criteria.reduce((sum, c) => sum + c.percentage, 0) === 100 && (
                    <span className="text-green-600 ml-2">✓ Valid</span>
                  )}
                </div>
              </div>

              {/* Candidates */}
              <div className="space-y-6">
                <div className="flex justify-between items-center border-b pb-2">
                  <h4 className="text-lg font-semibold text-black">Candidates</h4>
                  <button
                    type="button"
                    onClick={addEditCandidate}
                    className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Candidate
                  </button>
                </div>

                {editData.candidates.map((candidate, index) => (
                  <div key={index} className="flex gap-4 items-end border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="w-24">
                      <label className="block text-sm font-medium text-black mb-2">
                        Number *
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={candidate.candidate_number}
                        onChange={(e) => updateEditCandidate(index, 'candidate_number', parseInt(e.target.value) || 1)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="#"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-black mb-2">
                        Candidate Name *
                      </label>
                      <input
                        type="text"
                        value={candidate.name}
                        onChange={(e) => updateEditCandidate(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Enter candidate name"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-black mb-2">
                        Representation *
                      </label>
                      <input
                        type="text"
                        value={candidate.representation}
                        onChange={(e) => updateEditCandidate(index, 'representation', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="e.g., School, Company, etc."
                      />
                    </div>
                    {editData.candidates.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEditCandidate(index)}
                        className="text-red-600 hover:text-red-700 p-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Save/Cancel Buttons at Bottom */}
              <div className="flex justify-end gap-3 mt-8 pt-6 border-t">
                <button
                  onClick={handleCancelEdit}
                  className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition-colors font-medium"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Award Modal */}
        {showEditAwardModal && editingAward && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => {
              setShowEditAwardModal(false)
              setEditingAward(null)
            }}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-black mb-2">Edit Award</h3>
                <p className="text-gray-600">Update award details and settings</p>
              </div>

              <div className="space-y-4">
                {/* Award Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Award Name *
                  </label>
                  <input
                    type="text"
                    value={editingAward?.name || ''}
                    onChange={(e) => setEditingAward(prev => ({...(prev || {}), name: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Best in Talent, Most Promising"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={editingAward?.description || ''}
                    onChange={(e) => setEditingAward(prev => ({...(prev || {}), description: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Brief description of the award"
                    rows="3"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category (Optional)
                  </label>
                  <select
                    value={editingAward?.category_id || ''}
                    onChange={(e) => setEditingAward(prev => ({...(prev || {}), category_id: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Uncategorized</option>
                    {awardCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {awardCategories.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">No categories available. Create one first.</p>
                  )}
                </div>

                {/* Award Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Award Type *
                  </label>
                  <select
                    value={editingAward?.award_type || 'assigned'}
                    onChange={(e) => setEditingAward(prev => ({
                      ...(prev || {}),
                      award_type: e.target.value,
                      assigned_candidate_id: '',
                      criteria_id: '',
                      overall_ranking_position: ''
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="assigned">Assigned by Admin</option>
                    <option value="criteria_based">Criteria Winner</option>
                    <option value="vote">Judge Voting</option>
                    <option value="overall_ranking">Overall Ranking (Top Candidates)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {editingAward?.award_type === 'assigned' && 'Manually assign to a specific candidate'}
                    {editingAward?.award_type === 'criteria_based' && 'Automatically awarded to the winner of a specific criteria'}
                    {editingAward?.award_type === 'vote' && 'Judges vote for the winner'}
                    {editingAward?.award_type === 'overall_ranking' && 'Automatically awarded based on overall rankings (Top 1, 1st runner-up, etc.)'}
                  </p>
                </div>

                {/* Assigned Candidate (for assigned type) */}
                {editingAward?.award_type === 'assigned' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assign to Candidate
                    </label>
                    <select
                      value={editingAward?.assigned_candidate_id || ''}
                      onChange={(e) => setEditingAward(prev => ({...(prev || {}), assigned_candidate_id: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select a candidate (optional)</option>
                      {event?.candidates?.map((candidate) => (
                        <option key={candidate.id} value={candidate.id}>
                          #{candidate.candidate_number} - {candidate.name} ({candidate.representation})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Linked Criteria (for criteria_based type) */}
                {editingAward?.award_type === 'criteria_based' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Link to Criteria *
                    </label>
                    <select
                      value={editingAward?.criteria_id || ''}
                      onChange={(e) => setEditingAward(prev => ({...(prev || {}), criteria_id: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select a criteria</option>
                      {event?.criteria?.map((criteria) => (
                        <option key={criteria.id} value={criteria.id}>
                          {criteria.name} ({criteria.percentage}%)
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Winner of this criteria will automatically receive this award
                    </p>
                  </div>
                )}

                {/* Linked Overall Ranking (for overall_ranking type) */}
                {editingAward?.award_type === 'overall_ranking' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Link to Overall Ranking *
                    </label>
                    <select
                      value={editingAward?.overall_ranking_position || ''}
                      onChange={(e) => setEditingAward(prev => ({...(prev || {}), overall_ranking_position: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select a ranking position</option>
                      {getRankingOptions().map((ranking) => (
                        <option key={ranking.value} value={ranking.value}>
                          {ranking.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      The current top candidate for this ranking will receive this award
                    </p>
                  </div>
                )}

                {/* Add to Overall Scoring - For vote and assigned types */}
                {(editingAward?.award_type === 'vote' || editingAward?.award_type === 'assigned') && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Add to Overall Scoring</label>
                        <p className="text-xs text-gray-500">Winner's points will be added to their overall score</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEditingAward(prev => ({...(prev || {}), add_to_overall: !prev?.add_to_overall}))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          editingAward?.add_to_overall ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          editingAward?.add_to_overall ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                    
                    {editingAward?.add_to_overall && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Points for Winner *
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          value={editingAward?.overall_points || 0}
                          onChange={(e) => setEditingAward(prev => ({...(prev || {}), overall_points: e.target.value}))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g., 5"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          These points will be added to the winner's total score in the rankings
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowEditAwardModal(false)
                    setEditingAward(null)
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditAward}
                  disabled={
                    !editingAward?.name?.trim() ||
                    (editingAward?.award_type === 'criteria_based' && !editingAward?.criteria_id)
                  }
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Update Award
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Manage Categories Modal */}
        {showAddCategoryModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowAddCategoryModal(false)}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-black">Manage Award Categories</h3>
                <button
                  onClick={() => setShowAddCategoryModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Existing Categories */}
              {awardCategories.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Award Categories</h4>
                  <div className="flex flex-wrap gap-2">
                    {awardCategories.map((category) => (
                      <div key={category.id} className="flex items-center bg-white border-2 border-gray-300 rounded-lg px-3 py-2">
                        <span className="font-medium text-gray-900">{category.name}</span>
                        <button
                          onClick={() => {
                            setEditingCategory(category)
                            setShowEditCategoryModal(true)
                          }}
                          className="ml-2 text-blue-600 hover:text-blue-700"
                          title="Edit category"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className="ml-1 text-red-600 hover:text-red-700"
                          title="Delete category"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add New Category Form */}
              <div className="border-t pt-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-4">Add New Category</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category Name *
                    </label>
                    <input
                      type="text"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                      placeholder="e.g., Major, Minor, Special"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description (Optional)
                    </label>
                    <textarea
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                      placeholder="Brief description of this category"
                      rows="2"
                    />
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <button
                    onClick={() => setShowAddCategoryModal(false)}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg transition-colors font-medium"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleAddCategory}
                    disabled={!newCategory.name.trim()}
                    className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Create Category
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Category Modal */}
        {showEditCategoryModal && editingCategory && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => {
              setShowEditCategoryModal(false)
              setEditingCategory(null)
            }}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-black mb-2">Edit Category</h3>
                <p className="text-gray-600">Update category details</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    value={editingCategory.name}
                    onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Major, Minor, Special"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={editingCategory.description || ''}
                    onChange={(e) => setEditingCategory({...editingCategory, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Brief description of this category"
                    rows="2"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowEditCategoryModal(false)
                    setEditingCategory(null)
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditCategory}
                  disabled={!editingCategory.name.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Update Category
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Event Modal */}
        {showDeleteModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => {
              setShowDeleteModal(false)
              setDeletePassword('')
            }}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-black mb-2">Delete Event</h3>
                <p className="text-gray-600">
                  Are you sure you want to delete "<strong>{event.name}</strong>"? 
                  This action cannot be undone and will permanently remove all event data including:
                </p>
                <ul className="text-sm text-gray-600 mt-3 text-left">
                  <li>• All candidates and their scores</li>
                  <li>• All judges and access codes</li>
                  <li>• All judging criteria</li>
                  <li>• Event logo and settings</li>
                </ul>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-black mb-2">
                  Enter your password to confirm *
                </label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && deletePassword.trim() && !deleting) {
                      handleDeleteEvent()
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Enter your password"
                  required
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false)
                    setDeletePassword('')
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg transition-colors font-medium"
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteEvent}
                  disabled={deleting || !deletePassword.trim()}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting ? 'Deleting...' : 'Delete Event'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Judge Modal */}
        {showAddJudgeModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => {
              setShowAddJudgeModal(false)
              setJudgeActionPassword('')
              setJudgeActionError('')
            }}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-black mb-2">Add New Judge</h3>
                <p className="text-gray-600 mb-4">
                  This will add a new judge slot to the event and generate a unique access code.
                </p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-left">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div className="text-sm text-yellow-800">
                      <p className="font-semibold mb-1">Security Notice:</p>
                      <p>The new judge access code will be displayed only once. Make sure to save it securely.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-black mb-2">
                  Enter your password to confirm *
                </label>
                <input
                  type="password"
                  value={judgeActionPassword}
                  onChange={(e) => {
                    setJudgeActionPassword(e.target.value)
                    setJudgeActionError('')
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && judgeActionPassword.trim() && !isProcessingJudgeAction) {
                      handleAddJudge()
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Enter your password"
                  required
                />
                {judgeActionError && (
                  <p className="text-sm text-red-600 mt-2">{judgeActionError}</p>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowAddJudgeModal(false)
                    setJudgeActionPassword('')
                    setJudgeActionError('')
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg transition-colors font-medium"
                  disabled={isProcessingJudgeAction}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddJudge}
                  disabled={isProcessingJudgeAction || !judgeActionPassword.trim()}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessingJudgeAction ? 'Adding...' : 'Add Judge'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Remove Judge Modal */}
        {showRemoveJudgeModal && judgeToRemove && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => {
              setShowRemoveJudgeModal(false)
              setJudgeToRemove(null)
              setJudgeActionPassword('')
              setJudgeActionError('')
            }}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-black mb-2">Remove Judge</h3>
                <p className="text-gray-600 mb-3">
                  Are you sure you want to remove <strong>Judge #{judgeToRemove.judge_number}</strong>
                  {judgeToRemove.name && <> (<strong>{judgeToRemove.name}</strong>)</>}?
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-left">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div className="text-sm text-red-800">
                      <p className="font-semibold mb-1">Warning - This action cannot be undone!</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Judge will be permanently removed</li>
                        <li>Access code: <span className="font-mono font-bold">{judgeToRemove.judge_access_code}</span> will be revoked</li>
                        <li>All scores submitted by this judge will be deleted</li>
                        <li>All special award votes by this judge will be deleted</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-black mb-2">
                  Enter your password to confirm *
                </label>
                <input
                  type="password"
                  value={judgeActionPassword}
                  onChange={(e) => {
                    setJudgeActionPassword(e.target.value)
                    setJudgeActionError('')
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && judgeActionPassword.trim() && !isProcessingJudgeAction) {
                      handleRemoveJudge()
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Enter your password"
                  required
                />
                {judgeActionError && (
                  <p className="text-sm text-red-600 mt-2">{judgeActionError}</p>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowRemoveJudgeModal(false)
                    setJudgeToRemove(null)
                    setJudgeActionPassword('')
                    setJudgeActionError('')
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg transition-colors font-medium"
                  disabled={isProcessingJudgeAction}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRemoveJudge}
                  disabled={isProcessingJudgeAction || !judgeActionPassword.trim()}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessingJudgeAction ? 'Removing...' : 'Remove Judge'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Scoring Details Modal */}
        {showScoringDetailsModal && scoringDetailsData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{scoringDetailsData.award.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {scoringDetailsData.type === 'criteria' && scoringDetailsData.criteria && 
                        `Criteria: ${scoringDetailsData.criteria.name}`
                      }
                      {scoringDetailsData.type === 'overall_ranking' && 
                        `Overall Ranking (All Criteria)`
                      }
                      {scoringDetailsData.type === 'vote' && 
                        `Vote-based Award`
                      }
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowScoringDetailsModal(false)
                      setScoringDetailsData(null)
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="p-6 overflow-y-auto flex-1">
                {scoringDetailsData.type === 'criteria' && scoringDetailsData.criteria && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900 mb-3">Judge Scoring Status</h4>
                    {event?.judges?.map(judge => {
                      const totalCandidates = event?.candidates?.length || 0
                      const criteriaScores = event?.scores?.filter(
                        s => s.criteria_id === scoringDetailsData.criteria.id && s.judge_id === judge.id
                      ) || []
                      const scoredCandidates = new Set(criteriaScores.map(s => s.candidate_id)).size
                      const isComplete = scoredCandidates >= totalCandidates
                      
                      return (
                        <div key={judge.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isComplete ? 'bg-green-500' : 'bg-orange-400'}`}>
                              {isComplete ? (
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3" />
                                </svg>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">Judge #{judge.judge_number} - {judge.name}</p>
                              <p className="text-sm text-gray-600">{scoredCandidates}/{totalCandidates} candidates scored</p>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

                {scoringDetailsData.type === 'overall_ranking' && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900 mb-3">Judge Scoring Status (All Criteria)</h4>
                    {event?.judges?.map(judge => {
                      const totalCandidates = event?.candidates?.length || 0
                      const totalCriteria = event?.criteria?.length || 0
                      const judgeScores = event?.scores?.filter(s => s.judge_id === judge.id) || []
                      const expectedScores = totalCandidates * totalCriteria
                      const isComplete = judgeScores.length >= expectedScores
                      
                      return (
                        <div key={judge.id} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isComplete ? 'bg-green-500' : 'bg-orange-400'}`}>
                              {isComplete ? (
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3" />
                                </svg>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">Judge #{judge.judge_number} - {judge.name}</p>
                              <p className="text-sm text-gray-600">{judgeScores.length}/{expectedScores} total scores submitted</p>
                            </div>
                          </div>
                          
                          {/* Breakdown by criteria */}
                          <div className="ml-9 mt-2 space-y-1">
                            {scoringDetailsData.allCriteria.map(criteria => {
                              const criteriaScores = judgeScores.filter(s => s.criteria_id === criteria.id)
                              const scoredCandidates = new Set(criteriaScores.map(s => s.candidate_id)).size
                              const criteriaComplete = scoredCandidates >= totalCandidates
                              
                              return (
                                <div key={criteria.id} className="flex items-center gap-2 text-sm">
                                  {criteriaComplete ? (
                                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  )}
                                  <span className={criteriaComplete ? 'text-gray-700' : 'text-gray-500'}>
                                    {criteria.name} ({scoredCandidates}/{totalCandidates})
                                  </span>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

                {scoringDetailsData.type === 'vote' && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900 mb-3">Judge Voting Status</h4>
                    {event?.judges?.map(judge => {
                      const awardVotes = event.special_award_votes?.filter(
                        vote => vote.special_award_id === scoringDetailsData.award.id && vote.judge_id === judge.id
                      ) || []
                      const hasVoted = awardVotes.length > 0
                      
                      return (
                        <div key={judge.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${hasVoted ? 'bg-green-500' : 'bg-orange-400'}`}>
                              {hasVoted ? (
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3" />
                                </svg>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">Judge #{judge.judge_number} - {judge.name}</p>
                              <p className="text-sm text-gray-600">{hasVoted ? 'Voted' : 'Not yet voted'}</p>
                            </div>
                          </div>
                          {hasVoted && awardVotes[0]?.candidate_id && (
                            <div className="text-sm text-gray-600">
                              Voted for: <span className="font-medium">
                                #{event.candidates.find(c => c.id === awardVotes[0].candidate_id)?.candidate_number} - 
                                {event.candidates.find(c => c.id === awardVotes[0].candidate_id)?.name}
                              </span>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowScoringDetailsModal(false)
                    setScoringDetailsData(null)
                  }}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Restore Event Modal */}
        {showRestoreModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Restore Scores</h3>
                  <button
                    onClick={() => {
                      setShowRestoreModal(false)
                      setRestoreFile(null)
                    }}
                    className="text-gray-400 hover:text-gray-600"
                    disabled={isRestoring}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-800">Scores Only</p>
                      <p className="text-sm text-blue-700 mt-1">
                        This will restore only the scores. Criteria, candidates, and judges will remain unchanged.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Scores Backup File (.json)
                  </label>
                  <input
                    type="file"
                    accept=".json"
                    onChange={(e) => setRestoreFile(e.target.files[0])}
                    disabled={isRestoring}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 disabled:opacity-50"
                  />
                  {restoreFile && (
                    <p className="mt-2 text-sm text-gray-600">
                      Selected: <span className="font-medium">{restoreFile.name}</span>
                    </p>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowRestoreModal(false)
                      setRestoreFile(null)
                    }}
                    disabled={isRestoring}
                    className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors font-medium disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRestoreEvent}
                    disabled={!restoreFile || isRestoring}
                    className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isRestoring ? (
                      <>
                        <svg className="animate-spin h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Restoring...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Restore Scores
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Event Settings Modal */}
        {showEventSettingsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
              <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <h3 className="text-2xl font-bold">Event Settings</h3>
                  </div>
                  <button
                    onClick={() => setShowEventSettingsModal(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mt-4">
                  {event.created_by === user.id && (
                    <button
                      onClick={() => setSettingsTab('coadmins')}
                      className={`px-4 py-2 rounded-lg font-medium transition-all ${
                        settingsTab === 'coadmins'
                          ? 'bg-white text-purple-600'
                          : 'bg-purple-700 text-white hover:bg-purple-800'
                      }`}
                    >
                      Co-Admins
                    </button>
                  )}
                  <button
                    onClick={() => setSettingsTab('cast-background')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      settingsTab === 'cast-background'
                        ? 'bg-white text-purple-600'
                        : 'bg-purple-700 text-white hover:bg-purple-800'
                    }`}
                  >
                    Cast Background
                  </button>
                  {event.created_by === user.id && (
                    <button
                      onClick={() => {
                        setSettingsTab('activity-logs')
                        fetchActivityLogs()
                      }}
                      className={`px-4 py-2 rounded-lg font-medium transition-all ${
                        settingsTab === 'activity-logs'
                          ? 'bg-white text-purple-600'
                          : 'bg-purple-700 text-white hover:bg-purple-800'
                      }`}
                    >
                      Activity Logs
                    </button>
                  )}
                </div>
              </div>

              <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
                {/* Co-Admins Tab - Owner Only */}
                {settingsTab === 'coadmins' && event.created_by === user.id && (
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Add Co-Admin</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Co-admins can view and manage this event. They need to have an account first.
                      </p>
                      <div className="flex gap-3">
                        <input
                          type="email"
                          value={coAdminEmail}
                          onChange={(e) => setCoAdminEmail(e.target.value)}
                          placeholder="Enter email address"
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                        <button
                          onClick={handleAddCoAdmin}
                          disabled={addingCoAdmin}
                          className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {addingCoAdmin ? 'Adding...' : 'Add'}
                        </button>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Current Co-Admins</h4>
                      {loadingCoAdmins ? (
                        <div className="text-center py-8">
                          <svg className="animate-spin h-8 w-8 text-purple-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <p className="text-gray-500 mt-2">Loading co-admins...</p>
                        </div>
                      ) : coAdminsDetails.length > 0 ? (
                        <div className="space-y-2">
                          {coAdminsDetails.map((coAdmin) => (
                            <div key={coAdmin.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{coAdmin.email}</p>
                                  <p className="text-xs text-gray-500">ID: {coAdmin.id.substring(0, 8)}...</p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleRemoveCoAdmin(coAdmin.id)}
                                className="text-red-600 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                                title="Remove co-admin"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 bg-gray-50 rounded-lg">
                          <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <p className="text-gray-500">No co-admins added yet</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Cast Background Tab */}
                {settingsTab === 'cast-background' && (
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Custom Cast Background</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Upload an image or video to be used as the background when casting awards. Supports JPG, PNG, GIF, WEBP, MP4, WEBM, OGG (max 50MB).
                      </p>
                      
                      {/* Current Background */}
                      {event.cast_background_url && !castBackgroundPreview && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Current Background:</p>
                          <div className="relative rounded-lg overflow-hidden border-2 border-gray-200">
                            {event.cast_background_type === 'video' ? (
                              <video 
                                key={event.cast_background_url} 
                                src={event.cast_background_url} 
                                className="w-full h-48 object-cover" 
                                muted 
                                loop 
                                autoPlay 
                              />
                            ) : (
                              <img 
                                key={event.cast_background_url} 
                                src={event.cast_background_url} 
                                alt="Cast Background" 
                                className="w-full h-48 object-cover" 
                              />
                            )}
                            <button
                              onClick={handleRemoveCastBackground}
                              className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition-colors"
                              title="Remove background"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Preview */}
                      {castBackgroundPreview && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                          <div className="relative rounded-lg overflow-hidden border-2 border-purple-400">
                            {castBackgroundFile.type.startsWith('video') ? (
                              <video src={castBackgroundPreview} className="w-full h-48 object-cover" muted loop autoPlay />
                            ) : (
                              <img src={castBackgroundPreview} alt="Preview" className="w-full h-48 object-cover" />
                            )}
                          </div>
                        </div>
                      )}

                      {/* Upload Input */}
                      <div className="mb-4">
                        <input
                          type="file"
                          accept="image/*,video/*"
                          onChange={handleCastBackgroundUpload}
                          disabled={uploadingBackground}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 disabled:opacity-50"
                        />
                      </div>

                      {/* Save Button */}
                      {castBackgroundFile && (
                        <div className="flex gap-3">
                          <button
                            onClick={() => {
                              setCastBackgroundFile(null)
                              setCastBackgroundPreview(null)
                            }}
                            disabled={uploadingBackground}
                            className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors font-medium disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveCastBackground}
                            disabled={uploadingBackground}
                            className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {uploadingBackground ? (
                              <>
                                <svg className="animate-spin h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Uploading...
                              </>
                            ) : (
                              <>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Save Background
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Activity Logs Tab - Owner Only */}
                {settingsTab === 'activity-logs' && event.created_by === user.id && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">Activity Logs</h4>
                        <p className="text-sm text-gray-600">
                          Track all changes made by admins and co-admins
                        </p>
                      </div>
                      <button
                        onClick={fetchActivityLogs}
                        disabled={loadingLogs}
                        className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <svg className={`w-4 h-4 ${loadingLogs ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh
                      </button>
                    </div>

                    {/* Filter */}
                    <div className="flex gap-2">
                      {['all', 'create', 'update', 'delete'].map((filter) => (
                        <button
                          key={filter}
                          onClick={() => setLogsFilter(filter)}
                          className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                            logsFilter === filter
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {filter.charAt(0).toUpperCase() + filter.slice(1)}
                        </button>
                      ))}
                    </div>

                    {/* Logs List */}
                    {loadingLogs ? (
                      <div className="text-center py-12">
                        <svg className="animate-spin h-8 w-8 text-purple-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="text-gray-500 mt-2">Loading activity logs...</p>
                      </div>
                    ) : activityLogs.filter(log => logsFilter === 'all' || log.action_type === logsFilter).length > 0 ? (
                      <div className="space-y-3">
                        {activityLogs
                          .filter(log => logsFilter === 'all' || log.action_type === logsFilter)
                          .map((log) => (
                            <div key={log.id} className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors">
                              <div className="flex items-start gap-3">
                                {/* Action Icon */}
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  log.action_type === 'create' ? 'bg-green-100' :
                                  log.action_type === 'update' ? 'bg-blue-100' :
                                  'bg-red-100'
                                }`}>
                                  {log.action_type === 'create' && (
                                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                  )}
                                  {log.action_type === 'update' && (
                                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  )}
                                  {log.action_type === 'delete' && (
                                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  )}
                                </div>

                                {/* Log Details */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      <p className="font-medium text-gray-900">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mr-2 ${
                                          log.action_type === 'create' ? 'bg-green-100 text-green-800' :
                                          log.action_type === 'update' ? 'bg-blue-100 text-blue-800' :
                                          'bg-red-100 text-red-800'
                                        }`}>
                                          {log.action_type.toUpperCase()}
                                        </span>
                                        {log.entity_type.replace('_', ' ').toUpperCase()}
                                      </p>
                                      <p className="text-sm text-gray-700 mt-1">
                                        {log.entity_name}
                                        {log.details?.description && ` - ${log.details.description}`}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        {log.user_email}
                                      </p>
                                    </div>
                                    <p className="text-xs text-gray-500 whitespace-nowrap">
                                      {new Date(log.created_at).toLocaleString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-gray-500">No activity logs found</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-6 py-4 flex justify-end">
                <button
                  onClick={() => setShowEventSettingsModal(false)}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Admin Scoring Modal for Locked Criteria */}
        {showAdminScoringModal && selectedLockedCriteria && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowAdminScoringModal(false)
              setSelectedLockedCriteria(null)
            }}
          >
            <div 
              className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">
                        {event.candidates.some(c => {
                          const key = `${c.id}-${selectedLockedCriteria.id}`
                          return adminScores[key] !== undefined && adminScores[key] !== null && adminScores[key] !== 0
                        }) ? 'Edit Admin Scores' : 'Input Admin Scores'}
                      </h3>
                      <p className="text-purple-100 text-sm mt-1">
                        {selectedLockedCriteria.name} (Max: {selectedLockedCriteria.percentage}%)
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowAdminScoringModal(false)
                      setSelectedLockedCriteria(null)
                    }}
                    className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                {selectedLockedCriteria.description && (
                  <div className="mt-3 p-3 bg-white bg-opacity-10 rounded-lg">
                    <p className="text-sm text-purple-50">{selectedLockedCriteria.description}</p>
                  </div>
                )}
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="space-y-3">
                  {event.candidates.map((candidate) => {
                    const key = `${candidate.id}-${selectedLockedCriteria.id}`
                    const score = adminScores[key] || 0
                    
                    return (
                      <div 
                        key={candidate.id}
                        className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-purple-300 transition-colors"
                      >
                        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                          #{candidate.candidate_number}
                        </div>
                        
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{candidate.name}</h4>
                          {candidate.representation && (
                            <p className="text-sm text-gray-600">{candidate.representation}</p>
                          )}
                        </div>

                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            min="0"
                            max={selectedLockedCriteria.percentage}
                            step="0.01"
                            value={score}
                            onChange={(e) => handleAdminScoreChange(
                              candidate.id, 
                              selectedLockedCriteria.id, 
                              e.target.value
                            )}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center font-semibold"
                            placeholder="0"
                          />
                          <span className="text-sm text-gray-500 font-medium">
                            / {selectedLockedCriteria.percentage}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>

                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">About Locked Criteria</p>
                      <p>When a criteria is locked, judges won't be able to score it. You can manually input scores here that will be used in the overall ranking calculations. <strong>You can edit these scores anytime</strong> by reopening this modal.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowAdminScoringModal(false)
                    setSelectedLockedCriteria(null)
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAdminScores}
                  className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Scores
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Admin Notes Modal */}
        {showAdminNotesModal && selectedCriteriaForNotes && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowAdminNotesModal(false)
              setSelectedCriteriaForNotes(null)
              setAdminNotesInput('')
            }}
          >
            <div 
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Admin Note</h3>
                      <p className="text-amber-100 text-sm mt-1">
                        {selectedCriteriaForNotes.name}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowAdminNotesModal(false)
                      setSelectedCriteriaForNotes(null)
                      setAdminNotesInput('')
                    }}
                    className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note for Judges
                  </label>
                  <textarea
                    value={adminNotesInput}
                    onChange={(e) => setAdminNotesInput(e.target.value)}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                    placeholder="Enter a note that will be displayed to judges for this criteria..."
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    This note will be visible to all judges when they view this criteria.
                  </p>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">About Admin Notes</p>
                      <p>Use this to provide context to judges about the criteria. For locked criteria, you can explain why it's locked or provide scoring details.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowAdminNotesModal(false)
                    setSelectedCriteriaForNotes(null)
                    setAdminNotesInput('')
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAdminNotes}
                  className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors font-medium flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Note
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Notifications Modal */}
        {showNotifications && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  <h2 className="text-xl font-bold text-white">Notifications</h2>
                  {(pendingRequestsCount + pendingHelpCount) > 0 && (
                    <span className="bg-red-500 text-white text-sm px-3 py-1 rounded-full font-bold">
                      {pendingRequestsCount + pendingHelpCount} Pending
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setShowNotifications(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Score Change Requests */}
                {changeRequests.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      Score Change Requests
                      {pendingRequestsCount > 0 && (
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-bold">
                          {pendingRequestsCount} pending
                        </span>
                      )}
                    </h3>
                    
                    <div className="space-y-3">
                      {changeRequests.map((request) => (
                        <div key={request.id} className={`border rounded-lg p-4 ${
                          request.status === 'pending' ? 'bg-blue-50 border-blue-300' :
                          request.status === 'approved' ? 'bg-green-50 border-green-300' :
                          'bg-gray-50 border-gray-300'
                        }`}>
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-semibold text-gray-900">
                                  Judge #{request.judge?.judge_number} - {request.judge?.name || 'Unknown'}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                  request.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                                  request.status === 'approved' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {request.status}
                                </span>
                              </div>

                              <p className="text-sm text-gray-600 mb-1">
                                <span className="font-medium">
                                  {request.request_type === 'criteria' ? 'Criteria' : 'Special Award'}:
                                </span>{' '}
                                {request.request_type === 'criteria' ? request.criteria?.name : request.special_award?.name}
                              </p>

                              <p className="text-sm text-gray-700 bg-white p-2 rounded border border-gray-200 mb-2">
                                <span className="font-medium">Reason:</span> {request.reason}
                              </p>

                              <p className="text-xs text-gray-500">
                                Requested: {new Date(request.requested_at).toLocaleString()}
                              </p>

                              {request.status !== 'pending' && request.reviewed_at && (
                                <p className="text-xs text-gray-500">
                                  {request.status === 'approved' ? 'Approved' : 'Rejected'}: {new Date(request.reviewed_at).toLocaleString()}
                                </p>
                              )}

                              {request.status === 'rejected' && request.admin_notes && (
                                <p className="text-sm text-red-700 bg-red-50 p-2 rounded border border-red-200 mt-2">
                                  <span className="font-medium">Admin Note:</span> {request.admin_notes}
                                </p>
                              )}
                            </div>

                            {request.status === 'pending' && (
                              <div className="flex flex-col sm:flex-row gap-2">
                                <button
                                  onClick={() => handleApproveChangeRequest(request.id)}
                                  disabled={processingRequest === request.id}
                                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                  {processingRequest === request.id ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                      Processing...
                                    </>
                                  ) : (
                                    <>
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                      Approve
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={() => handleRejectChangeRequest(request.id)}
                                  disabled={processingRequest === request.id}
                                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                  {processingRequest === request.id ? (
                                    'Processing...'
                                  ) : (
                                    <>
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                      Reject
                                    </>
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Help Requests */}
                {helpRequests.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Help Requests
                      {pendingHelpCount > 0 && (
                        <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full font-bold">
                          {pendingHelpCount} pending
                        </span>
                      )}
                    </h3>
                    
                    <div className="space-y-3">
                      {helpRequests.map((request) => (
                        <div key={request.id} className={`border rounded-lg p-4 ${
                          request.status === 'pending' ? 'bg-purple-50 border-purple-300' : 'bg-gray-50 border-gray-300'
                        }`}>
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-semibold text-gray-900">
                                  Judge #{request.judge?.judge_number} - {request.judge?.name || 'Unknown'}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                  request.status === 'pending' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {request.status}
                                </span>
                              </div>

                              <p className="text-sm text-gray-700 bg-white p-3 rounded border border-gray-200 mb-2">
                                {request.message}
                              </p>

                              <p className="text-xs text-gray-500">
                                Requested: {new Date(request.created_at).toLocaleString()}
                              </p>

                              {request.status === 'resolved' && request.resolved_at && (
                                <>
                                  <p className="text-xs text-gray-500">
                                    Resolved: {new Date(request.resolved_at).toLocaleString()}
                                  </p>
                                  {request.admin_notes && (
                                    <p className="text-sm text-green-700 bg-green-50 p-2 rounded border border-green-200 mt-2">
                                      <span className="font-medium">Admin Note:</span> {request.admin_notes}
                                    </p>
                                  )}
                                </>
                              )}
                            </div>

                            {request.status === 'pending' && (
                              <button
                                onClick={() => handleResolveHelpRequest(request.id)}
                                disabled={processingRequest === request.id}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                              >
                                {processingRequest === request.id ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Mark as Resolved
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {changeRequests.length === 0 && helpRequests.length === 0 && (
                  <div className="text-center py-12">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Notifications</h3>
                    <p className="text-gray-600">You're all caught up! No pending requests at the moment.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

export default EventDetails 